import { getDbSafe, getAuthSafe, getStorageSafe, auth as legacyAuth, storage as legacyStorage } from './firebaseConfig';
import { readGoldBalance, goldFields } from './goldCore';
import { FeedHideService } from './FeedHideService';
import {
    collection, getDocs, doc, getDoc, setDoc, addDoc, updateDoc, deleteDoc,
    query, where, orderBy, serverTimestamp, arrayUnion, arrayRemove, increment, onSnapshot, writeBatch, runTransaction, limit, Timestamp, collectionGroup, documentId, startAfter, deleteField, getCountFromServer
} from 'firebase/firestore';
import { ref, uploadBytes, uploadString, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup, signInWithCredential, onAuthStateChanged, UserCredential, fetchSignInMethodsForEmail, EmailAuthProvider, reauthenticateWithCredential, reauthenticateWithPopup, signInWithCustomToken, browserPopupRedirectResolver } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { User, UserProfile, Yap, ShoutedYap, Forum, Tribe, UserRank, Notification } from '../types';
import { buildYapAuthorFields, getUserAvatar, isGhostAvatar } from '../utils/userUtils';
import { bootMark } from '../debug/bootTracer';
import { formatTimeAgo } from '../components/utils/timeFormat';
import { YAP_MAX_CHARACTERS, exceedsYapLimit } from '../constants/posts';

// ── SECURITY: Rate-limit tracker (in-memory, per session) ────────────────────
// Maps uid -> last action timestamp for various actions.
const _rateLimitMap: Map<string, Record<string, number>> = new Map();

function _checkRateLimit(uid: string, action: string, cooldownMs: number): void {
    const now = Date.now();
    const userLimits = _rateLimitMap.get(uid) || {};
    const last = userLimits[action] || 0;
    if (now - last < cooldownMs) {
        const remaining = Math.ceil((cooldownMs - (now - last)) / 1000);
        throw new Error(`RATE_LIMITED: Please wait ${remaining}s before doing that again.`);
    }
    userLimits[action] = now;
    _rateLimitMap.set(uid, userLimits);
}

export const UserService = {
    // Deprecated sync methods - replaced by async versions
    getCurrentUser: (): User => {
        // Placeholder to satisfy types until refactor is complete
        return {} as User;
    },

    getUserById: (id: string | number): User | undefined => {
        return undefined;
    },

    getUserByUsername: (username: string): User | undefined => {
        return undefined;
    },

    getAllUsers: (): User[] => {
        return [];
    },

    /**
     * Just the ids of who someone is friends with. One query, no profile reads.
     *
     * getFriends below returns full User objects, which costs 1 + N document
     * reads: the subcollection query plus a getDoc per friend. Callers that only
     * need ids were paying all of that for nothing — the ids ARE the
     * subcollection document ids, so they are already in the first response.
     *
     * The swipe deck is the case that matters. It awaits the friend list purely
     * to build an exclusion set before it can even start looking for people to
     * show, so for someone with a hundred friends that was a hundred round trips
     * sitting in front of the first card.
     */
    getFriendIds: async (userId: string): Promise<string[]> => {
        try {
            const db = await getDbSafe();
            const snapshot = await getDocs(collection(db, "users", userId, "friends"));
            return snapshot.docs.map(d => String(d.id));
        } catch (error) {
            console.error("Error fetching friend ids:", error);
            return [];
        }
    },

    getFriends: async (userId: string): Promise<User[]> => {
        try {
            const db = await getDbSafe();
            const friendsRef = collection(db, "users", userId, "friends");
            const snapshot = await getDocs(friendsRef);
            // Preserve the friendship's createdAt (when they were added) so callers
            // can sort by "newest add". It lives on the friends subcollection doc,
            // NOT the friend's user doc (whose createdAt is their signup date).
            // Friendship docs use `createdAt` (addFriend) OR `addedAt` (accept/connect
            // flows) depending on how the friendship was formed — accept either.
            const friendEntries = snapshot.docs.map(d => ({ id: d.id, friendedAt: d.data()?.createdAt ?? d.data()?.addedAt ?? null }));

            if (friendEntries.length === 0) return [];

            const friends = await Promise.all(friendEntries.map(async ({ id: fid, friendedAt }) => {
                const db = await getDbSafe();
                const fDoc = await getDoc(doc(db, "users", fid));
                if (fDoc.exists()) {
                    return { id: fDoc.id, ...fDoc.data(), friendedAt } as unknown as User;
                }
                return null;
            }));

            // Filter out nulls AND duplicates (if any exists due to bad data)
            const validFriends = friends.filter(f => f !== null) as User[];
            const uniqueFriends = Array.from(new Map(validFriends.map(f => [f.id, f])).values());
            
            return uniqueFriends;
        } catch (error) {
            console.error("Error fetching friends:", error);
            return [];
        }
    },

    addFriend: async (userId: string, friendId: string): Promise<void> => {
        const db = await getDbSafe();
        // Verify friend exists first to prevent ghost friends
        const friendDoc = await getDoc(doc(db, "users", friendId));
        if (!friendDoc.exists()) {
            throw new Error("User does not exist");
        }

        const batch = writeBatch(db);

        // 1. Add to current user's friends
        const userFriendRef = doc(db, "users", userId, "friends", friendId);
        batch.set(userFriendRef, { 
            friendId, 
            createdAt: serverTimestamp() 
        });

        // 2. Add to friend's friends (Mutual)
        const friendUserRef = doc(db, "users", friendId, "friends", userId);
        batch.set(friendUserRef, { 
            friendId: userId, 
            createdAt: serverTimestamp() 
        });

        // 3. Update friendsCount for both users (Atomic Source of Truth)
        batch.update(doc(db, "users", userId), { friendsCount: increment(1) });
        batch.update(doc(db, "users", friendId), { friendsCount: increment(1) });

        await batch.commit();
    },

    removeFriend: async (userId: string, friendId: string): Promise<void> => {
        const db = await getDbSafe();
        const batch = writeBatch(db);

        // 1. Remove from current user's friends
        const userFriendRef = doc(db, "users", userId, "friends", friendId);
        batch.delete(userFriendRef);

        // 2. Remove from friend's friends
        const friendUserRef = doc(db, "users", friendId, "friends", userId);
        batch.delete(friendUserRef);

        // 3. Update friendsCount for both users (Atomic Source of Truth)
        batch.update(doc(db, "users", userId), { friendsCount: increment(-1) });
        batch.update(doc(db, "users", friendId), { friendsCount: increment(-1) });

        await batch.commit();
    },

    blockUser: async (blockedUserId: string): Promise<void> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        if (!auth.currentUser) throw new Error("No user logged in");
        
        const currentUserRef = doc(db, "users", auth.currentUser.uid);
        await updateDoc(currentUserRef, {
            blockedUsers: arrayUnion(blockedUserId)
        });
    },

    // Async versions for Firebase integration
    fetchCurrentUser: async (): Promise<User> => {
        const user = await UserService.fetchCurrentUserInternal();
        return UserService.syncAndAuditUser(user);
    },

    fetchCurrentUserInternal: async (): Promise<User> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        // Only fetch once Firebase Auth has a current user — never optimistically
        // before the token exists (that's the source of the timeout race on login
        // from the tribe-invite flow).
        if (!auth.currentUser) throw new Error("No user logged in");
        const docRef = doc(db, "users", auth.currentUser.uid);

        // Retry transient failures (timeouts/network) up to 2x with exponential
        // backoff (0.5s, 1s). A genuine "profile not found" is NOT retried.
        const MAX_RETRIES = 2;
        let lastError: any;
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
                const userDoc = await Promise.race([
                    getDoc(docRef),
                    new Promise<never>((_, reject) => setTimeout(() => reject(new Error('FIRESTORE_FETCH_USER_TIMEOUT')), 12000)),
                ]);
                if (userDoc.exists()) {
                    return { id: userDoc.id, ...userDoc.data() } as User;
                }
                throw new Error("User profile not found");
            } catch (error: any) {
                lastError = error;
                // Don't retry a real "not found" — only transient timeouts/network.
                if (error?.message === 'User profile not found') break;
                if (attempt < MAX_RETRIES) {
                    console.warn(`[fetchCurrentUser] attempt ${attempt + 1} failed (${error?.message}); retrying…`);
                    await new Promise((r) => setTimeout(r, 500 * Math.pow(2, attempt)));
                }
            }
        }
        console.error("Error fetching current user:", lastError);
        throw lastError;
    },

    /**
     * Helper to resolve the best possible avatar from a user profile.
     * Priorities: Cropped > Portfolio > Avatar > Image > Legacy data
     */
    /**
     * Delegates to getUserAvatar. This used to hand-roll its own order and got
     * all three of the same things wrong that the ad-hoc call sites did:
     *
     *   • no ghost filtering, so a pravatar or /anonymous-avatar.png placeholder
     *     written at signup won outright;
     *   • it read `portfolio` — the UNCROPPED originals — rather than
     *     portfolioPreviews, so it showed a different framing than the user set;
     *   • croppedImage came BEFORE the portfolio, so a stale or ghost cropped
     *     value outranked the photo the user had actually uploaded.
     *
     * This one matters more than the display-only sites because it also runs at
     * WRITE time (createYap stamps yapData.avatar from it), so a wrong answer
     * here was denormalized onto the post and outlived the profile — which is
     * why the feed kept showing it after the profile was fixed.
     *
     * fallbackData is the yap document itself at read time: its own stored
     * author fields, used when the author's user document could not be loaded.
     * Filtered too, since those stored copies are exactly where old ghosts live.
     */
    resolveUserAvatar: (userData: any, fallbackData?: any): string => {
        const fromUser = getUserAvatar(userData);
        if (fromUser) return fromUser;

        const stored = [fallbackData?.avatar, fallbackData?.image]
            .find((v) => typeof v === 'string' && v.trim() !== '' && !isGhostAvatar(v));
        return stored || '';
    },

    syncAndAuditUser: async (user: User): Promise<User> => {
        const db = await getDbSafe();
        const { getDoc, setDoc, doc, collection, query, where, getDocs, writeBatch, serverTimestamp } = await import('firebase/firestore');
        const { SafeStorage } = await import('../utils/safeStorage');
        
        // 💰 HEAL: Ensure initial gold balance if missing or zero (for accounts created during the 'zero gold' bug)
        // Deliberately NOT using readGoldBalance(): this compares `gold` and
        // `goldBalance` INDEPENDENTLY to detect a half-written document. Collapsing
        // them to the canonical read would defeat the check it exists to perform.
        if (!user.gold || user.gold === 0 || user.goldBalance === undefined) {
             const currentGold = user.gold || 0;
             const currentBalance = user.goldBalance || 0;
             
             if (currentGold < 1500 && currentBalance < 1500) {
                 // [Removed PII log: was logging user.id + gold balance]
                 try {
                     const { updateDoc, doc } = await import('firebase/firestore');
                     await updateDoc(doc(db, 'users', String(user.id)), {
                         gold: 1500,
                         goldBalance: 1500
                     });
                     user.gold = 1500;
                     user.goldBalance = 1500;
                 } catch (e) {
                     console.warn('[UserService] Gold healing failed:', e);
                 }
             }
        }

        // 🆔 GET INTENT: What name did the user actually type?
        const intentUsername = SafeStorage.getItem('login_intent_username');
        const currentUsername = user.username?.replace('@', '').toLowerCase();
        
        // 🔒 SAFETY GUARD: Never audit if the current account is already "Real" (substantial)
        // This prevents overwriting a complete account with a ghost/old candidate.
        const isSubstantial = user.name && user.name !== '???' && user.name !== 'New User' && user.name.length > 2;
        const hasValidDOB = user.dateOfBirth && user.dateOfBirth.length > 4;
        const hasPortfolio = Array.isArray(user.portfolio) && user.portfolio.length > 0;
        
        // 🔒 CRITICAL FIX: If the account is already substantial, CLEAR any pending intent 
        // to prevent accidental swaps during subsequent audits or slaps.
        if (isSubstantial && (hasValidDOB || hasPortfolio)) {
            if (intentUsername) {
                console.log(`[UserService] Audit: Account ${user.id} is substantial. Purging stale login intent: ${intentUsername}`);
                SafeStorage.removeItem('login_intent_username');
            }
            return user;
        }

        // 🕵️ EMERGENCY MERGER: Trigger ONLY if profile is truly incomplete OR explicitly matches a fresh login intent
        const isIncomplete = !user.username || user.username.toLowerCase().includes('user') || !user.name || user.name === 'New User' || user.name === '???';
        const isIdentityMismatch = intentUsername && currentUsername && intentUsername !== currentUsername;
        
        if (isIncomplete || isIdentityMismatch) {
            // Search for the "Real" data based on the Intent name
            const searchUsername = intentUsername || currentUsername || '';
            
            if (searchUsername) {
                const q = query(
                    collection(db, "users"), 
                    where("username", "in", [`@${searchUsername}`, searchUsername])
                );
                
                const snapshot = await getDocs(q);
                // Find candidates with ACTUAL data, excluding the current (likely empty) one
                const candidates = snapshot.docs
                    .map(d => ({ id: d.id, ...d.data() } as User))
                    .filter(c => String(c.id) !== String(user.id))
                    .filter(c => c.name && c.name !== '???' && c.name !== 'New User');
                
                // Sort by "best" (most complete)
                const bestCandidate = candidates.sort((a, b) => {
                    const aScore = (a.bio ? 1 : 0) + (a.image ? 1 : 0) + (a.occupation ? 1 : 0);
                    const bScore = (b.bio ? 1 : 0) + (b.image ? 1 : 0) + (b.occupation ? 1 : 0);
                    return bScore - aScore;
                })[0];

                if (bestCandidate) {
                    // 🛡️ FINAL SAFETY: Never overwrite an account that has ANY portfolio data, 
                    // even if it's considered "incomplete" (missing name/DOB).
                    if (hasPortfolio) {
                        console.warn(`[UserService] Identity Restore BLOCKED: Account ${user.id} has portfolio items. Purging intent: ${intentUsername}`);
                        SafeStorage.removeItem('login_intent_username');
                        return user;
                    }
                    
                    // 🛡️ FORMAL SEPARATION: Overwrite the "Shell" account with the "Original" data
                    const restoredData = {
                        ...bestCandidate,
                        id: String(user.id), // Link original data to THIS Auth UID
                        username: bestCandidate.username.startsWith('@') ? bestCandidate.username : `@${bestCandidate.username}`,
                        source: 'IDENTITY_RESTORE_DEEP',
                        restoredAt: serverTimestamp(),
                        originalId: String(bestCandidate.id),
                        // Force bypass setup if original was complete
                        dateOfBirth: bestCandidate.dateOfBirth || '1990-01-01',
                        country: bestCandidate.country || 'Nigeria'
                    };

                    await setDoc(doc(db, 'users', String(user.id)), restoredData, { merge: true });
                    
                    // 🛡️ Cleanup: Audit successful, clear the trigger
                    SafeStorage.removeItem('login_intent_username');
                    
                    // DEEP MIGRATION: Recursively move all assets (Non-blocking)
                    try {
                        const batch = writeBatch(db);
                        
                        // 1. Yaps
                        const yapsQ = query(collection(db, "yaps"), where("userId", "==", String(bestCandidate.id)));
                        const yapsSnap = await getDocs(yapsQ);
                        yapsSnap.forEach(d => batch.update(d.ref, { userId: String(user.id) }));
                        
                        // 2. Shouts
                        const shoutsQ = query(collection(db, "shouts"), where("shoutedBy", "==", String(bestCandidate.id)));
                        const shoutsSnap = await getDocs(shoutsQ);
                        shoutsSnap.forEach(d => batch.update(d.ref, { shoutedBy: String(user.id) }));
                        
                        await batch.commit().catch(e => console.warn("[UserService] Batch migration failed:", e));
                    } catch (e) {
                        console.warn("[UserService] Yaps/Shouts migration failed:", e);
                    }
                    
                    // 3. Friends List (Non-blocking)
                    try {
                        const friendsQ = query(collection(db, "users", String(bestCandidate.id), "friends"));
                        const friendsSnap = await getDocs(friendsQ);
                        for (const friendDoc of friendsSnap.docs) {
                            await setDoc(doc(db, "users", String(user.id), "friends", String(friendDoc.id)), friendDoc.data()).catch(() => {});
                        }
                    } catch (e) {
                        console.warn("[UserService] Friends list migration failed:", e);
                    }

                    // 4. Chat Migration: Remap participants in chats (Non-blocking)
                    try {
                        const chatsAQ = query(collection(db, "chats"), where("participants", "array-contains", String(bestCandidate.id)));
                        const chatsASnap = await getDocs(chatsAQ);
                        for (const chatDoc of chatsASnap.docs) {
                            const data = chatDoc.data();
                            const newParticipants = data.participants.map((p: string) => p === String(bestCandidate.id) ? String(user.id) : p);
                            await updateDoc(chatDoc.ref, { participants: newParticipants }).catch(() => {});
                        }
                    } catch (e) {
                        console.warn("[UserService] Chat migration failed:", e);
                    }

                    // 5. Registry Healing & Shell Cleanup (Non-blocking)
                    try {
                        const registryRef = doc(db, 'usernames', searchUsername);
                        await setDoc(registryRef, {
                            uid: String(user.id),
                            updatedAt: serverTimestamp(),
                            source: 'RESTORE_AUDIT_HEAL'
                        }, { merge: true }).catch(() => {});

                        // Vacate the shell name so it doesn't linger on the restored account
                        if (isIdentityMismatch && currentUsername) {
                            // [Removed PII log: was logging vacated shell username]
                            const shellRegistryRef = doc(db, 'usernames', currentUsername);
                            await deleteDoc(shellRegistryRef).catch(() => {});
                        }
                    } catch (e) {
                        console.warn("[UserService] Registry cleanup failed:", e);
                    }
                    
                    // Clear intent after successful restore attempt
                    SafeStorage.removeItem('login_intent_username');
                    // [Removed PII log: was logging searchUsername after identity restore]
                    return { ...user, ...restoredData } as User;
                }
            }
        }
        
        // Always clear intent after a successful audit, even if no restore happened
        SafeStorage.removeItem('login_intent_username');
        return user;
    },

    fetchUserById: async (id: string): Promise<User | undefined> => {
        try {
            const db = await getDbSafe();
            const userDoc = await getDoc(doc(db, "users", id));
            if (userDoc.exists()) {
                return { id: userDoc.id, ...userDoc.data() } as User;
            }
            return undefined;
        } catch (error) {
            console.error("Error fetching user by ID:", error);
            return undefined;
        }
    },

    fetchUserByUsername: async (username: string): Promise<User | undefined> => {
        try {
            const db = await getDbSafe();
            const handle = username.trim().replace(/^@+/, '').toLowerCase();
            if (!handle) return undefined;

            // 1. Registry first (usernames/{handle} → uid). This is the only
            //    reliable path: the `users` collection LIST rule is age-bracket
            //    gated, so a `where('username','==',…)` query is rejected whenever
            //    the target sits in a different bracket than the viewer — which
            //    silently broke @-mention resolution. A single-doc `get` on
            //    users/{uid} is allowed for any signed-in user, and the registry
            //    is keyed on the bare lowercase handle so accounts stored without
            //    the leading '@' (Google sign-up) resolve too.
            try {
                const registrySnap = await getDoc(doc(db, 'usernames', handle));
                const uid = registrySnap.exists()
                    ? (registrySnap.data()?.uid || registrySnap.data()?.userId)
                    : null;
                if (uid) {
                    const userSnap = await getDoc(doc(db, 'users', String(uid)));
                    if (userSnap.exists()) {
                        return { id: userSnap.id, ...userSnap.data() } as User;
                    }
                }
            } catch (registryError) {
                console.warn("[UserService] Username registry lookup failed:", registryError);
            }

            // 2. Fallback: direct query for legacy accounts missing a registry doc.
            //    May be denied by rules across age brackets — hence the registry first.
            const cleanUsername = `@${handle}`;
            const q = query(collection(db, "users"), where("username", "==", cleanUsername), limit(1));
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                const docSnap = snapshot.docs[0];
                return { id: docSnap.id, ...docSnap.data() } as User;
            }
            return undefined;
        } catch (error) {
            console.error("Error fetching user by username:", error);
            return undefined;
        }
    },

    fetchAllUsers: async (): Promise<User[]> => {
        try {
            const db = await getDbSafe();
            const snapshot = await getDocs(collection(db, "users"));
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
        } catch (error) {
            console.error("Error fetching all users:", error);
            return [];
        }
    },

    fetchUserStats: async (userId: string): Promise<{ friendsCount: number; yapsCount: number; slapCount: number }> => {
        try {
            const db = await getDbSafe();
            
            // 1. Fetch real friends count from sub-collection (Accurate)
            const friendsRef = collection(db, "users", userId, "friends");
            const friendsSnap = await getDocs(friendsRef);
            const friendsCount = friendsSnap.size;

            // 2. Yap count via server-side aggregation — returns the true total
            //    with a single billed read instead of paying for (and capping at)
            //    100 full documents. Prolific users used to freeze at "100 yaps".
            const yapsQ = query(collection(db, "yaps"), where("userId", "==", userId));
            const yapsCount = (await getCountFromServer(yapsQ)).data().count;

            // 3. Slaps are usually stored directly on the doc, but we check if it exists
            const userDoc = await getDoc(doc(db, "users", userId));
            const slapCount = userDoc.data()?.slapCount || 0;

            console.log(`[UserService] Accurate stats for ${userId}:`, { friendsCount, yapsCount, slapCount });
            return { friendsCount, yapsCount, slapCount };
        } catch (error) {
            console.error(`[UserService] Error fetching stats for ${userId}:`, error);
            return { friendsCount: 0, yapsCount: 0, slapCount: 0 };
        }
    },

    fetchDiscoverUsers: async (currentUserId: string, excludeIds: string[] = [], options?: { residence?: string, ageRange?: [number, number], interests?: string[], tribes?: string[], targetSize?: number, viewerAgeGroup?: 'adult' | 'minor' }): Promise<User[]> => {
        try {
            const residence = options?.residence;
            const ageRange = options?.ageRange;
            const interests = options?.interests;
            const tribes = options?.tribes;
            // Fail-closed: viewer with no ageGroup → 'minor', matches the list rule default.
            const viewerAgeGroup: 'adult' | 'minor' = options?.viewerAgeGroup ?? 'minor';

            const db = await getDbSafe();
            const usersRef = collection(db, "users");
            
            // 🔒 SMART FETCH PARAMETERS
            const BATCH_SIZE = 500; // Increased to ensure we find enough users even with strict filters
            const TARGET_SIZE = options?.targetSize || 15;
            const MAX_SCAN = options?.targetSize ? (options.targetSize * 50) : 1000; // Increased to 750-1000 for better discovery

            let validUsers: User[] = [];
            let lastVisible: any = null;
            let scannedCount = 0;
            let loopCount = 0;
            let currentTier = 1; // 🏢 TIERED FALLBACK: 1 (Strict) -> 2 (Local) -> 3 (Global Inter) -> 4 (Catch-all)

            // Fail-closed: returns null for missing/invalid DOB so the caller excludes the candidate.
            // Regex parse avoids new Date('YYYY-MM-DD') UTC midnight off-by-one in UTC- zones.
            const calculateAge = (dob?: string): number | null => {
                if (!dob) return null;
                const mm = dob.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
                if (!mm) return null;
                const b = new Date(parseInt(mm[1]), parseInt(mm[2]) - 1, parseInt(mm[3]));
                if (isNaN(b.getTime())) return null;
                const today = new Date();
                let age = today.getFullYear() - b.getFullYear();
                const m = today.getMonth() - b.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
                return age;
            };

            console.log(`[Discover] Starting Tiered Fetch. Excluded: ${excludeIds.length}. Interests: ${interests?.length || 0}`);

            while (validUsers.length < TARGET_SIZE && scannedCount < MAX_SCAN && loopCount < 15) {
                loopCount++;
                
                let q;
                const constraints: any[] = [
                    limit(BATCH_SIZE)
                ];

                // 🏢 RANDOMIZED DISCOVERY: Use refreshKey to alternate sorting orders
                // This ensures that "Refresh" shows a different set of users.
                const refreshKey = (options as any)?.refreshKey || 0;
                if (refreshKey % 3 === 0) {
                    constraints.unshift(orderBy("createdAt", "desc"));
                } else if (refreshKey % 3 === 1) {
                    constraints.unshift(orderBy("createdAt", "asc"));
                } else {
                    constraints.unshift(orderBy("name", "asc"));
                }

                // Age bracket — required by Phase 2d list rule. Every query on the
                // users collection must be filtered to the viewer's own bracket.
                // Fail-closed default ('minor') matches the rule's resource.data default.
                constraints.push(where("ageGroup", "==", viewerAgeGroup));

                // 🏢 TIERED QUERY ADJUSTMENT
                // Tier 1: Local Residence (Hard Filter)
                // Tier 2: Local Residence (Fallback if Tier 1 yields nothing)
                // Tier 3+: International (No Residence check)
                if (residence && currentTier <= 2) {
                    constraints.unshift(where("home_residence", "==", residence));
                }

                if (lastVisible) {
                    constraints.push(startAfter(lastVisible));
                }

                try {
                    q = query(usersRef, ...constraints);
                } catch (e) {
                    console.error("[Discover] Query construction failed:", e);
                    break;
                }

                let snapshot;
                try {
                    snapshot = await getDocs(q);
                } catch (queryError: any) {
                    if (queryError?.message?.includes('index')) {
                        console.warn("🔥 MISSING INDEX: Falling back to unordered fetch.");
                        const fallbackConstraints: any[] = [
                            where("ageGroup", "==", viewerAgeGroup),
                            limit(BATCH_SIZE),
                        ];
                        if (residence && currentTier <= 2) fallbackConstraints.unshift(where("home_residence", "==", residence));
                        if (lastVisible) fallbackConstraints.push(startAfter(lastVisible));
                        snapshot = await getDocs(query(usersRef, ...fallbackConstraints));
                    } else {
                        throw queryError;
                    }
                }

                // 🏢 TIER SHIFT: If current query is empty, BROADEN the search scope
                if (snapshot.empty) {
                    if (currentTier < 4) {
                        console.log(`[Discover] Tier ${currentTier} exhausted. Advancing to Tier ${currentTier + 1}...`);
                        currentTier++;
                        lastVisible = null; // Start fresh from top for the new tier
                        continue;
                    } else {
                        break; // No more users in entire database
                    }
                }

                lastVisible = snapshot.docs[snapshot.docs.length - 1];
                scannedCount += snapshot.docs.length;

                const batchUsers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));

                // 🏢 SCORING & FILTERING
                const scoredBatch = batchUsers.map(user => {
                    let score = 0;
                    
                    // 1. Identity/Interest Matching Score
                    if (interests && interests.length > 0) {
                        const userIdentities = user.identities || user.interests || [];
                        const matches = interests.filter(i => userIdentities.includes(i));
                        score = matches.length; // 0, 1, 2, or 3
                    }

                    // 2. Bonus for new users (if Tier 4)
                    if (currentTier === 4) score = -1; // Lower priority catch-all

                    return { user, score };
                });

                // Apply Hard Filters (Age, Blocked, etc.)
                // 🏢 SHUFFLE & SELECTION: Randomize the batch for variety before picking top TARGET_SIZE
                const finalBatch = scoredBatch
                    .sort((a, b) => b.score - a.score || Math.random() - 0.5);

                for (const item of finalBatch) {
                    if (validUsers.length >= TARGET_SIZE) break;
                    const user = item.user;

                    // 1. Basic Exclusions
                    if (user.id === currentUserId) continue;
                    if (excludeIds.includes(String(user.id))) continue;
                    if (validUsers.some(u => u.id === user.id)) continue; // Internal dedup
                    if (user.showInDiscover === false) continue;
                    // Moderation: hidden accounts (portfolio violations) are invisible in discovery.
                    if ((user as any).accountHidden === true) continue;

                    if ((user as any).isBanned || (user as any).isSuspended || (user as any).isModerated) continue;
                    if (!user.name || user.name === 'New User' || user.name === '???') continue;
                    if (!user.username || user.username.includes('user_')) continue;
                    if (user.setupComplete === false && !user.portfolio?.some((p: string) => p)) continue;

                    // 2. Age Range — fail-closed: null age (missing/invalid DOB) → excluded from all pools.
                    // No stored-age fallback: the frozen user.age field predates the DOB system and cannot be trusted.
                    if (ageRange) {
                        const age = calculateAge(user.dateOfBirth);
                        if (age === null || age < Number(ageRange[0]) || age > Number(ageRange[1])) continue;
                    }

                    // 3. 🔒 HARD IDENTITY FILTER: Direct check — bypasses the score system
                    // (score gets overwritten to -1 at Tier 4, so we can't rely on item.score here).
                    // When identities are selected, ALWAYS require at least ONE match.
                    if (interests && interests.length > 0) {
                        const userIdentities = (user.identities || user.interests || []) as string[];
                        const hasMatch = interests.some(i => userIdentities.includes(i));
                        if (!hasMatch) continue;
                    }

                    validUsers.push(user);
                }
            }

            console.log(`[Discover] Smart Fetch Complete. Scanned: ${scannedCount}, Found: ${validUsers.length} in Tier ${currentTier}`);
            return validUsers;

        } catch (error) {
            console.error("Error fetching discover users (Smart Fetch):", error);
            return [];
        }
    },

    /**
     * Aggregates the most common identities manually tagged by users.
     * In a production environment, this should ideally be an indexed collection.
     */
    fetchTrendingTags: async (viewerAgeGroup?: 'adult' | 'minor'): Promise<string[]> => {
        try {
            const db = await getDbSafe();
            const usersRef = collection(db, "users");
            // Bracket-filtered: each viewer sees trending tags from their own age group.
            // Fail-closed: absent viewerAgeGroup → 'minor', matches list rule default.
            const ageGroup = viewerAgeGroup ?? 'minor';
            const q = query(usersRef, where("ageGroup", "==", ageGroup), orderBy("createdAt", "desc"), limit(300));
            const snapshot = await getDocs(q);
            
            const tagCounts: Record<string, number> = {};
            snapshot.docs.forEach(doc => {
                const identities = doc.data().identities || [];
                identities.forEach((tag: string) => {
                    tagCounts[tag] = (tagCounts[tag] || 0) + 1;
                });
            });

            // Sort by count descending
            return Object.entries(tagCounts)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 40) // Top 40 tags
                .map(([tag]) => tag);
        } catch (error) {
            console.error("Error fetching trending tags:", error);
            return ['Yoruba', 'Igbo', 'Hausa', 'Akan', 'Amhara', 'Afrobeats']; // real identity-tag fallback
        }
    },

    deleteAccount: async (): Promise<void> => {
        const auth = await getAuthSafe();
        const user = auth.currentUser;
        
        if (!user) throw new Error("No user logged in");
        const userId = user.uid;

        try {
            console.log(`[deleteAccount] Starting comprehensive deletion for user: ${userId}`);
            
            // 1. Purge all Firestore/Storage data
            await AdminService.purgeUserData(userId);
            
            // 2. Clear the session marker BEFORE the auth record goes away.
            //
            // ProtectedRoute treats "no firebaseUser but afroyap_had_session is
            // set" as a connectivity failure rather than a logout, and shows the
            // offline screen — deliberately, so a dropped network does not dump
            // someone at a login form they may not have credentials for. It
            // cannot tell that apart from an account that was intentionally
            // destroyed, so a deletion landed on "no connection" instead of the
            // login screen.
            //
            // UserDataContext clears this too, but only from the auth-state
            // listener and only when navigator.onLine, which the deletion path
            // does not reliably win. Clearing it here makes the deletion present
            // as the deliberate logout it is.
            try { localStorage.removeItem('afroyap_had_session'); } catch { /* storage unavailable */ }

            // 3. Delete Auth Account (Only possible for current user)
            await user.delete();

            console.log(`[deleteAccount] Account deletion complete.`);
        } catch (error) {
            // Deletion can still fail here — most often auth/requires-recent-login,
            // which the caller recovers from by re-authenticating and retrying. The
            // account survives, so put the session marker back; without this a
            // failed attempt would leave a signed-in user who gets sent to login
            // rather than the offline screen the next time the network drops.
            try { localStorage.setItem('afroyap_had_session', '1'); } catch { /* storage unavailable */ }
            console.error("Error deleting account:", error);
            throw error;
        }
    },

    /**
     * Record a user interaction (like/pass) to persist 'seen' status
     */
    recordUserInteraction: async (currentUserId: string, targetUserId: string, type: 'like' | 'pass'): Promise<{ success: boolean }> => {
        try {
            const db = await getDbSafe();
            const interactionRef = doc(db, 'users', currentUserId, 'interactions', targetUserId);
            await setDoc(interactionRef, {
                type,
                timestamp: serverTimestamp()
            }, { merge: true });
            return { success: true };
        } catch (error) {
            console.warn("Error recording user interaction (likely permission issue, falling back to local):", error);
            return { success: false };
        }
    },

    /**
     * Fetch all users the current user has interacted with (liked or passed)
     */
    fetchUserInteractions: async (currentUserId: string): Promise<string[]> => {
        try {
            const db = await getDbSafe();
            const interactionsRef = collection(db, 'users', currentUserId, 'interactions');
            // We only need the IDs
            const snapshot = await getDocs(interactionsRef);
            return snapshot.docs.map(doc => doc.id);
        } catch (error) {
            console.error("Error fetching user interactions:", error);
            return [];
        }
    },

    /**
     * Persist slaps to a user profile
     */
    incrementSlaps: async (targetUserId: string, count: number): Promise<void> => {
        try {
            const db = await getDbSafe();
            const { increment } = await import('firebase/firestore');
            const userRef = doc(db, 'users', targetUserId);
            await updateDoc(userRef, {
                slapCount: increment(count)
            });
        } catch (error) {
            console.error("[UserService] Error incrementing slaps:", error);
            throw error;
        }
    }
};

// Helper function to create user document in Firestore with timeout protection
export const createUserDocument = async (user: User, additionalData?: any): Promise<void> => {
    if (!user) return;

    // Declared out here so the catch can release it. Set only when THIS call
    // writes a new registry entry, never when the name was already ours.
    let claimedRegistryRef: ReturnType<typeof doc> | null = null;

    try {
        const db = await getDbSafe();
        const userRef = doc(db, "users", String(user.id));

        // Use a timeout for the getDoc check
        const snapshot = await Promise.race([
            getDoc(userRef),
            new Promise<never>((_, reject) => setTimeout(() => reject(new Error('FIRESTORE_GET_DOC_TIMEOUT')), 15000))
        ]);

        const email = user.email || '';
        const displayName = user.name || 'New User';
        const createdAt = user.createdAt || serverTimestamp();

        // 1. Claim the username FIRST, before any user document exists.
        //
        // This used to run second. The atomic claim was correct, but by the time
        // it rejected a taken name the Auth account and the user document had
        // already been written — so the signup "went through" onto a name the
        // account did not own, and the only way out was to restart onboarding.
        //
        // Claiming first means a taken name aborts with nothing created. If the
        // document write below then fails, the claim is released rather than
        // left holding a name for an account that does not exist.
        if (user.username) {
            try {
                const cleanUsername = user.username.replace('@', '').toLowerCase();
                const registryRef = doc(db, 'usernames', cleanUsername);

                console.log('[createUserDocument] Claiming username in registry...');
                await runTransaction(db, async (transaction) => {
                    const regDoc = await transaction.get(registryRef);
                    if (regDoc.exists()) {
                        if (regDoc.data()?.uid === user.id) return; // Already claimed by us
                        throw new Error('USERNAME_TAKEN_IN_REGISTRY');
                    }
                    transaction.set(registryRef, {
                        uid: user.id,
                        email: email || '',
                        timestamp: serverTimestamp()
                    });
                    claimedRegistryRef = registryRef;
                });
                console.log('[createUserDocument] Username claimed.');
            } catch (e: any) {
                console.error('[createUserDocument] Transactional Registry claim failed:', e);
                // DO NOT FAIL SILENTLY: without the registry entry the account
                // cannot be logged into by username at all.
                if (e.message === 'USERNAME_TAKEN_IN_REGISTRY') {
                    throw new Error('USERNAME_TAKEN: This username is already registered in our system.');
                }
                throw e;
            }
        }

        // 2. Create User Document
        console.log('[createUserDocument] Creating user document (merge: true)...');
        const isRelayEmail = email.endsWith('@privaterelay.appleid.com');
        await Promise.race([
            setDoc(userRef, {
                uid: user.id,
                email,
                displayName,
                createdAt,
                gold: 2000,
                goldBalance: 2000,
                friendsCount: 0,
                showLocation: true,
                // Verification defaults — only set on new docs (merge:true skips if already present)
                emailVerified: false,
                phoneVerified: false,
                verifiedBadge: false,
                isRelayEmail,
                ...additionalData,
                ...user
            }, { merge: true }),
            new Promise<never>((_, reject) => setTimeout(() => reject(new Error('FIRESTORE_SET_DOC_TIMEOUT')), 25000))
        ]);
        console.log('[createUserDocument] User document initialized/updated.');

    } catch (error) {
        console.error("Error creating/checking user document", error);

        // Release the name if we claimed it and the document never landed.
        //
        // Claiming first is what stops a taken username being accepted, but it
        // introduces the opposite risk: a registry entry pointing at an account
        // that was never created, holding a name nobody can use or recover.
        // Only a claim made by THIS call is rolled back — the transaction sets
        // claimedRegistryRef exclusively on the branch that wrote a new entry,
        // so a name the user already owned is never released.
        if (claimedRegistryRef) {
            try {
                await deleteDoc(claimedRegistryRef);
                console.warn('[createUserDocument] Rolled back the username claim.');
            } catch (rollbackError) {
                console.error(
                    '[createUserDocument] Could not roll back the username claim — '
                    + 'it may be held with no account behind it:',
                    rollbackError,
                );
            }
        }

        throw error;
    }
};

/**
 * Fill in live author identity on a page of feed documents.
 *
 * The feed used to render whatever `avatar` was stamped onto the post at write
 * time and never look at the author again. That stamp came from the old
 * resolveUserAvatar, so it could be a signup placeholder, an uncropped original,
 * or simply stale — and it outlived every fix applied to the profile itself.
 * The symptom was one user showing their photo on the yap detail screen, which
 * DOES read the user document, and a bare initial in the feed, which did not.
 *
 * Batched with `in` queries of 30 so a 100-post page costs about four reads
 * rather than one per post. Authors are deduplicated first, since a feed page is
 * usually a handful of people posting several times each.
 *
 * Fails soft: if the lookup breaks, posts keep their stamped values and the feed
 * still renders. A stale avatar is much better than an empty screen.
 */
async function hydrateAuthors<T extends Record<string, any>>(db: any, rows: T[]): Promise<T[]> {
    const ids = Array.from(new Set(
        rows.map(r => String(r.userId || r.shoutedBy || '')).filter(id => id && id !== 'undefined'),
    ));
    if (ids.length === 0) return rows;

    const byId = new Map<string, any>();
    try {
        for (let i = 0; i < ids.length; i += 30) {
            const chunk = ids.slice(i, i + 30);
            const snap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)));
            snap.docs.forEach(d => byId.set(d.id, d.data()));
        }
    } catch (e) {
        console.warn('[hydrateAuthors] lookup failed, using stored values', e);
        return rows;
    }

    return rows.map(row => {
        const author = byId.get(String(row.userId || row.shoutedBy || ''));
        if (!author) return row;
        // Only identity fields that go stale are refreshed. Everything else on
        // the post — content, counts, timestamps — is the post's own.
        const live = UserService.resolveUserAvatar(author, row);
        return {
            ...row,
            avatar: live || row.avatar || '',
            ...(row.shoutedBy ? { shoutedByAvatar: live || row.shoutedByAvatar || '' } : {}),
        };
    });
}

export const YapService = {
    getAllYaps: (): Yap[] => {
        return []; // Deprecated
    },

    getYapById: (id: string): Yap | undefined => {
        return undefined; // Deprecated
    },

    getYapsByUserId: (userId: string | number): Yap[] => {
        return []; // Deprecated
    },

    getYapsByUsername: (username: string): Yap[] => {
        return []; // Deprecated
    },

    // Async versions for Firebase integration
    //
    // Cursor-paged read of the global yaps collection, newest-first.
    //
    // The world feed is an open-ended range (everything on or after
    // WORLD_FEED_CUTOFF), so a single fixed `limit` would silently truncate the
    // tail once the collection outgrew it — older yaps became permanently
    // unreachable. Callers now walk backwards a page at a time using `before`
    // (an exclusive upper bound on `timestamp`) instead.
    //
    // `hasMore` is derived from the RAW page size, not the filtered result:
    // a page consisting entirely of soft-deleted yaps still means more pages
    // exist. `cursor` likewise comes from the last raw doc so paging can never
    // stall on a run of filtered-out documents.
    fetchYapsPage: async (
        sinceTimestamp?: string,
        options?: { pageSize?: number; before?: string }
    ): Promise<{ yaps: Yap[]; hasMore: boolean; cursor: string | null }> => {
        const pageSize = options?.pageSize ?? 500;
        try {
            const db = await getDbSafe();
            // Range filters and orderBy all target `timestamp`, so this needs no
            // composite index regardless of which bounds are present.
            const constraints: any[] = [];
            if (sinceTimestamp) constraints.push(where("timestamp", ">=", new Date(sinceTimestamp)));
            if (options?.before) constraints.push(where("timestamp", "<", new Date(options.before)));
            constraints.push(orderBy("timestamp", "desc"), limit(pageSize));

            const snapshot = await getDocs(query(collection(db, "yaps"), ...constraints));

            const yaps = snapshot.docs.map((doc: any) => {
                const data = doc.data();
                const isoTimestamp = data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : new Date().toISOString();
                return {
                    id: doc.id,
                    ...data,
                    time: data.timestamp?.toDate ? formatTimeAgo(data.timestamp.toDate()) : 'Recently',
                    timestamp: isoTimestamp,
                    sortTime: isoTimestamp, // ← consistent sort key for chronological ordering in the feed
                    likes: data.metrics?.likes || data.likes || 0,
                    comments: data.metrics?.comments || data.comments || 0,
                    shouts: data.metrics?.shouts || data.shouts || 0,
                } as Yap;
            });

            // Authors are resolved live rather than trusted from the stamp — see
            // hydrateAuthors. Without this the feed shows a signup placeholder or
            // a stale copy while the yap detail screen shows the real photo.
            const hydrated = await hydrateAuthors(db, yaps as any[]) as Yap[];

            // Cursor from the last RAW doc (pre-filtering) so a page of deleted
            // yaps still advances the walk.
            const cursor = yaps.length > 0 ? (yaps[yaps.length - 1] as any).timestamp : null;
            const hasMore = snapshot.size === pageSize;

            // Always filter soft-deleted / hidden yaps
            let activeYaps = hydrated.filter((yap: any) => !yap.deleted && !yap.is_hidden);

            // Backstop the server-side range bound
            if (sinceTimestamp) {
                const sinceDate = new Date(sinceTimestamp);
                activeYaps = activeYaps.filter((yap: any) => {
                    const yapDate = new Date(yap.timestamp || '');
                    return yapDate.getTime() >= sinceDate.getTime();
                });
            }

            return { yaps: activeYaps, hasMore, cursor };
        } catch (error) {
            console.error("Error fetching yaps:", error);
            return { yaps: [], hasMore: false, cursor: null };
        }
    },

    // Single-page convenience wrapper — used by callers that only need a recent
    // slice (moderation dashboard, etc.). The paged feed uses fetchYapsPage.
    fetchAllYaps: async (sinceTimestamp?: string): Promise<Yap[]> => {
        const { yaps } = await YapService.fetchYapsPage(sinceTimestamp);
        return yaps;
    },

    // Cursor-paged read of the shouts collection — mirrors fetchYapsPage so the
    // world feed can walk both collections backwards in step.
    fetchShoutsPage: async (
        sinceTimestamp?: string,
        options?: { pageSize?: number; before?: string }
    ): Promise<{ shouts: ShoutedYap[]; hasMore: boolean; cursor: string | null }> => {
        const pageSize = options?.pageSize ?? 500;
        try {
            const db = await getDbSafe();
            const constraints: any[] = [];
            if (sinceTimestamp) constraints.push(where("timestamp", ">=", new Date(sinceTimestamp)));
            if (options?.before) constraints.push(where("timestamp", "<", new Date(options.before)));
            constraints.push(orderBy("timestamp", "desc"), limit(pageSize));

            const snapshot = await getDocs(query(collection(db, "shouts"), ...constraints));

            const shouts = snapshot.docs.map(doc => {
                const data = doc.data();
                const isoTimestamp = data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : new Date().toISOString();
                
                return {
                    id: doc.id,
                    ...data,
                    time: data.timestamp?.toDate ? formatTimeAgo(data.timestamp.toDate()) : 'Recently',
                    timestamp: isoTimestamp,
                    likes: data.metrics?.likes || data.likes || 0,
                    comments: data.metrics?.comments || data.comments || 0,
                } as any;
            });

            // Cursor from the last RAW doc so filtered-out shouts can't stall paging.
            const cursor = shouts.length > 0 ? (shouts[shouts.length - 1] as any).timestamp : null;
            const hasMore = snapshot.size === pageSize;

            // Never surface shouts for soft-deleted original yaps
            // Live author identity, same as fetchYapsPage. A shout carries the
            // shouter's avatar, so shoutedByAvatar is refreshed too.
            const hydratedShouts = await hydrateAuthors(db, shouts as any[]);

            let activeShouts = hydratedShouts.filter((s: any) => {
                const orig = s.originalYap as any;
                return !s.deleted && !s.is_hidden && !(orig?.deleted) && !(orig?.is_hidden);
            });

            if (sinceTimestamp) {
                const sinceDate = new Date(sinceTimestamp);
                activeShouts = activeShouts.filter((s: any) => new Date(s.timestamp) >= sinceDate);
            }
            return { shouts: activeShouts, hasMore, cursor };
        } catch (error) {
            console.error("Error fetching all shouts:", error);
            return { shouts: [], hasMore: false, cursor: null };
        }
    },

    fetchAllShouts: async (sinceTimestamp?: string): Promise<ShoutedYap[]> => {
        const { shouts } = await YapService.fetchShoutsPage(sinceTimestamp);
        return shouts;
    },

    /**
     * Lightweight live listener for the world feed's "New Yaps Dropped!" pill.
     * Subscribes to the newest few yaps and reports the newest non-deleted yap's
     * creation timestamp (ISO) whenever it changes. Callers compare it against
     * the timestamp at the top of their feed — because decay re-ranking never
     * changes a yap's `timestamp`, only genuine create events surface here.
     * Returns an unsubscribe function.
     */
    /**
     * The newest yap that would actually APPEAR in the World feed.
     *
     * @param viewerId the signed-in uid, so a user is never told about their own post.
     *
     * The "New Yaps Dropped" pill compares this against the newest yap already
     * on screen, and the two used to be drawn from different populations: the
     * baseline came from the feed AFTER passesWorldFilter, while this watched
     * the raw collection. So a tribe or forum post that was never cross-posted
     * beat a baseline that deliberately excluded it, and the pill offered to
     * jump to a yap the feed can never show. Worse, because that post never
     * enters the feed the baseline never caught up, so the pill stayed wrong.
     *
     * The same filter the feed uses is applied here. A yap the reader posted
     * themselves is skipped too: the pill exists to announce other people's
     * posts, not your own.
     *
     * The window is wider than the single newest document because the filter
     * can discard several in a row; if every one in the window is filtered out
     * this reports null, which shows no pill rather than a wrong one.
     */
    watchNewestYap: (
        onNewest: (newestIso: string | null) => void,
        viewerId?: string | null,
    ): (() => void) => {
        let unsub: (() => void) | null = null;
        let cancelled = false;
        (async () => {
            try {
                const db = await getDbSafe();
                if (cancelled) return;
                const q = query(collection(db, "yaps"), orderBy("timestamp", "desc"), limit(25));
                unsub = onSnapshot(q, (snap) => {
                    const me = String(viewerId || '');
                    const newest = snap.docs
                        .map((d: any) => ({ id: d.id, ...d.data() }))
                        .filter((y: any) => !y.deleted && !y.is_hidden)
                        // Group posts only count when explicitly cross-posted —
                        // the same rule passesWorldFilter applies to the feed.
                        .filter((y: any) => {
                            const inGroup = y.forumId || y.tribeId || y.topicId;
                            return !inGroup || y.isCrossPosted === true;
                        })
                        .filter((y: any) => !me || String(y.userId || '') !== me)[0] as any;
                    if (!newest || !newest.timestamp) { onNewest(null); return; }
                    const iso = newest.timestamp?.toDate
                        ? newest.timestamp.toDate().toISOString()
                        : (typeof newest.timestamp === 'string' ? newest.timestamp : null);
                    onNewest(iso);
                }, (err) => { console.error("[YapService] watchNewestYap error:", err); });
            } catch (e) {
                console.error("[YapService] watchNewestYap setup failed:", e);
            }
        })();
        return () => { cancelled = true; if (unsub) unsub(); };
    },

    fetchUserShouts: async (userId: string): Promise<ShoutedYap[]> => {
        try {
            const db = await getDbSafe();
            const q = query(collection(db, "shouts"), where("shoutedBy", "==", userId), orderBy("timestamp", "desc"));
            const snapshot = await getDocs(q);

            return snapshot.docs.map(doc => {
                const data = doc.data();
                const timestampStr = data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : new Date().toISOString();
                
                return {
                    id: doc.id,
                    ...data,
                    timestamp: timestampStr,
                    likes: data.metrics?.likes || data.likes || 0,
                    comments: data.metrics?.comments || data.comments || 0,
                } as any;
            });
        } catch (error) {
            console.error("Error fetching user shouts:", error);
            return [];
        }
    },

    /**
     * Does this yap have at least one shout that carries a caption?
     *
     * Gates the "View Shouts" entry point, which should only appear when there
     * is something written to read. It can't be answered from the yap's shout
     * counter: that counts simple reposts too, and a repost stores
     * `shoutCaption: null`.
     *
     * The inequality is what makes this one document read instead of a scan of
     * every shout on the yap — it needs the (originalYapId, shoutCaption)
     * composite index, and falls back to a bounded scan until that exists.
     */
    hasCaptionedShouts: async (yapId: string): Promise<boolean> => {
        if (!yapId) return false;
        // A caption-carrying shout that has been soft-deleted must not light up
        // the button, since the list screen filters those rows out.
        const anyLiveCaption = (docs: any[]) => docs.some(d => {
            const data = d.data() || {};
            if (data.deleted || data.is_hidden) return false;
            return typeof data.shoutCaption === 'string' && data.shoutCaption.trim() !== '';
        });

        try {
            const db = await getDbSafe();
            try {
                const snap = await getDocs(query(
                    collection(db, "shouts"),
                    where("originalYapId", "==", String(yapId)),
                    where("shoutCaption", "!=", null),
                    limit(5),
                ));
                return anyLiveCaption(snap.docs);
            } catch {
                // Composite index not deployed yet — bounded equality scan.
                const snap = await getDocs(query(
                    collection(db, "shouts"),
                    where("originalYapId", "==", String(yapId)),
                    limit(50),
                ));
                return anyLiveCaption(snap.docs);
            }
        } catch (error) {
            console.error("[YapService] hasCaptionedShouts failed:", error);
            return false;
        }
    },

    /**
     * The CAPTIONED shouts of one yap, newest first — the feed behind the
     * "View Shouts" screen. Simple reposts are deliberately absent: the screen
     * exists to read what people wrote, and a repost writes nothing.
     *
     * The inequality narrows the read to captioned shouts server-side, using the
     * same (originalYapId, shoutCaption) index as hasCaptionedShouts. An
     * inequality forces its own field to lead the ordering, so timestamp sorting
     * happens in memory rather than costing a third index field — a single yap's
     * shouts number in the tens.
     *
     * The shouter's name, username, avatar and flag are denormalized onto the
     * shout doc at creation, so rendering the list needs no per-row user lookup.
     */
    fetchShoutsForYap: async (yapId: string): Promise<ShoutedYap[]> => {
        if (!yapId) return [];

        const toShout = (d: any) => {
            const data = d.data();
            const date = data.timestamp?.toDate ? data.timestamp.toDate() : null;
            return {
                id: d.id,
                ...data,
                timestamp: date ? date.toISOString() : new Date().toISOString(),
                time: date ? formatTimeAgo(date) : 'Recently',
                likes: data.metrics?.likes || data.likes || 0,
                comments: data.metrics?.comments || data.comments || 0,
            } as any;
        };

        try {
            const db = await getDbSafe();
            let docs: any[];
            try {
                const snapshot = await getDocs(query(
                    collection(db, "shouts"),
                    where("originalYapId", "==", String(yapId)),
                    where("shoutCaption", "!=", null),
                ));
                docs = snapshot.docs;
            } catch {
                // Composite index not deployed yet — read the yap's shouts and
                // narrow below.
                const snapshot = await getDocs(query(
                    collection(db, "shouts"),
                    where("originalYapId", "==", String(yapId)),
                ));
                docs = snapshot.docs;
            }

            // Soft-deleted shouts never surface (the rule fetchShoutsPage applies
            // to the world feed), and the caption re-check covers both the
            // fallback path above and any doc storing an empty string.
            return docs
                .map(toShout)
                .filter((s: any) => !s.deleted && !s.is_hidden)
                .filter((s: any) => typeof s.shoutCaption === 'string' && s.shoutCaption.trim() !== '')
                .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        } catch (error) {
            console.error("[YapService] fetchShoutsForYap failed:", error);
            return [];
        }
    },

    createYap: async (yap: Partial<Yap>): Promise<Yap> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        if (!auth.currentUser) throw new Error('Must be logged in');

        // 🔒 SECURITY: Rate limit — 5s between posts per user
        _checkRateLimit(auth.currentUser.uid, 'createYap', 5000);

        // 🔒 SECURITY: Fetch user doc and enforce ban at service layer
        const callerDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (callerDoc.exists() && callerDoc.data().status === 'banned') {
            throw new Error('BANNED: Your account has been suspended. You cannot create posts.');
        }

        try {
            // ── Upload any base64 images to Firebase Storage ──────────────────────
            const uploadBase64Image = async (base64: string, index: number): Promise<string> => {
                // Already a real URL (Unsplash, Firebase Storage, etc.) — skip upload
                if (!base64.startsWith('data:')) return base64;
                try {
                    const storage = await getStorageSafe();
                    const uid = auth.currentUser!.uid;
                    const timestamp = Date.now();
                    const path = `yaps/${uid}/${timestamp}-${index}.jpg`;
                    const storageRef = ref(storage, path);
                    // Optimized: Use uploadString for data URLs
                    const snapshot = await uploadString(storageRef, base64, 'data_url');
                    return await getDownloadURL(snapshot.ref);
                } catch (uploadErr) {
                    console.error('[YapService] Image upload failed, skipping image:', uploadErr);
                    return ''; // Omit rather than fail the whole post
                }
            };

            // Handle single image field
            if (yap.image && typeof yap.image === 'string') {
                const url = await uploadBase64Image(yap.image, 0);
                yap = { ...yap, image: url || undefined };
            }

            // Handle images array (multi-image posts)
            if (Array.isArray((yap as any).images) && (yap as any).images.length > 0) {
                const uploaded = await Promise.all(
                    (yap as any).images.map((img: string, i: number) => uploadBase64Image(img, i))
                );
                (yap as any).images = uploaded.filter(Boolean);
            }
            // ─────────────────────────────────────────────────────────────────────
            // Fetch current user profile data
            const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
            const userData = userDoc.exists() ? userDoc.data() : null;

            // Prepare yap data
            const yapData: any = {
                ...yap,
                userId: auth.currentUser.uid,
                timestamp: serverTimestamp(),
                metrics: {
                    likes: 0,
                    comments: 0,
                    shouts: 0,
                    shares: 0
                },
                // Flat fields for compatibility
                likes: 0,
                comments: 0,
                shouts: 0,
                visibility: yap.visibility || 'public',
                rank: userData?.rank || 'user'
            };

            // Handle anonymous yaps
            if (yap.isAnonymous) {
                // Store real author ID for reply matching
                yapData.authorId = auth.currentUser.uid;
                // Set anonymous display fields
                yapData.author = 'Anonymous';
                yapData.username = '@anonymous';
                yapData.avatar = '/anonymous-avatar.png';
                yapData.country = '';
                // Preserve expiration time
                yapData.expiresAt = yap.expiresAt;

                // Update user's anonymous post timestamps
                const now = new Date();
                const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

                // Get current timestamps, filter old ones, and add new one
                const currentTimestamps = (userData?.anonymousPostTimestamps || []) as string[];
                const validTimestamps = currentTimestamps.filter((t: string) => new Date(t) > twentyFourHoursAgo);
                validTimestamps.push(now.toISOString());

                await updateDoc(doc(db, "users", auth.currentUser.uid), {
                    anonymousPostTimestamps: validTimestamps
                });
            } else {
                // Regular yap — use centralized helper to guarantee origin flag ≠ residence
                yapData.authorId = auth.currentUser.uid;
                yapData.author = userData?.name || userData?.displayName || 'User';
                yapData.username = userData?.username || `@user${auth.currentUser.uid.slice(0, 6)}`;
                yapData.avatar = UserService.resolveUserAvatar(userData);
                // 🏳️ ALWAYS use buildYapAuthorFields — never write authorCountryFlag/location inline.
                Object.assign(yapData, buildYapAuthorFields(userData || {}));
            }

            let collectionName = "yaps";
            if (yap.tribeId) {
                collectionName = "tribe_posts";
                yapData.isTribeOnly = true;
            } else if (yap.forumId) {
                collectionName = "forum_posts";
                yapData.isForumOnly = true;
            }

            const docRef = await addDoc(collection(db, collectionName), yapData);

            // Increment group metrics if applicable. A forum "topic" cross-post
            // lives in the main `yaps` collection (so it shows in YapTab) but must
            // still bump the tagged forum's activity so it surfaces in that forum's
            // feed and trending. Treat forumTopic.forumId like a forum target here.
            const crossPostForumId = !yap.forumId && !yap.tribeId ? yap.forumTopic?.forumId : undefined;
            if (yap.tribeId || yap.forumId || crossPostForumId) {
                try {
                    const groupCollection = yap.tribeId ? "tribes" : "forums";
                    const groupId = yap.tribeId || yap.forumId || crossPostForumId;
                    const groupRef = doc(db, groupCollection, groupId!);
                    await updateDoc(groupRef, {
                        postCount: increment(1),
                        lastActivityAt: serverTimestamp()
                    });
                } catch (metricError) {
                    console.warn("[YapService] Failed to update group metrics:", metricError);
                }
            }

            // Update user's recentYap in their profile
            if (!yap.isAnonymous) {
                try {
                    await updateDoc(doc(db, "users", auth.currentUser.uid), {
                        recentYap: {
                            id: docRef.id,
                            content: yap.content,
                            timestamp: new Date().toISOString(),
                            likes: 0,
                            comments: 0
                        }
                    });
                } catch (profileError) {
                    console.warn("[YapService] Failed to update user recentYap:", profileError);
                }
            }

            // ─── 4. Process Mentions (Non-blocking) ──────────────────────────────────
            if (yap.content && !yap.isAnonymous) {
                _processMentions(yap.content, docRef.id, 'yap', auth.currentUser!.uid, yapData.author)
                    .catch(e => console.warn('[YapService] Mention processing failed:', e));
            }

            // Return the created yap with ID and estimated time
            return {
                id: docRef.id,
                ...yapData,
                time: 'Now',
                timestamp: new Date().toISOString() // Approximate for immediate UI update
            } as Yap;
        } catch (error) {
            console.error("Error creating yap:", error);
            throw error;
        }
    },

    // Helper to find document in multiple collections
    findPostRef: async (id: string) => {
        const db = await getDbSafe();
        const collections = ["yaps", "forum_posts", "tribe_posts", "shouts", "events"];
        for (const col of collections) {
            try {
                const ref = doc(db, col, id);
                const snap = await getDoc(ref);
                if (snap.exists()) {
                    return { ref, data: snap.data(), collectionName: col };
                }
            } catch (e) {
                // Ignore and try next
            }
        }
        return null;
    },

    fetchYapById: async (yapId: string): Promise<Yap | null> => {
        const db = await getDbSafe();
        const collections = ["yaps", "forum_posts", "tribe_posts", "shouts"];

        // Per-iteration try/catch — a permission error or transient failure on one
        // collection must NOT abort the search. Push-notification cold-launches
        // are especially prone to this when auth restoration races document reads.
        for (const col of collections) {
            try {
                const yapRef = doc(db, col, yapId);
                const yapSnap = await getDoc(yapRef);
                if (yapSnap.exists()) {
                    const data = yapSnap.data();
                    const isoTimestamp = data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : new Date().toISOString();
                    return {
                        id: yapSnap.id,
                        ...data,
                        time: data.timestamp?.toDate ? formatTimeAgo(data.timestamp.toDate()) : 'Recently',
                        timestamp: isoTimestamp,
                        likes: data.metrics?.likes || data.likes || 0,
                        comments: data.metrics?.comments || data.comments || 0,
                        shouts: data.metrics?.shouts || data.shouts || 0,
                    } as Yap;
                }
            } catch (e) {
                console.warn(`[YapService.fetchYapById] read failed for ${col}/${yapId} — trying next collection`, e);
            }
        }
        return null;
    },
    
    fetchYapReplies: async (yapId: string): Promise<any[]> => {
        try {
            const db = await getDbSafe();
            const collections = ["yaps", "forum_posts", "tribe_posts", "shouts"];
            
            for (const col of collections) {
                const repliesRef = collection(db, col, yapId, "comments");
                try {
                    const q = query(repliesRef, orderBy("timestamp", "desc"));
                    const snapshot = await getDocs(q);
                    if (!snapshot.empty) {
                        const results = snapshot.docs.map(doc => {
                            const data = doc.data();
                            console.log(`[YapService] fetchYapReplies: Reply ${doc.id} data:`, {
                                author: data.author,
                                likes: data.likes,
                                likedBy: data.likedBy,
                                hasLikedBy: !!data.likedBy,
                                likedByLength: data.likedBy?.length || 0
                            });
                            return {
                                ...data,
                                id: doc.id // Ensure doc.id acts as the true source of truth
                            };
                        });
                        console.log(`[YapService] fetchYapReplies: Found ${results.length} replies in ${col}`);
                        return results;
                    }
                } catch (e) {
                    // Try next collection or simple fetch
                }
            }

            // Simple fetch fallback from any collection
            for (const col of collections) {
                const snapshot = await getDocs(collection(db, col, yapId, "comments"));
                if (!snapshot.empty) {
                    const results = snapshot.docs.map(doc => ({
                        ...doc.data(),
                        id: doc.id
                    }));
                    console.log(`[YapService] fetchYapReplies (fallback): Found ${results.length} replies in ${col}`);
                    return results.sort((a: any, b: any) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
                }
            }
            console.log(`[YapService] fetchYapReplies: No replies found for ${yapId} in any collection`);
            return [];
        } catch (error) {
            console.error("[YapService] Critical error fetching replies:", error);
            return [];
        }
    },

    shareYap: async (yapId: string): Promise<void> => {
        try {
            const db = await getDbSafe();
            const auth = await getAuthSafe();
            const result = await YapService.findPostRef(yapId);
            if (!result) {
                console.warn(`[YapService] shareYap: Post ${yapId} not found for increment.`);
                return;
            }

            const { ref: yapRef, data: yapData, collectionName } = result;
            const batch = writeBatch(db);

            // Increment the share counter atomically
            batch.update(yapRef, {
                "metrics.shares": increment(1), // Separate DMs/shares from shouts
                lastActivityAt: serverTimestamp()
            });

            await batch.commit();
            console.log(`[YapService] shareYap: Incremented share count for ${yapId}`);

            // Record WHO shared, not just that a share happened. The counter alone
            // carried no actor, so there was no way to ask "did this reader share
            // this yap?" — which the World feed's recently-interacted hide needs.
            // Mirrors the `likes` subcollection: doc id = uid, so re-sharing is
            // idempotent. Best-effort; the share itself already succeeded.
            // Only /yaps and /shouts carry a shares subcollection: group posts
            // cannot be shared (canShareOrShout blocks it), so writing one for
            // them would just trip a rules denial for a path nothing reads.
            const userId = auth.currentUser?.uid;
            if (userId && FeedHideService.isHideable(collectionName)) {
                try {
                    await setDoc(doc(db, collectionName, yapId, 'shares', userId), {
                        userId,
                        timestamp: serverTimestamp(),
                    });
                } catch (shareDocErr) {
                    console.warn('[YapService] share subcollection write failed:', shareDocErr);
                }
                void FeedHideService.recordHide(yapId, 'share', collectionName, yapData);
            }
        } catch (error) {
            console.error("[YapService] Failed to increment share count:", error);
        }
    },

    /**
     * How long a post stays editable. Mirrored in firestore.rules — a client
     * cannot be trusted with this, and the rule is what actually enforces it;
     * this exists so the UI can hide an option that would be refused, and so a
     * late attempt fails with a sentence rather than a raw permission error.
     */
    EDIT_WINDOW_MS: 15 * 60 * 1000,

    /** Milliseconds left in the edit window, or 0 once it has closed. */
    editWindowRemaining: (timestamp: any): number => {
        if (!timestamp) return 0;
        const ms = typeof timestamp?.toMillis === 'function'
            ? timestamp.toMillis()
            : new Date(timestamp).getTime();
        if (Number.isNaN(ms)) return 0;
        return Math.max(0, ms + YapService.EDIT_WINDOW_MS - Date.now());
    },

    /**
     * Change what a post says, within the edit window.
     *
     * Works for a yap or a reply: findPostRef already resolves an id across
     * yaps, forum_posts and tribe_posts, and a reply is addressed by its parent.
     *
     * Writes `edited` and `editedAt` alongside the text. Showing that something
     * was changed is the point — silent edits let a post be rewritten under
     * replies that were answering something else.
     */
    editPost: async (
        postId: string,
        newContent: string,
        options?: { replyId?: string; collectionName?: string },
    ): Promise<void> => {
        const auth = await getAuthSafe();
        if (!auth.currentUser) throw new Error('You need to be signed in.');
        const db = await getDbSafe();

        const trimmed = (newContent || '').trim();
        if (!trimmed) throw new Error('A post cannot be empty.');
        // 1000 characters; Legion has no limit (constants/posts.ts).
        if (trimmed.length > YAP_MAX_CHARACTERS) {
            const me = await getDoc(doc(db, 'users', auth.currentUser.uid));
            if (exceedsYapLimit(trimmed, me.exists() ? me.data() : null)) {
                throw new Error(`Keep it under ${YAP_MAX_CHARACTERS} characters.`);
            }
        }

        const found = options?.collectionName
            ? { ref: doc(db, options.collectionName, postId) }
            : await YapService.findPostRef(postId);
        if (!found) throw new Error('That post no longer exists.');

        const targetRef = options?.replyId
            ? doc(found.ref, 'comments', options.replyId)
            : found.ref;

        const snap = await getDoc(targetRef);
        if (!snap.exists()) throw new Error('That post no longer exists.');
        const data: any = snap.data();

        if (String(data.userId || data.authorId || '') !== auth.currentUser.uid) {
            throw new Error('You can only edit your own posts.');
        }
        if (String(data.content || '').trim() === trimmed) return;   // nothing changed

        // Checked here as well as in the rules, so a late edit says why instead
        // of surfacing "Missing or insufficient permissions".
        if (YapService.editWindowRemaining(data.timestamp) <= 0) {
            throw new Error('The 15 minute window to edit this has passed.');
        }

        await updateDoc(targetRef, {
            content: trimmed,
            edited: true,
            editedAt: serverTimestamp(),
        });
    },

    addReply: async (yapId: string, reply: any): Promise<string> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        if (!auth.currentUser) throw new Error("Must be logged in");

        // 🔒 SECURITY: Rate limit — 3s between replies
        _checkRateLimit(auth.currentUser.uid, 'addReply', 3000);

        // 🔒 SECURITY: Enforce ban at service layer
        const callerDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (callerDoc.exists() && callerDoc.data().status === 'banned') {
            throw new Error('BANNED: Your account has been suspended. You cannot post replies.');
        }

        console.log(`[YapService] Adding reply to post ${yapId}`);

        // Step 1: Find the post (critical - must succeed)
        const result = await YapService.findPostRef(yapId);
        if (!result) throw new Error("Post not found");
        const { ref: yapRef, data: yapData } = result;

        // Step 2: Add reply to the discovered subcollection (critical - must succeed)
        const repliesRef = collection(yapRef, "comments");
        // Remove client-side ID to prevent it from being saved and overwriting the real doc ID on fetch
        const { id: _, ...replyDataWithoutId } = reply;

        // Strip undefined fields to prevent Firestore 'Unsupported field value: undefined' errors
        Object.keys(replyDataWithoutId).forEach(key => {
            if (replyDataWithoutId[key] === undefined) {
                delete replyDataWithoutId[key];
            }
        });

        const newReplyData = {
            ...replyDataWithoutId,
            userId: auth.currentUser.uid,
            // Which collection this reply hangs off.
            //
            // Replies live at {collection}/{postId}/comments/{id}, and a
            // collectionGroup query over `comments` cannot tell a yap's replies
            // from a tribe or forum post's — the documents carried nothing to
            // distinguish them. YapStats counts replies "on yaps and shouts",
            // so without this the count silently includes group replies.
            //
            // Historical replies have no parentType, but the recap window is
            // 24 hours, so counts are correct within a day of this shipping.
            parentType: result.collectionName,
            timestamp: serverTimestamp(),
            createdAt: serverTimestamp(),
            metrics: {
                likes: 0,
                comments: 0,
                shouts: 0,
                shares: 0
            },
            likes: 0
        };
        
        const docRef = await addDoc(repliesRef, newReplyData);
        console.log(`[YapService] Reply document created successfully with ID: ${docRef.id}`);

        // Replying counts as engaging with the post, so it holds it out of the
        // replier's World feed too. findPostRef already gave us the collection and
        // the doc data, so normalizing a shout to its original costs no extra read.
        void FeedHideService.recordHide(yapId, 'reply', result.collectionName, yapData);

        try {
            await updateDoc(yapRef, {
                "metrics.comments": increment(1),
                comments: increment(1)
            });
            console.log(`[YapService] Metrics updated successfully`);
        } catch (metricsError: any) {
            console.error("[YapService] Metrics update failed (non-critical):", {
                code: metricsError.code,
                message: metricsError.message
            });
            // Don't throw - the reply was created successfully
        }

        // Step 4: Send notification (non-critical - log but don't throw)
        if (yapData.userId && yapData.userId !== auth.currentUser.uid) {
            try {
                let notifType = 'YAP_COMMENT';
                if (result.collectionName === 'forum_posts') notifType = 'FORUM_COMMENT';
                else if (result.collectionName === 'tribe_posts') notifType = 'TRIBE_COMMENT';

                // 🎯 PERSONAL CONTEXT: Include group name + post snippet so the notification feels specific
                const groupId = yapData.tribeId || yapData.forumId || null;
                const groupName = yapData.tribeName || yapData.forumName || null;
                const postSnippet = yapData.content
                    ? String(yapData.content).slice(0, 60) + (yapData.content.length > 60 ? '...' : '')
                    : null;
                const replySnippet = reply.content
                    ? String(reply.content).slice(0, 60) + (reply.content.length > 60 ? '...' : '')
                    : null;

                // Build a human message: "[Author] replied: \"[snippet]\"" or fallback
                let personalMessage = `${reply.author} replied to your post`;
                if (notifType === 'TRIBE_COMMENT' && groupName) {
                    personalMessage = replySnippet
                        ? `${reply.author} replied in ${groupName}: "${replySnippet}"`
                        : `${reply.author} replied to your post in ${groupName}`;
                } else if (notifType === 'FORUM_COMMENT' && groupName) {
                    personalMessage = replySnippet
                        ? `${reply.author} replied in ${groupName}: "${replySnippet}"`
                        : `${reply.author} replied to your post in ${groupName}`;
                } else if (notifType === 'yap_comment' && replySnippet) {
                    personalMessage = `${reply.author} replied: "${replySnippet}"`;
                }

                // Build personalised title
                let notifTitle = 'New Comment';
                if (notifType === 'TRIBE_COMMENT') notifTitle = groupName ? `New Reply in ${groupName}` : 'New Tribe Reply';
                else if (notifType === 'FORUM_COMMENT') notifTitle = groupName ? `New Reply in ${groupName}` : 'New Forum Reply';

                await NotificationService.sendNotification(
                    yapData.userId,
                    notifType.toUpperCase(),
                    notifTitle,
                    personalMessage,
                    {
                        yapId,
                        postId: yapId,
                        fromUserId: auth.currentUser.uid,
                        fromUserName: reply.author,
                        fromUserAvatar: reply.avatar || null,
                        targetId: yapId,
                        // 🎯 Group context for deep-linking to the correct tribe/forum
                        tribeId:   notifType === 'TRIBE_COMMENT'  ? (groupId || null) : null,
                        tribeName: notifType === 'TRIBE_COMMENT'  ? (groupName || null) : null,
                        forumId:   notifType === 'FORUM_COMMENT'  ? (groupId || null) : null,
                        forumName: notifType === 'FORUM_COMMENT'  ? (groupName || null) : null,
                        postSnippet,
                    }
                );
                console.log(`[YapService] Notification sent successfully`);
            } catch (notiError) {
                console.error("[YapService] Notification failed (non-critical):", notiError);
                // Don't throw - the reply was created successfully
            }
        }

        // Step 5: Process Mentions (Non-blocking)
        // Pass the PARENT post id as the source — when the recipient taps the
        // mention notification we want to land on the post detail (where the
        // mentioning reply is visible), not on a non-existent /yap/{commentId}.
        //
        // Dedup: the parent yap author already received a COMMENT notification
        // above. If they're also @-mentioned in the reply, skip the MENTION
        // notification so they don't get two pings for the same action.
        if (reply.content) {
            const skip = yapData.userId && yapData.userId !== auth.currentUser.uid ? [String(yapData.userId)] : [];
            _processMentions(reply.content, yapId, 'comment', auth.currentUser!.uid, reply.author, skip)
                .catch(e => console.warn('[YapService] Reply mention processing failed:', e));
        }

        return docRef.id;
    },

    likeReply: async (yapId: string, replyId: string): Promise<void> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        if (!auth.currentUser) throw new Error("Must be logged in");
        const userId = auth.currentUser.uid;

        console.log(`[YapService] Liking reply ${replyId} on post ${yapId}`);

        // 1. Locate parent post
        const result = await YapService.findPostRef(yapId);
        if (!result) {
            console.warn(`[YapService] likeReply: findPostRef failed for ${yapId}. Assumed 'yaps' collection.`);
            var yapRef = doc(db, 'yaps', yapId);
        } else {
            var yapRef = result.ref;
        }

        // 2. Locate reply within parent post (check both 'comments' and 'replies' subcollections)
        let replyRef: any = null;
        const subCollections = ["comments", "replies"];
        
        for (const sub of subCollections) {
            const potentialRef = doc(db, yapRef.path, sub, replyId);
            try {
                const snap = await getDoc(potentialRef);
                if (snap.exists()) {
                    replyRef = potentialRef;
                    console.log(`[YapService] likeReply: FOUND reply in subcollection: ${sub}`);
                    break;
                }
            } catch (e) {
                // Keep looking
            }
        }

        if (!replyRef) {
             console.error(`[YapService] likeReply FAILED to find reply ${replyId} under ${yapRef.path}`);
             throw new Error("Reply not found in any expected subcollection.");
        }
        
        console.log(`[YapService] likeReply: Targeting verified reply at ${replyRef.path}`);
        console.log(`[YapService] likeReply: Targeting reply at ${replyRef.path}`);

        let replyAuthorId: string | undefined;
        let replyContent: string | undefined;
        let alreadyLiked = false;

        await runTransaction(db, async (transaction) => {
            const freshSnap = await transaction.get(replyRef);
            if (!freshSnap.exists()) {
                 console.error(`[YapService] likeReply FAILED to find reply at ${replyRef.path}`);
                 
                 // Debug: List all docs in this subcollection to see if we have an ID mismatch
                 try {
                    const debugSnapshot = await getDocs(collection(db, yapRef.path, "comments"));
                    console.log(`[YapService] DEBUG: Available reply IDs in ${yapRef.path}/comments:`, 
                        debugSnapshot.docs.map(d => d.id));
                 } catch(e) {
                    console.error("[YapService] Failed to run debug listing:", e);
                 }

                 throw new Error("Reply not found");
            }
            const freshData: any = freshSnap.data() || {};
            const likedBy = freshData.likedBy || [];
            
            console.log(`[YapService] likeReply transaction START:`, { likedByCount: likedBy.length, userId });

            replyAuthorId = freshData.userId;
            replyContent = freshData.content;

            if (!likedBy.some((id: any) => String(id) === String(userId))) {
                console.log(`[YapService] likeReply: Adding like for ${userId}`);
                transaction.update(replyRef, {
                    "metrics.likes": increment(1),
                    likes: increment(1),
                    likedBy: arrayUnion(String(userId))
                });
            } else {
                console.warn(`[YapService] likeReply: Already liked by ${userId}`);
                alreadyLiked = true;
            }
        });

        // Bank the like for analytics, same as yap and shout likes.
        //
        // likeReply was the one like path that wrote no like_event, so reply
        // likes were invisible to anything counting them — including the
        // YapStats recap, which reports "likes sent". Best-effort and
        // non-blocking, matching the other three writers: a lost analytics row
        // must never take down the like it describes.
        if (!alreadyLiked) {
            addDoc(collection(db, 'like_events'), {
                actorId: userId,
                targetId: replyId,
                targetType: 'replies',
                parentId: yapId,
                action: 'like',
                ts: serverTimestamp()
            }).catch(() => {});
        }

        // Send notification if it's a new like and not self-like
        if (!alreadyLiked && replyAuthorId && replyAuthorId !== userId) {
            try {
                const isVoice = replyContent === '[Voice Reply]';

                // The liker's actual name, not "Someone".
                //
                // This was hardcoded, so a reply like was anonymous no matter
                // who sent it — and aggregation reads fromUserName to build
                // "X and N others", so without it every stack said "Someone".
                let likerName = 'Someone';
                let likerAvatar = '';
                try {
                    const likerSnap = await getDoc(doc(db, 'users', userId));
                    if (likerSnap.exists()) {
                        const ld = likerSnap.data() as any;
                        likerName = ld.name || ld.username || 'Someone';
                        likerAvatar = getUserAvatar(ld);
                    }
                } catch { /* the notification is still worth sending */ }

                const message = isVoice
                    ? `${likerName} liked your voice reply`
                    : `${likerName} liked your reply`;

                await NotificationService.sendNotification(
                    replyAuthorId,
                    // Its own type, not YAP_LIKE. targetId decides what a
                    // notification stacks against, and as a YAP_LIKE this
                    // targeted the YAP — so a like on your reply merged with
                    // likes on the post itself and came out reading "liked your
                    // post". Keyed on the reply, likes on the same reply stack
                    // together and nothing else joins them.
                    'REPLY_LIKE',
                    'New Like',
                    message,
                    {
                        yapId,
                        replyId,
                        targetId: replyId,
                        fromUserId: userId,
                        fromUserName: likerName,
                        fromUserAvatar: likerAvatar,
                    }
                );
                console.log(`[YapService] Reply like notification sent`);
            } catch (notiError) {
                console.error("[YapService] Reply like notification failed (non-critical):", notiError);
            }
        }
    },

    unlikeReply: async (yapId: string, replyId: string): Promise<void> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        if (!auth.currentUser) throw new Error("Must be logged in");
        const userId = auth.currentUser.uid;

        console.log(`[YapService] Unliking reply ${replyId} on post ${yapId}`);

        const result = await YapService.findPostRef(yapId);
        if (!result) {
            console.error(`[YapService] unlikeReply FAILED: Parent post ${yapId} not found.`);
            throw new Error("Parent post not found.");
        }
        const { ref: yapRef } = result;
        
        let replyRef: any = null;
        const subCollections = ["comments", "replies"];
        
        for (const sub of subCollections) {
            const potentialRef = doc(db, yapRef.path, sub, replyId);
            try {
                const snap = await getDoc(potentialRef);
                if (snap.exists()) {
                    replyRef = potentialRef;
                    break;
                }
            } catch (e) {}
        }

        if (!replyRef) {
             throw new Error("Reply not found");
        }

        await runTransaction(db, async (transaction) => {
            const freshSnap = await transaction.get(replyRef);
            if (!freshSnap.exists()) {
                 console.error(`[YapService] unlikeReply FAILED to find reply at ${replyRef.path}`);
                 throw new Error("Reply not found");
            }
            const freshData: any = freshSnap.data() || {};
            const likedBy = freshData.likedBy || [];

            if (likedBy.some((id: any) => String(id) === String(userId))) {
                transaction.update(replyRef, {
                    "metrics.likes": increment(-1),
                    likes: increment(-1),
                    likedBy: arrayRemove(String(userId))
                });
            }
        });
    },

    deleteReply: async (yapId: string, replyId: string): Promise<void> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        if (!auth.currentUser) throw new Error("Must be logged in to delete a reply.");
        const currentUserId = auth.currentUser.uid;

        console.log(`[YapService] deleteReply START: Post=${yapId}, Reply=${replyId}`);

        // 1. Locate parent post using robust helper
        const result = await YapService.findPostRef(yapId);
        if (!result) {
            console.error(`[YapService] deleteReply FAILED: Parent post ${yapId} not found.`);
            throw new Error(`Could not find the parent post in the database. Please refresh the page.`);
        }
        const { ref: targetYapRef } = result;

        // 2. Locate reply within parent post (check both 'comments' and 'replies' subcollections)
        let targetReplyRef: any = null;
        let foundSubcollection: string | null = null;
        const subCollections = ["comments", "replies"];

        for (const sub of subCollections) {
            const replyRef = doc(db, targetYapRef.path, sub, replyId);
            console.log(`[YapService] Checking for reply at: ${replyRef.path}`);
            try {
                const replySnap = await getDoc(replyRef);
                if (replySnap.exists()) {
                    targetReplyRef = replyRef;
                    foundSubcollection = sub;
                    console.log(`[YapService] FOUND reply in subcollection: ${sub}`);
                    break;
                }
            } catch (e: any) {
                console.warn(`[YapService] Error checking subcollection ${sub}:`, e.message);
            }
        }

        if (!targetReplyRef || !foundSubcollection) {
            console.error(`[YapService] deleteReply FAILED: Reply ${replyId} not found under post ${yapId} in any expected subcollection.`);
            throw new Error(`Could not find this reply in the database (${yapId}/${replyId}). Please refresh the page.`);
        }

        // 3. Cascade: walk the subcollection and collect every descendant ID so
        // deleting a parent reply also removes its entire conversation chain.
        // (Full subcollection scan — reply counts per yap are small in practice.)
        const subColRef = collection(db, targetYapRef.path, foundSubcollection);
        const allRepliesSnap = await getDocs(subColRef);
        const allReplies = allRepliesSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));

        const toDelete = new Set<string>([replyId]);
        let frontier: string[] = [replyId];
        while (frontier.length > 0) {
            const next: string[] = [];
            for (const parentId of frontier) {
                for (const r of allReplies) {
                    if (r.parentReplyId === parentId && !toDelete.has(r.id)) {
                        toDelete.add(r.id);
                        next.push(r.id);
                    }
                }
            }
            frontier = next;
        }
        console.log(`[YapService] deleteReply cascade: ${toDelete.size} reply doc(s) will be removed.`);

        console.log(`[YapService] Proceeding to transaction for ${targetReplyRef.path}`);
        await runTransaction(db, async (transaction) => {
            const replySnap = await transaction.get(targetReplyRef);
            if (!replySnap.exists()) throw new Error("Reply document disappeared during transaction.");

            const replyData = replySnap.data() as any || {};
            const postSnap = await transaction.get(targetYapRef);
            if (!postSnap.exists()) throw new Error("Parent post not found.");

            const postData = postSnap.data() as any || {};

            // Authorization Check: Author of reply OR owner of parent post OR Moderator (Watcher/Legion)
            const isReplyAuthor = String(replyData.userId || replyData.authorId) === String(currentUserId);
            const isPostOwner = String(postData.userId || postData.shoutedBy) === String(currentUserId);

            const userDoc = await transaction.get(doc(db, "users", currentUserId));
            const userRank = userDoc.exists() ? userDoc.data().rank : 'user';
            const isModerator = userRank === 'watcher' || userRank === 'legion';

            console.log(`[YapService] Auth check result:`, { isReplyAuthor, isPostOwner, isModerator, currentUserId });

            if (!isReplyAuthor && !isPostOwner && !isModerator) {
                throw new Error("Permission denied. You are not authorized to delete this reply.");
            }

            // Delete the reply AND every descendant in the chain.
            for (const id of toDelete) {
                transaction.delete(doc(db, targetYapRef.path, foundSubcollection!, id));
            }

            transaction.update(targetYapRef, {
                "metrics.comments": increment(-toDelete.size),
                comments: increment(-toDelete.size)
            });
            console.log(`[YapService] deleteReply SUCCESS: ${toDelete.size} reply doc(s) deleted (root ${replyId}).`);
        });
    },

    deleteYap: async (yapId: string): Promise<void> => {
        try {
            const db = await getDbSafe();
            const result = await YapService.findPostRef(yapId);
            if (!result) return;
            const { ref, data, collectionName } = result;

            // 1. If it's a shout, decrement original post metrics
            if (collectionName === 'shouts' && data.originalYapId) {
                try {
                    const originalResult = await YapService.findPostRef(data.originalYapId);
                    if (originalResult) {
                        await updateDoc(originalResult.ref, {
                            "metrics.shouts": increment(-1),
                            shouts: increment(-1)
                        });
                        console.log(`[YapService] Decremented shout count for original yap ${data.originalYapId}`);
                    }
                } catch (err) {
                    console.warn("[YapService] Failed to decrement original yap shouts on shout delete:", err);
                }
            }

            // 2. Delete the document itself
            await deleteDoc(ref);

            // 3. Update all shouts that embed this yap (if what we deleted was a regular yap)
            if (collectionName !== 'shouts') {
                try {
                    const shoutsQuery = query(collection(db, "shouts"), where("originalYapId", "==", yapId));
                    const shoutsSnap = await getDocs(shoutsQuery);
                    const batch = writeBatch(db);
                    shoutsSnap.docs.forEach(docSnap => {
                        batch.update(docSnap.ref, {
                            "originalYap.content": "This yap has been deleted",
                            "originalYap.deleted": true,
                            "originalYap.images": [],
                            "originalYap.image": null
                        });
                    });
                    if (!shoutsSnap.empty) {
                        await batch.commit();
                        console.log(`[YapService] Updated ${shoutsSnap.size} shouts to reflect deleted yap ${yapId}`);
                    }
                } catch (shoutUpdateError) {
                    console.warn("[YapService] Failed to update referencing shouts on delete:", shoutUpdateError);
                }
            }

            // 4. Update the user document to clear recentYap if it was this one
            const userId = data.userId || data.shoutedBy || data.authorId;
            if (userId) {
                try {
                    const userRef = doc(db, 'users', userId);
                    const userDoc = await getDoc(userRef);
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        // Check if either the ID matches OR (fallback for old data) the content matches exactly
                        if (userData.recentYap && (userData.recentYap.id === yapId || userData.recentYap.content === data.content)) {
                            await updateDoc(userRef, {
                                recentYap: null
                            });
                            console.log(`[YapService] Cleared recentYap for user ${userId}`);
                        }
                    }
                } catch (profileError) {
                    console.warn("[YapService] Failed to clear user recentYap on delete:", profileError);
                }
            }
        } catch (error) {
            console.error("[YapService] Detailed delete error:", error);
        }
    },


    // Delete Yap (Moderator/Owner) — SOFT DELETE
    deleteYapAsModerator: async (yapId: string, userId: string, reason?: string): Promise<void> => {
        const db = await getDbSafe();
        
        try {
            const result = await YapService.findPostRef(yapId);
            if (!result) throw new Error("Post not found");
            const { ref: yapRef, data: yapData } = result;

            // ✅ SOFT DELETE: flag the document instead of destroying it
            await updateDoc(yapRef, {
                deleted: true,
                status: 'archived',
                deletedReason: reason || 'Removed by moderator',
                deletedBy: userId,
                deletedAt: serverTimestamp(),
            });

            // Update all shouts that embed this yap to show tombstone
            try {
                const shoutsQuery = query(collection(db, "shouts"), where("originalYapId", "==", yapId));
                const shoutsSnap = await getDocs(shoutsQuery);
                const batch = writeBatch(db);
                shoutsSnap.docs.forEach(doc => {
                    batch.update(doc.ref, {
                        "originalYap.content": "This yap has been deleted",
                        "originalYap.deleted": true,
                        "originalYap.images": [],
                        "originalYap.image": null
                    });
                });
                if (!shoutsSnap.empty) {
                    await batch.commit();
                    console.log(`[YapService] Updated ${shoutsSnap.size} shouts to reflect moderator deletion of yap ${yapId}`);
                }
            } catch (shoutUpdateError) {
                console.warn("[YapService] Failed to update referencing shouts on moderator delete:", shoutUpdateError);
            }

            // 📢 Dispatch global event for instant UI removal
            window.dispatchEvent(new CustomEvent('yapDeleted', { detail: { yapId } }));
            
            // Note: callers (e.g. LegionDeleteModal) are responsible for sending
            // the author notification with full context (punishment info, reason).
        } catch (error) {
            console.error("[YapService] Detailed moderator delete error:", error);
            throw error;
        }
    },

    deleteMockYaps: async (): Promise<void> => {
        // ... existing
        try {
            const db = await getDbSafe();
            const q = query(collection(db, "yaps"));
            const snapshot = await getDocs(q);

            const mockIds = ['1', '2', '3', '4', '5', 'anon1', 'anon2', 'anon3'];
            const mockAuthors = ['Kwame Mensah', 'Amara Okafor', 'Zainab Ali', 'Thabo Mbeki', 'Fatima Diallo'];

            const batch = writeBatch(db);
            let count = 0;

            snapshot.docs.forEach((doc) => {
                const data = doc.data();
                if (mockIds.includes(doc.id) || mockAuthors.includes(data.author)) {
                    batch.delete(doc.ref);
                    count++;
                }
            });

            if (count > 0) {
                await batch.commit();
                console.log(`Deleted ${count} mock yaps.`);
            } else {
                console.log('No mock yaps found.');
            }
        } catch (error) {
            console.error("Error deleting mock yaps:", error);
            throw error;
        }
    },

    // 📌 LEGION FEED PIN: Pin a yap to the very top of the world timeline.
    // Only one Legion feed-pin is active at a time globally.
    pinYapToFeed: async (yapId: string, legionId: string, legionUsername: string): Promise<void> => {
        const db = await getDbSafe();
        try {
            const { getDoc, getDocs, query, collection, where, updateDoc, doc, serverTimestamp } = await import('firebase/firestore');

            // 1. Clear any existing feed-pin from this Legion (they can only have 1 at a time)
            const existingQ = query(
                collection(db, 'yaps'),
                where('pinnedToFeedBy', '==', legionId)
            );
            const existingSnap = await getDocs(existingQ);
            for (const d of existingSnap.docs) {
                await updateDoc(doc(db, 'yaps', d.id), {
                    pinnedToFeed: false,
                    pinnedToFeedBy: null,
                    pinnedToFeedAt: null,
                    pinnedToFeedByUsername: null,
                });
            }

            // 2. Resolve the target, following a shout to the yap it quotes.
            //
            // pinnedToFeed is read off the yaps collection by the World feed, so
            // a pin written onto a shout document sets a flag nothing reads.
            // This used to throw "Target yap for pinning does not exist" the
            // moment a Legion mod pinned a shout, because the id was looked up
            // in `yaps` and a shout id is never there.
            //
            // Callers should pass the resolved id (see pinTargetId in
            // utils/postMenuOptions), but resolving here too means the failure
            // cannot come back through some other path.
            let targetYapId = yapId;
            let yapRef = doc(db, 'yaps', targetYapId);
            let yapDoc = await getDoc(yapRef);

            if (!yapDoc.exists()) {
                const shoutDoc = await getDoc(doc(db, 'shouts', yapId));
                const originalId = shoutDoc.exists()
                    ? (shoutDoc.data()?.originalYapId || shoutDoc.data()?.originalYap?.id)
                    : null;

                if (originalId) {
                    targetYapId = String(originalId);
                    yapRef = doc(db, 'yaps', targetYapId);
                    yapDoc = await getDoc(yapRef);
                }
            }

            if (!yapDoc.exists()) {
                throw new Error("Target yap for pinning does not exist.");
            }

            const yapData = yapDoc.data();

            // 3. Pin the new yap
            await updateDoc(yapRef, {
                pinnedToFeed: true,
                pinnedToFeedBy: legionId,
                pinnedToFeedByUsername: legionUsername,
                pinnedToFeedAt: serverTimestamp(),
            });

            // 4. 📣 BROADCAST "Daily Yap" notification
            // Deprecated: Client-side O(N) broadcast is slow. 
            // The Cloud Function onYapPinned in functions/index.js now handles this 
            // automatically when it sees 'pinnedToFeed' set to true.
            console.log('[YapService] Yap pinned. Server-side broadcast will handle notifications.');

        } catch (error) {
            console.error('[YapService] pinYapToFeed error:', error);
            throw error;
        }
    },

    unpinYapFromFeed: async (yapId: string): Promise<void> => {
        const db = await getDbSafe();
        try {
            const { updateDoc, doc } = await import('firebase/firestore');
            await updateDoc(doc(db, 'yaps', yapId), {
                pinnedToFeed: false,
                pinnedToFeedBy: null,
                pinnedToFeedAt: null,
                pinnedToFeedByUsername: null,
            });
        } catch (error) {
            console.error('[YapService] unpinYapFromFeed error:', error);
            throw error;
        }
    },

    // ── TRENDING YAPS ─────────────────────────────────────────────────────────
    // Unlike pinYapToFeed (one pin per Legion), multiple Yaps can be trending
    // simultaneously. No auto-clear of existing trending Yaps on markAsTrending.

    markAsTrending: async (yapId: string, legionId: string): Promise<void> => {
        try {
            // Shouts live in the 'shouts' collection, not 'yaps'. Use findPostRef so
            // marking a shout as trending works the same as marking a regular yap.
            const result = await YapService.findPostRef(yapId);
            if (!result) throw new Error('Post not found in any collection.');
            await updateDoc(result.ref, {
                isTrending: true,
                trendingSetAt: serverTimestamp(),
                trendingSetBy: legionId,
            });
        } catch (error) {
            console.error('[YapService] markAsTrending error:', error);
            throw error;
        }
    },

    /**
     * Moderator curation of the logged-out World preview. Only the preview
     * reads this; signed-in members still see the post everywhere.
     */
    setHiddenFromPreview: async (yapId: string, hidden: boolean, moderatorId: string): Promise<void> => {
        const result = await YapService.findPostRef(yapId);
        if (!result) throw new Error('Post not found in any collection.');
        await updateDoc(result.ref, {
            hiddenFromPreview: hidden,
            hiddenFromPreviewBy: hidden ? moderatorId : null,
            hiddenFromPreviewAt: hidden ? serverTimestamp() : null,
        });
    },

    unmarkTrending: async (yapId: string): Promise<void> => {
        try {
            const result = await YapService.findPostRef(yapId);
            if (!result) throw new Error('Post not found in any collection.');
            await updateDoc(result.ref, {
                isTrending: false,
                trendingSetAt: null,
                trendingSetBy: null,
            });
        } catch (error) {
            console.error('[YapService] unmarkTrending error:', error);
            throw error;
        }
    },

    /**
     * Returns the 10 most recently trending-marked posts (yaps OR shouts), sorted
     * by trendingSetAt desc. Queries both collections in parallel and merges results.
     *
     * Required Firestore composite indexes:
     *   yaps:   isTrending ASC, trendingSetAt DESC
     *   shouts: isTrending ASC, trendingSetAt DESC
     * See firestore.indexes.json.
     */
    fetchTrendingYaps: async (): Promise<Yap[]> => {
        try {
            const db = await getDbSafe();
            // Over-fetched relative to what the Highlights row displays, so that
            // blocked authors / muted words filtered out on the viewer's side
            // leave the section shorter rather than empty.
            const trendingQuery = (col: string) => query(
                collection(db, col),
                where('isTrending', '==', true),
                orderBy('trendingSetAt', 'desc'),
                limit(20)
            );

            const [yapSnap, shoutSnap] = await Promise.all([
                getDocs(trendingQuery('yaps')),
                getDocs(trendingQuery('shouts')).catch(() => null), // index may not exist yet
            ]);

            const mapDoc = (d: any): Yap => {
                const data = d.data();
                const isoTimestamp = data.timestamp?.toDate
                    ? data.timestamp.toDate().toISOString()
                    : new Date().toISOString();
                // Normalise shout fields so TrendingYapCard can render without branching.
                // For shouts: the card shows the SHOUTER as the author.
                const isShout = !!(data.shoutedBy);
                return {
                    id: d.id,
                    ...data,
                    // Author surface — prefer shouter for shouts
                    author:   isShout ? (data.shoutedByName     || data.author || 'User') : (data.author || 'User'),
                    username: isShout ? (data.shoutedByUsername  || data.username || '') : (data.username || ''),
                    avatar:   isShout ? (data.shoutedByAvatar    || data.avatar || '') : (data.avatar || ''),
                    rank:     isShout ? (data.shoutedByRank      || data.rank || '') : (data.rank || ''),
                    authorCountryFlag: isShout
                        ? (data.shoutedByCountryFlag || data.authorCountryFlag || '')
                        : (data.authorCountryFlag || data.country || ''),
                    // Content — shout caption if set, otherwise original yap content
                    content: isShout
                        ? (data.shoutCaption || data.originalYap?.content || data.content || '')
                        : (data.content || ''),
                    time: data.timestamp?.toDate ? formatTimeAgo(data.timestamp.toDate()) : 'Recently',
                    timestamp: isoTimestamp,
                    likes:    data.metrics?.likes    || data.likes    || 0,
                    comments: data.metrics?.comments || data.comments || 0,
                    shouts:   data.metrics?.shouts   || data.shouts   || 0,
                } as Yap;
            };

            const all = [
                ...yapSnap.docs.map(mapDoc),
                ...(shoutSnap?.docs ?? []).map(mapDoc),
            ]
                .filter((y: any) => !y.deleted && !y.is_hidden && y.content)
                .sort((a: any, b: any) => {
                    const aTs = (a as any).trendingSetAt?.toMillis?.() ?? 0;
                    const bTs = (b as any).trendingSetAt?.toMillis?.() ?? 0;
                    return bTs - aTs;
                })
                .slice(0, 10);

            // Same live author lookup the feed does. Without it this rendered
            // whatever avatar was stamped onto the post when it was created,
            // which is exactly the placeholder-and-initial problem YapTab had —
            // trending was simply a second path that had never been hydrated.
            // Ten rows, so this is one batched read.
            return await hydrateAuthors(db, all as any[]) as Yap[];
        } catch (error) {
            console.error('[YapService] fetchTrendingYaps error:', error);
            return [];
        }
    },

    pinYap: async (yapId: string, isPinned: boolean): Promise<void> => {
        const db = await getDbSafe();
        
        try {
            const result = await YapService.findPostRef(yapId);
            if (!result) return;
            const { ref: yapRef } = result;

            // Update the yap document
            await updateDoc(yapRef, { isPinned });

            // Also update the user's pinnedYapIds array
            const auth = await getAuthSafe();
            if (auth.currentUser) {
                const userRef = doc(db, "users", auth.currentUser.uid);
                if (isPinned) {
                    await updateDoc(userRef, {
                        pinnedYapIds: arrayUnion(yapId)
                    });
                } else {
                    await updateDoc(userRef, {
                        pinnedYapIds: arrayRemove(yapId)
                    });
                }
            }
        } catch (error) {
            console.error("[YapService] Detailed pin error:", error);
        }
    },

    fetchUserYaps: async (userId: string): Promise<Yap[]> => {
        try {
            const db = await getDbSafe();
            // Query by userId only — no orderBy to avoid requiring a composite index
            const q = query(collection(db, "yaps"), where("userId", "==", userId));
            const snapshot = await getDocs(q);
            console.log(`[YapService] fetchUserYaps: Found ${snapshot.docs.length} raw docs for userId=${userId}`);
            
            const results = snapshot.docs.map(doc => {
                const data = doc.data();
                const timestampStr = data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : new Date().toISOString();
                console.log(`[YapService] fetchUserYaps: doc=${doc.id}, isAnonymous=${data.isAnonymous}, timestamp=${timestampStr}, content="${(data.content || '').substring(0, 40)}..."`);
                return {
                    id: doc.id,
                    ...data,
                    time: timestampStr,
                    timestamp: timestampStr,
                    likes: data.metrics?.likes || data.likes || 0,
                    comments: data.metrics?.comments || data.comments || 0,
                    shouts: data.metrics?.shouts || data.shouts || 0,
                } as Yap;
            });
            
            // Client-side sort: newest first, excluding soft-deleted yaps
            return results
                .filter((yap: any) => !yap.deleted && !yap.is_hidden)
                .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
        } catch (error: any) {
            console.error("Error fetching user yaps:", error);
            if (error?.code === 'failed-precondition') {
                console.error("🔥 MISSING INDEX: Check console for a link to create the required index");
            }
            return [];
        }
    },

    likeYap: async (yapId: string): Promise<void> => {
        const auth = await getAuthSafe();
        if (!auth.currentUser) return;
        const db = await getDbSafe();
        
        const userId = auth.currentUser.uid;
        
        try {
            const result = await YapService.findPostRef(yapId);
            if (!result) return;
            const { ref: yapRef, data: yapData, collectionName } = result;

            await runTransaction(db, async (transaction) => {
                const freshSnap = await transaction.get(yapRef);
                const freshData = freshSnap.data() || {};
                const currentLikes = freshData.metrics?.likes || freshData.likes || 0;

                transaction.update(yapRef, {
                    "metrics.likes": increment(1),
                    likes: increment(1)
                });
            });

            // Record the liker in the post's `likes` subcollection (doc id = uid)
            // so authors can see who liked the post. This is the rules-sanctioned
            // store (subcollection is world-readable; the doc roster on /yaps is
            // NOT — non-owners may only touch metrics/likes). Best-effort: a
            // failure here must not break the like itself.
            try {
                await setDoc(doc(db, collectionName, yapId, 'likes', userId), {
                    userId,
                    timestamp: serverTimestamp(),
                });
            } catch (likeDocErr) {
                console.warn('[YapService] like subcollection write failed:', likeDocErr);
            }

            // 🔒 Persist to user document (separate from post update so it survives if post update fails)
            try {
                const userRef = doc(db, "users", userId);
                await updateDoc(userRef, {
                    likedYapIds: arrayUnion(yapId)
                });
            } catch (userUpdateError) {
                console.error("[YapService] User like persistence failed:", userUpdateError);
            }

            // Bank like history for analytics (best-effort, non-blocking)
            addDoc(collection(db, 'like_events'), {
                actorId: userId,
                targetId: yapId,
                targetType: collectionName,
                action: 'like',
                ts: serverTimestamp()
            }).catch(() => {});

            // Hold this yap out of the liker's World feed for 48h.
            void FeedHideService.recordHide(yapId, 'like', collectionName, yapData);

            // Notify author if not self
            if (yapData.userId !== auth.currentUser.uid) {
                const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
                const userName = userDoc.exists() ? userDoc.data()?.name : 'Someone';
                
                let notifType = 'YAP_LIKE';
                if (result.collectionName === 'forum_posts') notifType = 'FORUM_LIKE';
                else if (result.collectionName === 'tribe_posts') notifType = 'TRIBE_LIKE';

                try {
                    await NotificationService.sendNotification(
                        yapData.userId,
                        notifType.toUpperCase(),
                        'New Like',
                        `${userName} liked your post`,
                        {
                            yapId,
                            fromUserId: auth.currentUser.uid,
                            fromUserName: userName,
                            // getUserAvatar, not portfolioPreviews[0]: that slot
                            // is only their profile picture by coincidence, and it
                            // does not filter the signup placeholders — so a
                            // notification could show the wrong photo, or a
                            // stranger's face from i.pravatar.cc.
                            fromUserAvatar: userDoc.exists() ? getUserAvatar(userDoc.data() as any) : '',
                            targetId: yapId,
                        }
                    );
                } catch (notiError) {
                    console.error("[YapService] Notification failed:", notiError);
                }
            }
        } catch (error) {
            console.error("[YapService] Detailed like error:", error);
        }
    },

    unlikeYap: async (yapId: string): Promise<void> => {
        const auth = await getAuthSafe();
        if (!auth.currentUser) return;
        const db = await getDbSafe();

        const userId = auth.currentUser.uid;

        try {
            const result = await YapService.findPostRef(yapId);
            if (!result) return;
            const { ref: yapRef, data: yapData, collectionName } = result;

            await runTransaction(db, async (transaction) => {
                const freshSnap = await transaction.get(yapRef);
                const freshData = freshSnap.data() || {};
                const currentLikes = freshData.metrics?.likes || freshData.likes || 0;

                transaction.update(yapRef, {
                    "metrics.likes": increment(-1),
                    likes: increment(-1)
                });
            });

            // Undoing the like gives the yap back, unless a reply / share / shout
            // is still holding it out.
            void FeedHideService.clearHide(yapId, 'like', collectionName, yapData);

            // Remove the liker from the post's `likes` subcollection (best-effort).
            try {
                await deleteDoc(doc(db, collectionName, yapId, 'likes', userId));
            } catch (likeDocErr) {
                console.warn('[YapService] like subcollection delete failed:', likeDocErr);
            }

            // 🔒 Persist to user document (separate from post update so it survives if post update fails)
            try {
                const userRef = doc(db, "users", userId);
                await updateDoc(userRef, {
                    likedYapIds: arrayRemove(yapId)
                });
            } catch (userUpdateError) {
                console.error("[YapService] User unlike persistence failed:", userUpdateError);
            }

            // Bank unlike history for analytics (best-effort, non-blocking)
            addDoc(collection(db, 'like_events'), {
                actorId: userId,
                targetId: yapId,
                targetType: collectionName,
                action: 'unlike',
                ts: serverTimestamp()
            }).catch(() => {});
        } catch (error) {
            console.error("[YapService] Detailed unlike error:", error);
        }
    },

    /**
     * Returns the user ids that currently like a post, read from its world-
     * readable `likes` subcollection (maintained by likeYap/unlikeYap). Used to
     * show authors who liked their post. Note: only captures likes recorded after
     * the subcollection write was introduced — older likes counted before then
     * aren't listed.
     */
    /**
     * Who liked a quote shout.
     *
     * getYapLikers cannot answer this: likeShout writes no likes subcollection
     * and records the like only on the liker's own user doc (likedShoutIds), while
     * getYapLikers' fallback reads likedYapIds. That reverse index is the roster.
     *
     * Filtered to the viewer's age bracket because the users list rule only
     * permits bracket-scoped queries; without the filter the whole query is
     * denied and the list comes back empty. Likers in the other bracket are
     * therefore not listed, consistent with the age separation everywhere else.
     */
    getShoutLikers: async (shoutId: string, viewerAgeGroup?: string): Promise<string[]> => {
        try {
            const db = await getDbSafe();
            const snap = await getDocs(query(
                collection(db, 'users'),
                where('ageGroup', '==', viewerAgeGroup || 'minor'),
                where('likedShoutIds', 'array-contains', shoutId),
            ));
            return snap.docs.map(d => String(d.id));
        } catch (error) {
            console.error('[YapService] getShoutLikers failed:', error);
            return [];
        }
    },

    getYapLikers: async (yapId: string, viewerAgeGroup?: string): Promise<string[]> => {
        try {
            const db = await getDbSafe();
            const result = await YapService.findPostRef(yapId);
            if (!result) return [];
            const snap = await getDocs(collection(db, result.collectionName, yapId, 'likes'));
            const ids = snap.docs.map(d => String(d.data()?.userId || d.id));
            // Merge with any legacy likedBy roster on the post doc.
            const legacy = Array.isArray((result.data as any)?.likedBy) ? (result.data as any).likedBy : [];
            const merged = new Set([...ids, ...legacy.map(String)].filter(Boolean));

            // The likes subcollection only captures likes recorded after it was
            // introduced (and its write is best-effort), so older likes — or ones
            // whose subcollection write failed — are missing even when the like
            // counter is higher. This shows up as "No likes yet" on never-recorded
            // posts, or (e.g. on a freshly re-liked pinned yap) only the newest
            // liker. Whenever the roster is short of the counter, recover the rest
            // from the reverse index: users who have this yap in their likedYapIds.
            const likeCount = (result.data as any)?.metrics?.likes ?? (result.data as any)?.likes ?? 0;
            if (merged.size < likeCount) {
                try {
                    // Bracket-scoped: the users list rule denies any query not
                    // filtered to the viewer's ageGroup, and a denied query here
                    // used to fail into the catch below, so this recovery never
                    // recovered anyone. Likers in the other bracket stay unlisted.
                    const reverseSnap = await getDocs(query(
                        collection(db, 'users'),
                        where('ageGroup', '==', viewerAgeGroup || 'minor'),
                        where('likedYapIds', 'array-contains', yapId),
                    ));
                    reverseSnap.docs.forEach(d => merged.add(String(d.id)));
                } catch (reverseErr) {
                    console.warn('[YapService] getYapLikers reverse-index lookup failed:', reverseErr);
                }
            }

            return Array.from(merged);
        } catch (e) {
            console.error('[YapService] getYapLikers failed:', e);
            return [];
        }
    },

    shoutYap: async (yapId: string, caption?: string, images?: string[], audioData?: { url: string, duration: number }, visibility: 'public' | 'friends' = 'public'): Promise<void> => {
        const auth = await getAuthSafe();
        // Throw rather than return: callers optimistically show the shout and only
        // roll it back if this rejects. Silently resolving left the UI claiming a
        // shout that was never written, which only surfaced on the next reload.
        if (!auth.currentUser) throw new Error('SHOUT_NOT_SIGNED_IN');
        const userId = auth.currentUser.uid;
        const db = await getDbSafe();

        console.log(`[YapService] shoutYap: START for ${yapId} by ${userId}, audio=${!!audioData}, visibility=${visibility}`);

        try {
            const result = await YapService.findPostRef(yapId);
            if (!result) throw new Error('SHOUT_POST_NOT_FOUND');
            const { ref: yapRef, data: originalData } = result;
            const userRef = doc(db, "users", userId);

            let alreadyShouted = false;

            await runTransaction(db, async (transaction) => {
                const userSnap = await transaction.get(userRef);
                const userData = userSnap.data() || {};
                const shoutedYapIds = userData.shoutedYapIds || [];

                if (shoutedYapIds.includes(yapId)) {
                    console.warn(`[YapService] Already shouted ${yapId} in user profile. skipping.`);
                    alreadyShouted = true;
                    return;
                }

                // If we're here, it's a new shout for this user
                const freshSnap = await transaction.get(yapRef);
                const freshData = freshSnap.data() || {};
                const currentShouts = freshData.metrics?.shouts || freshData.shouts || 0;

                // Update original yap metrics
                transaction.update(yapRef, {
                    "metrics.shouts": increment(1),
                    shouts: increment(1)
                });

                // Add to user's shouted list
                transaction.update(userRef, {
                    shoutedYapIds: arrayUnion(yapId)
                });
            });

            // Enforced at the data layer, not just in the UI: the transaction above
            // is the authority on "one shout per user per yap". Throwing (rather
            // than returning quietly) lets the caller roll back the optimistic +1
            // it already applied, instead of showing a count one too high until
            // the next reload.
            if (alreadyShouted) throw new Error('SHOUT_ALREADY_EXISTS');

            // `yapId` is already the ORIGINAL yap here (shoutYap/unshoutYap are
            // both keyed on originalYapId), so the hide key needs no normalizing.
            void FeedHideService.recordHide(yapId, 'shout', result.collectionName, originalData);

            // Fetch shouter profile for denormalization
            const shouterDoc = await getDoc(userRef);
            const shouterData = shouterDoc.exists() ? shouterDoc.data() : {};

            // Update original data snapshot so the embedded card reflects the new count immediately
            const updatedOriginalData: any = { 
                ...originalData,
                id: yapId // ENSURE ID is preserved in snapshot for interactive components
            };
            updatedOriginalData.shouts = (updatedOriginalData.shouts || 0) + 1;
            if (updatedOriginalData.metrics) {
                updatedOriginalData.metrics.shouts = (updatedOriginalData.metrics.shouts || 0) + 1;
            }

            // Separate doc creation (not supported in transactions for new random IDs easily)
            await addDoc(collection(db, "shouts"), {
                shoutedBy: userId, // UID for indexing/purging
                shoutedByUsername: shouterData.username || 'unknown',
                shoutedByName: shouterData.name || 'User',
                shoutedByAvatar: shouterData.avatar || shouterData.image || '',
                shoutedByCountryFlag: shouterData.countryFlag || '',
                shoutedByCountry: shouterData.country || '',
                shoutedByResidence: shouterData.home_residence || '',
                shoutedByLocation: shouterData.location || '',
                authorCountryFlag: shouterData.countryFlag || '',
                originalYapId: yapId,
                timestamp: serverTimestamp(),
                likes: 0,
                comments: 0,
                metrics: {
                    likes: 0,
                    comments: 0,
                    shouts: 0,
                    shares: 0
                },
                originalYap: updatedOriginalData, // Store updated snapshot for accurate feed rendering
                shoutCaption: caption || null,
                shoutImages: images || [],
                shoutedByShowLocation: shouterData.showLocation !== false, // Use shouter's preference
                audioUrl: audioData?.url || null,
                duration: audioData?.duration || null,
                visibility: visibility
            });

            // Notify author if not self
            if (originalData.userId !== userId) {
                const userDoc = await getDoc(userRef);
                const userName = userDoc.exists() ? userDoc.data()?.name : 'Someone';

                try {
                    await NotificationService.sendNotification(
                        originalData.userId,
                        'YAP_SHOUT',
                        'New Shout',
                        `${userName} shouted your post`,
                        { yapId, fromUserId: userId, fromUserName: userName }
                    );
                } catch (notiError) {
                    console.error("[YapService] Shout notification failed:", notiError);
                }
            }

            // ─── 4. Process Mentions in Caption (Non-blocking) ───────────────────────
            if (caption) {
                _processMentions(caption, yapId, 'shout', userId, shouterData.name || 'User')
                    .catch(e => console.warn('[YapService] Shout mention processing failed:', e));
            }
        } catch (error) {
            console.error("[YapService] Detailed shout error:", error);
            throw error; // let the caller revert its optimistic state
        }
    },

    likeShout: async (shoutId: string): Promise<void> => {
        const auth = await getAuthSafe();
        if (!auth.currentUser) return;
        const db = await getDbSafe();
        const userId = auth.currentUser.uid;
        
        const shoutRef = doc(db, "shouts", shoutId);
        const userRef = doc(db, "users", userId);

        try {
            let likeApplied = false;
            await runTransaction(db, async (transaction) => {
                const userSnap = await transaction.get(userRef);
                const userData = userSnap.data() || {};
                const likedShoutIds = userData.likedShoutIds || [];

                if (likedShoutIds.includes(shoutId)) {
                    console.warn(`[YapService] likeShout: User ${userId} already liked shout ${shoutId}`);
                    return;
                }

                transaction.update(shoutRef, {
                    likes: increment(1),
                    "metrics.likes": increment(1)
                });
                transaction.update(userRef, {
                    likedShoutIds: arrayUnion(shoutId)
                });
                likeApplied = true;
            });

            if (likeApplied) {
                addDoc(collection(db, 'like_events'), {
                    actorId: userId,
                    targetId: shoutId,
                    targetType: 'shouts',
                    action: 'like',
                    ts: serverTimestamp()
                }).catch(() => {});

                // The shout doc is read here purely to recover originalYapId: the
                // hide key has to be the underlying yap, or liking a quote shout
                // would leave the yap it quotes still rotating through the feed.
                void (async () => {
                    const shoutSnap = await getDoc(shoutRef).catch(() => null);
                    await FeedHideService.recordHide(shoutId, 'like', 'shouts', shoutSnap?.data());
                })();
            }
        } catch (error) {
            console.error("[YapService] Detailed likeShout error:", error);
            throw error;
        }
    },

    unlikeShout: async (shoutId: string): Promise<void> => {
        const auth = await getAuthSafe();
        if (!auth.currentUser) return;
        const db = await getDbSafe();
        const userId = auth.currentUser.uid;

        const shoutRef = doc(db, "shouts", shoutId);
        const userRef = doc(db, "users", userId);

        try {
            let unlikeApplied = false;
            await runTransaction(db, async (transaction) => {
                const userSnap = await transaction.get(userRef);
                const userData = userSnap.data() || {};
                const likedShoutIds = userData.likedShoutIds || [];

                if (!likedShoutIds.includes(shoutId)) {
                    console.warn(`[YapService] unlikeShout: User ${userId} has not liked shout ${shoutId}`);
                    return;
                }

                transaction.update(shoutRef, {
                    likes: increment(-1),
                    "metrics.likes": increment(-1)
                });
                transaction.update(userRef, {
                    likedShoutIds: arrayRemove(shoutId)
                });
                unlikeApplied = true;
            });

            if (unlikeApplied) {
                addDoc(collection(db, 'like_events'), {
                    actorId: userId,
                    targetId: shoutId,
                    targetType: 'shouts',
                    action: 'unlike',
                    ts: serverTimestamp()
                }).catch(() => {});

                void (async () => {
                    const shoutSnap = await getDoc(shoutRef).catch(() => null);
                    await FeedHideService.clearHide(shoutId, 'like', 'shouts', shoutSnap?.data());
                })();
            }
        } catch (error) {
            console.error("[YapService] Detailed unlikeShout error:", error);
            throw error;
        }
    },

    unshoutYap: async (yapId: string): Promise<void> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        if (!auth.currentUser) throw new Error('UNSHOUT_NOT_SIGNED_IN');
        const userId = auth.currentUser.uid;

        try {
            // 1. Claim the unshout atomically.
            //
            //    Deleting a shout fires through two paths (toggleShout and
            //    removeShout) and some screens call both — they run concurrently,
            //    so "count the docs, then decrement by that many" is not enough:
            //    both calls read 1 doc before either deletes, both decrement, and
            //    the count lands at -1. The user's shoutedYapIds array is the
            //    single atomic marker: whichever transaction commits first removes
            //    the id and earns the right to decrement; any concurrent or repeat
            //    call sees it already gone and skips. Mirrors the alreadyShouted
            //    guard on the shout side.
            const userRef = doc(db, "users", userId);
            let ownsDecrement = false;
            await runTransaction(db, async (transaction) => {
                const userSnap = await transaction.get(userRef);
                const shoutedYapIds = userSnap.data()?.shoutedYapIds || [];
                if (!shoutedYapIds.includes(yapId)) {
                    ownsDecrement = false;
                    return;
                }
                transaction.update(userRef, { shoutedYapIds: arrayRemove(yapId) });
                ownsDecrement = true;
            });

            // 2. Delete the shout document(s) — idempotent, safe to run either way
            //    so a half-finished earlier attempt still gets cleaned up.
            const q = query(
                collection(db, "shouts"),
                where("shoutedBy", "==", userId),
                where("originalYapId", "==", yapId)
            );
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                const batch = writeBatch(db);
                snapshot.forEach((d) => batch.delete(d.ref));
                await batch.commit();
            }

            // 3. Decrement shout metrics on the original yap — ONLY if this call
            //    actually deleted a shout. Deleting a shout runs through two
            //    paths (toggleShout and removeShout) and some screens call both,
            //    e.g. YapDetailScreen.handleDeleteShout; an unconditional
            //    decrement then ran twice and drove the count negative (observed:
            //    0 → shout → 1 → unshout → -1). Doc deletion and arrayRemove are
            //    already idempotent; this makes the counter idempotent too.
            if (ownsDecrement) {
                try {
                    const result = await YapService.findPostRef(yapId);
                    if (result) {
                        await updateDoc(result.ref, {
                            "metrics.shouts": increment(-1),
                            shouts: increment(-1),
                        });
                    }
                } catch {
                    // Yap may be hard-deleted or not found — metrics update is non-critical
                }

                // Gated on ownsDecrement for the same reason the metrics update is:
                // both unshout paths can run for one user action, and only the call
                // that actually removed the shout should undo its side effects.
                void FeedHideService.clearHide(yapId, 'shout', 'yaps');
            }
        } catch (error) {
            console.error("[YapService] Detailed unshout error:", error);
            throw error; // caller restores the shout it optimistically removed
        }
    },

    updateShoutCaption: async (originalYapId: string, newCaption?: string, newImages?: string[]): Promise<void> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        if (!auth.currentUser) return;
        const userId = auth.currentUser.uid;

        try {
            const q = query(
                collection(db, "shouts"),
                where("shoutedBy", "==", userId),
                where("originalYapId", "==", originalYapId)
            );
            const snapshot = await getDocs(q);
            const batch = writeBatch(db);
            snapshot.forEach((docSnap) => {
                batch.update(docSnap.ref, {
                    shoutCaption: newCaption ?? null,
                    shoutImages: newImages ?? [],
                });
            });
            await batch.commit();
        } catch (error) {
            console.error("[YapService] Error updating shout caption:", error);
            throw error;
        }
    },

    fetchUserShoutedYaps: async (userId: string): Promise<ShoutedYap[]> => {
        try {
            const db = await getDbSafe();
            const q = query(collection(db, "shouts"), where("shoutedBy", "==", userId), orderBy("timestamp", "desc"));
            const snapshot = await getDocs(q);

            const shouts = await Promise.all(snapshot.docs.map(async (shoutDoc) => {
                const data = shoutDoc.data();
                
                // Use stored snapshot if available, otherwise fetch
                let originalYap: Yap;
                if (data.originalYap) {
                    // Start with the cached snapshot
                    originalYap = {
                        ...data.originalYap,
                        id: data.originalYapId // Ensure correct ID
                    };

                    // Always check the live yap document for the deleted flag
                    // to ensure we reflect deletions even if the shout cache is stale
                    if (data.originalYapId) {
                        try {
                            const liveYapDoc = await getDoc(doc(db, "yaps", data.originalYapId));
                            if (!liveYapDoc.exists()) {
                                // Yap was hard-deleted, mark it as deleted in result
                                originalYap = { ...originalYap, deleted: true };
                            } else {
                                // Yap exists — use the live deleted flag (it may have been soft-deleted)
                                const liveData = liveYapDoc.data();
                                originalYap = { ...originalYap, deleted: liveData.deleted === true };
                            }
                        } catch {
                            // Silently keep cached version on network error
                        }
                    } else if (data.originalYap?.deleted === true) {
                        // Shout doc's cache already says deleted — respect it 
                        originalYap = { ...originalYap, deleted: true };
                    }
                } else {
                    const originalYapDoc = await getDoc(doc(db, "yaps", data.originalYapId));
                    if (originalYapDoc.exists()) {
                        const yapData = originalYapDoc.data();
                        originalYap = {
                            id: originalYapDoc.id,
                            ...yapData,
                            time: yapData.timestamp?.toDate ? yapData.timestamp.toDate().toISOString() : new Date().toISOString(),
                            likes: yapData.metrics?.likes || yapData.likes || 0,
                            comments: yapData.metrics?.comments || yapData.comments || 0,
                            shouts: yapData.metrics?.shouts || yapData.shouts || 0,
                        } as Yap;
                    } else {
                        originalYap = {
                            id: data.originalYapId,
                            userId: '',
                            content: "This yap has been deleted",
                            author: "Unknown",
                            username: "@unknown",
                            avatar: "",
                            time: "",
                            likes: 0,
                            comments: 0,
                            shouts: 0,
                            country: "Unknown",
                            deleted: true,
                        } as Yap;
                    }
                }

                return {
                    id: shoutDoc.id,
                    ...data,
                    originalYap,
                    timestamp: data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : new Date().toISOString(),
                    likes: data.metrics?.likes || data.likes || 0,
                    comments: data.metrics?.comments || data.comments || 0,
                } as ShoutedYap;
            }));

            return shouts;
        } catch (error) {
            console.error("Error fetching user shouts:", error);
            return [];
        }
    },
};

export const ForumService = {
    getPosts: async (topicId: string, sinceTimestamp?: string): Promise<Yap[]> => {
        try {
            console.log(`[ForumService] getPosts START: topicId=${topicId}, sinceTimestamp=${sinceTimestamp}`);
            const { formatTimeAgo } = await import('../components/utils/timeFormat');
            const db = await getDbSafe();

            // Shared mapping: resolve author fields + normalise metrics for one
            // post doc, regardless of which collection it came from.
            const mapPostDoc = async (docSnapshot: any): Promise<Yap> => {
                const data = docSnapshot.data();
                let userData: any = {};
                const isoTimestamp = data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : new Date().toISOString();

                // Anonymous cross-posts must keep their stored anonymous identity —
                // never resolve the real author from userId, or anonymity leaks.
                if (data.isAnonymous) {
                    return {
                        id: docSnapshot.id,
                        ...data,
                        author: data.author || 'Anonymous',
                        username: data.username || '@anonymous',
                        // Empty, not the dead /anonymous-avatar.png — that file
                        // has never existed (only the -blue and -orange variants
                        // ship), so it 404'd. An anonymous yap renders its own
                        // avatar component; a broken URL only got in the way.
                        avatar: data.avatar || '',
                        time: data.timestamp?.toDate ? formatTimeAgo(data.timestamp.toDate()) : 'Now',
                        timestamp: isoTimestamp,
                        likes: data.metrics?.likes || data.likes || 0,
                        comments: data.metrics?.comments || data.comments || 0,
                        shouts: data.metrics?.shouts || data.shouts || 0,
                    } as Yap;
                }

                if (data.userId) {
                    try {
                        const userDoc = await getDoc(doc(db, "users", data.userId));
                        if (userDoc.exists()) {
                            userData = userDoc.data();
                        }
                    } catch (e) {
                        console.warn("Failed to fetch user data for post", docSnapshot.id);
                    }
                }

                return {
                    id: docSnapshot.id,
                    ...data,
                    author: userData.name || data.author || 'User',
                    username: userData.username || data.username || '@user',
                    avatar: UserService.resolveUserAvatar(userData, data),
                    country: userData.country || data.country || '',
                    home_residence: userData.home_residence || data.home_residence || '',
                    rank: userData.rank as UserRank,

                    time: data.timestamp?.toDate ? formatTimeAgo(data.timestamp.toDate()) : 'Recently',
                    timestamp: isoTimestamp,
                    likes: data.metrics?.likes || data.likes || 0,
                    comments: data.metrics?.comments || data.comments || 0,
                    shouts: data.metrics?.shouts || data.shouts || 0,
                } as Yap;
            };

            // Two sources, merged into one chronological feed:
            //  1. forum-exclusive posts in `forum_posts` (topicId == forum id)
            //  2. YapTab posts cross-posted via a forum topic tag — these live in
            //     the main `yaps` collection with forumTopic.forumId == forum id.
            // Both are single-field equality queries (auto-indexed); we sort
            // client-side below to avoid composite-index requirements.
            const [exclusiveSnap, crossPostSnap] = await Promise.all([
                getDocs(query(collection(db, "forum_posts"), where("topicId", "==", topicId))),
                getDocs(query(collection(db, "yaps"), where("forumTopic.forumId", "==", topicId))),
            ]);

            // Map both sources, then dedupe by id (same post should never appear twice).
            const mapped = await Promise.all([...exclusiveSnap.docs, ...crossPostSnap.docs].map(mapPostDoc));
            const seen = new Set<string>();
            const posts = mapped.filter(p => {
                if (seen.has(p.id)) return false;
                seen.add(p.id);
                return true;
            });

            // Filter by sinceTimestamp if provided
            let filteredPosts = posts;
            if (sinceTimestamp) {
                const sinceDate = new Date(sinceTimestamp);
                filteredPosts = posts.filter(p => {
                  const pTs = p.timestamp || '';
                  return pTs && new Date(pTs) >= sinceDate;
                });
            }

            // Client-side sort, excluding soft-deleted posts
            return filteredPosts
                .filter((p: any) => !p.deleted && !p.is_hidden)
                .sort((a, b) => {
                const dateA = new Date(a.timestamp || '').getTime();
                const dateB = new Date(b.timestamp || '').getTime();
                return dateB - dateA;
            });
        } catch (error) {
            console.error("Error fetching forum posts:", error);
            return [];
        }
    },

    createPost: async (topicId: string, content: string, images: string[] = []): Promise<Yap> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        if (!auth.currentUser) throw new Error("Must be logged in");

        const forumRef = doc(db, "forums", topicId);
        const userRef = doc(db, "users", auth.currentUser.uid);
        const newPostRef = doc(collection(db, "forum_posts"));

        let savedUserData: any = null;

        // Posting in a forum is free. The old 10-gold charge was spam deterrence
        // sized for a much busier app: at current volume it mostly blocked
        // legitimate posters who hadn't claimed their daily streak, in the one
        // surface that most needs seeding. Rate limiting + moderation cover spam.
        const result = await runTransaction(db, async (transaction) => {
            const userDoc = await transaction.get(userRef);
            const userData = userDoc.exists() ? userDoc.data() : null;
            savedUserData = userData;

            const postData = {
                topicId,
                userId: auth.currentUser?.uid,
                author: userData?.name || 'User',
                username: userData?.username || '@user',
                avatar: userData?.avatar || userData?.image || '',
                country: userData?.country || '',
                authorCountryFlag: userData?.countryFlag || '',
                home_residence: userData?.home_residence || '',
                rank: userData?.rank || '',
                content,
                images,
                image: images.length > 0 ? images[0] : null,
                timestamp: serverTimestamp(),
                metrics: {
                    likes: 0,
                    comments: 0,
                    shouts: 0,
                    shares: 0
                },
                likes: 0,
                comments: 0,
                shouts: 0,
                isForumOnly: true
            };

            // 1. Create the post
            transaction.set(newPostRef, postData);

            // 2. Increment forum post count and activity
            transaction.update(forumRef, {
                postCount: increment(1),
                lastActivityAt: serverTimestamp()
            });

            console.log(`[ForumService] Post created for ${auth.currentUser?.uid}`);

            return {
                id: newPostRef.id,
                ...postData,
                time: 'Now',
                timestamp: new Date().toISOString()
            } as Yap;
        });

        // ─── 5. Audit Log — best-effort AFTER transaction to avoid permission failures ───
        try {
            const auditRef = doc(collection(db, 'auditLogs'));
            await setDoc(auditRef, {
                actorId: auth.currentUser?.uid,
                actorUsername: savedUserData?.username || 'Unknown',
                targetId: auth.currentUser?.uid,
                targetUsername: savedUserData?.username || 'Unknown',
                actionType: 'forum_post',
                reason: `Forum post in ${topicId}`,
                timestamp: serverTimestamp()
            });
        } catch (auditErr) {
            console.warn('[ForumService] Audit log write failed (non-critical):', auditErr);
        }

        // ─── 6. Process Mentions + Notify forum members (Non-blocking) ──────────
        // Mentions run first so we can exclude the mentioned UIDs from the group
        // fan-out — otherwise a forum member who is @-mentioned in the post would
        // receive BOTH a MENTION and a FORUM_POST push for the same event.
        (async () => {
            const forumSnap2 = await getDoc(forumRef).catch(() => null);
            const forumName  = forumSnap2?.exists() ? (forumSnap2.data().name || 'Forum') : 'Forum';
            const posterName = savedUserData?.name || 'User';
            const mentionedUids = content
                ? await _processMentions(content, result.id, 'forum', auth.currentUser!.uid, posterName).catch(() => [] as string[])
                : [];
            await _notifyGroupMembers(
                'forum', topicId, forumName,
                result.id,
                auth.currentUser!.uid,
                posterName,
                content,
                mentionedUids
            );
        })().catch(e => console.warn('[ForumService] Post-creation notifications failed:', e));

        return result;

    },

    deletePost: async (forumId: string, postId: string) => {
        try {
            const db = await getDbSafe();
            const postRef = doc(db, "forum_posts", postId);
            const forumRef = doc(db, "forums", forumId);

            await runTransaction(db, async (transaction) => {
                const postDoc = await transaction.get(postRef);
                if (!postDoc.exists()) {
                    throw new Error("Post does not exist!");
                }

                // 1. Delete the post
                transaction.delete(postRef);

                // 2. Decrement forum post count
                transaction.update(forumRef, {
                    postCount: increment(-1)
                });
            });
            console.log(`[ForumService] Deleted post ${postId} from forum ${forumId}`);
        } catch (error) {
            console.error("Error deleting forum post:", error);
            throw error;
        }
    },

    updateForum: async (forumId: string, updates: Partial<Forum>) => {
        try {
            const db = await getDbSafe();
            const forumRef = doc(db, "forums", forumId);
            await updateDoc(forumRef, updates);
        } catch (error) {
            console.error("Error updating forum:", error);
            throw error;
        }
    },

    uploadLogo: async (forumId: string, file: Blob): Promise<string> => {
        try {
            const storage = await getStorageSafe();
            const timestamp = Date.now();
            const storageRef = ref(storage, `forums/${forumId}/logos/${timestamp}.jpg`);
            const snapshot = await uploadBytes(storageRef, file);
            return await getDownloadURL(snapshot.ref);
        } catch (error) {
            console.error("Error uploading forum logo:", error);
            throw error;
        }
    },

    uploadBanner: async (forumId: string, file: Blob): Promise<string> => {
        try {
            const storage = await getStorageSafe();
            const timestamp = Date.now();
            const storageRef = ref(storage, `forums/${forumId}/banners/${timestamp}.jpg`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error("Error uploading forum banner:", error);
            throw error;
        }
    },

    uploadCommunityBanner: async (userId: string, type: 'forum' | 'tribe', file: Blob): Promise<string> => {
        try {
            const storage = await getStorageSafe();
            const timestamp = Date.now();
            const path = `communities/${type}/temp/${userId}/${timestamp}.jpg`;
            const storageRef = ref(storage, path);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error(`Error uploading ${type} banner:`, error);
            throw error;
        }
    },

    uploadPostImage: async (type: 'forum' | 'tribe', communityId: string, userId: string, file: Blob): Promise<string> => {
        try {
            const storage = await getStorageSafe();
            const timestamp = Date.now();
            const path = `communities/${type}/${communityId}/posts/${userId}/${timestamp}.jpg`;
            const storageRef = ref(storage, path);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error(`Error uploading ${type} post image:`, error);
            throw error;
        }
    }
};

export const TribeService = {
    getPosts: async (tribeId: string, sinceTimestamp?: string): Promise<Yap[]> => {
        try {
            console.log(`[TribeService] getPosts START: tribeId=${tribeId}, sinceTimestamp=${sinceTimestamp}`);
            const { formatTimeAgo } = await import('../components/utils/timeFormat');
            const db = await getDbSafe();
            const q = query(
                collection(db, "tribe_posts"),
                where("tribeId", "==", tribeId)
                // orderBy("timestamp", "desc") // REMOVED to avoid 'Missing Index' errors
            );
            const snapshot = await getDocs(q);
            // Map posts and fetch user data in parallel
            const posts = await Promise.all(snapshot.docs.map(async (docSnapshot) => {
                const data = docSnapshot.data();
                let userData: any = {};
                const isoTimestamp = data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : new Date().toISOString();

                if (data.userId) {
                    try {
                        const userDoc = await getDoc(doc(db, "users", data.userId));
                        if (userDoc.exists()) {
                            userData = userDoc.data();
                        }
                    } catch (e) {
                        console.warn("Failed to fetch user data for tribe post", docSnapshot.id);
                    }
                }

                return {
                    id: docSnapshot.id,
                    ...data,
                    author: userData.name || data.author || 'User',
                    username: userData.username || data.username || '@user',
                    avatar: UserService.resolveUserAvatar(userData, data),
                    country: userData.country || data.country || '',
                    home_residence: userData.home_residence || data.home_residence || '',
                    rank: userData.rank as UserRank,

                    time: data.timestamp?.toDate ? formatTimeAgo(data.timestamp.toDate()) : 'Recently',
                    timestamp: isoTimestamp,
                    likes: data.metrics?.likes || data.likes || 0,
                    comments: data.metrics?.comments || data.comments || 0,
                    shouts: data.metrics?.shouts || data.shouts || 0,
                } as Yap;
            }));

            // Filter by sinceTimestamp if provided
            let filteredPosts = posts;
            if (sinceTimestamp) {
                const sinceDate = new Date(sinceTimestamp);
                filteredPosts = posts.filter(post => new Date(post.timestamp || '').getTime() >= sinceDate.getTime());
            }

            // Client-side sort, excluding soft-deleted posts
            return filteredPosts
                .filter((p: any) => !p.deleted && !p.is_hidden)
                .sort((a, b) => {
                const dateA = new Date(a.timestamp || '').getTime();
                const dateB = new Date(b.timestamp || '').getTime();
                return dateB - dateA;
            });
        } catch (error) {
            console.error("Error fetching tribe posts:", error);
            return [];
        }
    },

    createPost: async (tribeId: string, content: string, images: string[] = []): Promise<Yap> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        if (!auth.currentUser) throw new Error("Must be logged in");

        const tribeRef = doc(db, "tribes", tribeId);
        const userRef = doc(db, "users", auth.currentUser.uid);
        const newPostRef = doc(collection(db, "tribe_posts"));

        const post = await runTransaction(db, async (transaction) => {
            const userDoc = await transaction.get(userRef);
            const userData = userDoc.exists() ? userDoc.data() : null;
            // 1000 characters; Legion has no limit (constants/posts.ts).
            if (exceedsYapLimit(content.trim(), userData)) {
                throw new Error(`Post content is too long (max ${YAP_MAX_CHARACTERS} characters)`);
            }

            const postData = {
                tribeId,
                userId: auth.currentUser?.uid,
                author: userData?.name || 'User',
                username: userData?.username || '@user',
                avatar: userData?.avatar || userData?.image || '',
                country: userData?.country || '',
                authorCountryFlag: userData?.countryFlag || '',
                home_residence: userData?.home_residence || '',
                rank: userData?.rank || '',
                content,
                images,
                image: images.length > 0 ? images[0] : null,
                timestamp: serverTimestamp(),
                metrics: {
                    likes: 0,
                    comments: 0,
                    shouts: 0,
                    shares: 0
                },
                likes: 0,
                comments: 0,
                shouts: 0,
                isTribeOnly: true
            };

            // 1. Create the post
            transaction.set(newPostRef, postData);

            // 2. Increment tribe post count and activity
            transaction.update(tribeRef, {
                postCount: increment(1),
                trendingScore: increment(10), // Boost trending score
                lastActivityAt: serverTimestamp()
            });

            return {
                id: newPostRef.id,
                ...postData,
                time: 'Now',
                timestamp: new Date().toISOString()
            } as Yap;
        });

        // ─── 3. Process Mentions + Notify tribe members (Non-blocking) ───────────
        // Mentions run first so we can exclude the mentioned UIDs from the group
        // fan-out — otherwise a tribe member who is @-mentioned in the post would
        // receive BOTH a MENTION and a TRIBE_POST push for the same event.
        (async () => {
            const tribeSnap2 = await getDoc(doc(db, 'tribes', tribeId)).catch(() => null);
            const tribeName  = tribeSnap2?.exists() ? (tribeSnap2.data().name || 'Tribe') : 'Tribe';
            const mentionedUids = content
                ? await _processMentions(content, post.id, 'tribe', auth.currentUser!.uid, post.author).catch(() => [] as string[])
                : [];
            await _notifyGroupMembers(
                'tribe', tribeId, tribeName,
                post.id,
                auth.currentUser!.uid,
                post.author,
                content,
                mentionedUids
            );
        })().catch(e => console.warn('[TribeService] Post-creation notifications failed:', e));

        return post;
    },

    deletePost: async (tribeId: string, postId: string) => {
        try {
            const db = await getDbSafe();
            const postRef = doc(db, "tribe_posts", postId);
            const tribeRef = doc(db, "tribes", tribeId);

            await runTransaction(db, async (transaction) => {
                const postDoc = await transaction.get(postRef);
                if (!postDoc.exists()) {
                    throw new Error("Post does not exist!");
                }

                // 1. Delete the post
                transaction.delete(postRef);

                // 2. Decrement tribe post count
                transaction.update(tribeRef, {
                    postCount: increment(-1)
                });
            });
            console.log(`[TribeService] Deleted post ${postId} from tribe ${tribeId}`);
        } catch (error) {
            console.error("Error deleting tribe post:", error);
            throw error;
        }
    },

    updateTribe: async (tribeId: string, updates: Partial<Tribe>) => {
        try {
            const db = await getDbSafe();
            const tribeRef = doc(db, "tribes", tribeId);
            await updateDoc(tribeRef, updates);
        } catch (error) {
            console.error("Error updating tribe:", error);
            throw error;
        }
    },

    uploadLogo: async (tribeId: string, file: Blob): Promise<string> => {
        try {
            const storage = await getStorageSafe();
            const timestamp = Date.now();
            const storageRef = ref(storage, `tribes/${tribeId}/logos/${timestamp}.jpg`);
            const snapshot = await uploadBytes(storageRef, file);
            return await getDownloadURL(snapshot.ref);
        } catch (error) {
            console.error("Error uploading tribe logo:", error);
            throw error;
        }
    },

    uploadBanner: async (tribeId: string, file: Blob): Promise<string> => {
        try {
            const storage = await getStorageSafe();
            const timestamp = Date.now();
            const storageRef = ref(storage, `tribes/${tribeId}/banners/${timestamp}.jpg`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error("Error uploading tribe banner:", error);
            throw error;
        }
    },

    uploadCommunityBanner: async (userId: string, type: 'forum' | 'tribe', file: Blob): Promise<string> => {
        try {
            const storage = await getStorageSafe();
            const timestamp = Date.now();
            const path = `communities/${type}/temp/${userId}/${timestamp}.jpg`;
            const storageRef = ref(storage, path);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error(`Error uploading ${type} banner:`, error);
            throw error;
        }
    },

    uploadSchoolLogo: async (userId: string, file: Blob): Promise<string> => {
        try {
            const storage = await getStorageSafe();
            const timestamp = Date.now();
            // communities/tribe/logos path covered by communities/{allPaths=**} storage rule.
            // Works pre-creation (no tribeId yet) and post-creation alike.
            const path = `communities/tribe/logos/${userId}/${timestamp}_logo.jpg`;
            const storageRef = ref(storage, path);
            const snapshot = await uploadBytes(storageRef, file);
            return await getDownloadURL(snapshot.ref);
        } catch (error) {
            console.error('Error uploading school logo:', error);
            throw error;
        }
    },

    uploadPostImage: async (type: 'forum' | 'tribe', communityId: string, userId: string, file: Blob): Promise<string> => {
        try {
            const storage = await getStorageSafe();
            const timestamp = Date.now();
            const path = `communities/${type}/${communityId}/posts/${userId}/${timestamp}.jpg`;
            const storageRef = ref(storage, path);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error(`Error uploading ${type} post image:`, error);
            throw error;
        }
    },

    fetchTribesByIds: async (tribeIds: string[]): Promise<Tribe[]> => {
        if (!tribeIds || tribeIds.length === 0) return [];
        try {
            const db = await getDbSafe();
            const tribes: Tribe[] = [];
            
            // Filter unique tribe IDs to avoid double fetching
            const uniqueIds = Array.from(new Set(tribeIds));
            
            // Firestore 'in' query limit is 10-30 depending on version. 10 is safest.
            for (let i = 0; i < uniqueIds.length; i += 10) {
                const chunk = uniqueIds.slice(i, i + 10);
                const q = query(
                    collection(db, "tribes"),
                    where(documentId(), "in", chunk)
                );
                const snapshot = await getDocs(q);
                snapshot.docs.forEach(doc => {
                    tribes.push({ id: doc.id, ...doc.data() } as Tribe);
                });
            }
            return tribes;
        } catch (error) {
            console.error("Error fetching tribes by IDs:", error);
            return [];
        }
    },

    fetchTribesByOwner: async (ownerId: string): Promise<Tribe[]> => {
        if (!ownerId) return [];
        try {
            const db = await getDbSafe();
            const q = query(
                collection(db, "tribes"),
                where("ownerId", "==", ownerId),
                limit(10)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Tribe));
        } catch (error) {
            console.error("Error fetching tribes by owner:", error);
            return [];
        }
    }
};

export const AuthService = {
    login: async (email: string, password: string): Promise<User> => {
        const auth = await getAuthSafe();
        await signInWithEmailAndPassword(auth, email, password);
        // Auth itself succeeded; the UserDataContext snapshot listener will
        // populate the profile asynchronously. If the immediate Firestore
        // read races and returns "not exists" (propagation lag), swallow it
        // and return a minimal stub — the snapshot will hydrate the rest.
        try {
            return await UserService.fetchCurrentUser();
        } catch (e: any) {
            console.warn('[AuthService.login] Profile fetch raced after auth — proceeding (snapshot will hydrate):', e?.message);
            return { id: auth.currentUser?.uid || '' } as User;
        }
    },

    loginWithCustomToken: async (token: string): Promise<User> => {
        const auth = await getAuthSafe();
        await signInWithCustomToken(auth, token);
        try {
            return await UserService.fetchCurrentUser();
        } catch (e: any) {
            console.warn('[AuthService.loginWithCustomToken] Profile fetch raced after auth — proceeding:', e?.message);
            return { id: auth.currentUser?.uid || '' } as User;
        }
    },

    /**
     * Sends a Firebase password-reset email to the given address.
     * Called at the end of the manual recovery flow (after security question verification).
     */
    sendRecoveryEmail: async (email: string): Promise<void> => {
        const auth = await getAuthSafe();
        await sendPasswordResetEmail(auth, email);
    },


    reauthenticate: async (password: string): Promise<void> => {
        const auth = await getAuthSafe();
        const user = auth.currentUser;
        if (!user || !user.email) throw new Error("No user logged in or email missing");

        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
    },

    /** Provider ids on the signed-in account, e.g. ['google.com'] or ['password']. */
    getAuthProviders: async (): Promise<string[]> => {
        const auth = await getAuthSafe();
        return (auth.currentUser?.providerData || []).map(p => p.providerId).filter(Boolean);
    },

    /** True when the account has no password to re-enter — Google-only sign-in. */
    isPasswordlessAccount: async (): Promise<boolean> => {
        const providers = await AuthService.getAuthProviders();
        return providers.length > 0 && !providers.includes('password');
    },

    /**
     * Re-consent for a sensitive action (currently account deletion) on an
     * account that has no password.
     *
     * Firebase requires recent authentication before deletion. The existing
     * reauthenticate() builds an EmailAuthProvider credential, which a
     * Google-only account can never satisfy — it was being asked for a password
     * that was never set, so deletion was impossible for those users. This
     * re-consents through Google instead.
     */
    reauthenticateWithGoogle: async (): Promise<void> => {
        const auth = await getAuthSafe();
        const user = auth.currentUser;
        if (!user) throw new Error('No user logged in');

        if (Capacitor.isNativePlatform()) {
            const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
            const result = await FirebaseAuthentication.signInWithGoogle();
            const idToken = result.credential?.idToken;
            if (!idToken) throw new Error('GOOGLE_NO_ID_TOKEN');
            await reauthenticateWithCredential(user, GoogleAuthProvider.credential(idToken));
        } else {
            // Same resolver reason as googleLogin: the Auth instance is built
            // with initializeAuth and carries no default popup resolver.
            await reauthenticateWithPopup(user, new GoogleAuthProvider(), browserPopupRedirectResolver);
        }
    },

    emailLogin: async (email: string, password: string): Promise<User> => {
        const auth = await getAuthSafe();
        await signInWithEmailAndPassword(auth, email, password);
        try {
            return await UserService.fetchCurrentUser();
        } catch (e: any) {
            console.warn('[AuthService.emailLogin] Profile fetch raced after auth — proceeding:', e?.message);
            return { id: auth.currentUser?.uid || '' } as User;
        }
    },

    signup: async (email: string, password: string, userData: Partial<User>): Promise<User> => {
        const auth = await getAuthSafe();
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const cleanUsername = userData.username ? userData.username.replace('@', '').toLowerCase() : email.split('@')[0].toLowerCase();
        const newUser: User = {
            id: user.uid,
            name: userData.name || 'New User',
            username: `@${cleanUsername}`,
            location: userData.location || 'Nigeria',
            education: userData.education || '',
            occupation: userData.occupation || '',
            bio: userData.bio || '',
            identities: userData.identities || [],
            country: userData.country || 'Nigeria',
            countryFlag: userData.countryFlag || 'ng',
            // Deliberately empty rather than a pravatar placeholder. That URL is
            // a photograph of a random stranger, written into a REAL profile —
            // and because resolveUserAvatar checks croppedImage BEFORE the
            // portfolio, it also outranked the user's own photos. It additionally
            // serves no Access-Control-Allow-Origin header, so it cannot be drawn
            // into the export canvas at all, which is what turned downloaded yaps
            // into a bare initial. An empty avatar renders the initial everywhere
            // by design, which is the honest result for someone who has not set
            // a picture.
            image: userData.image || '',
            croppedImage: userData.croppedImage || '',
            gold: 2000,
            goldBalance: 2000,
            ...userData
        } as User;

        // Save to Firestore using helper
        await createUserDocument(newUser);
        return newUser;
    },

    // 🔒 PATCH 19: Username-First Signup
    signUpWithUsername: async (username: string, password: string, policyMetadata?: {
        policyAcceptedAt?: string;
        policyVersion?: string;
        ageConfirmed?: boolean;
        ageConfirmedAt?: string;
        minAgeAtSignup?: number;
    }): Promise<User> => {
        const log = (window as any).__BOOT_LOG || (() => {});
        log('[SIGNUP] 1. Starting signUpWithUsername');
        // [Removed PII console.log - was logging username]
        bootMark('AuthService:SignUpUsername:GetAuthStart');

        // [Removed PII console.log - was logging auth step]
        const auth = await getAuthSafe();
        // [Removed PII console.log - was logging auth details]
        bootMark('AuthService:SignUpUsername:GetAuthDone');

        // [Removed PII console.log - was logging db step]
        bootMark('AuthService:SignUpUsername:GetDbStart');
        const db = await getDbSafe();
        // [Removed PII console.log - was logging db details]
        bootMark('AuthService:SignUpUsername:GetDbDone');

        // 1. Generate placeholder email
        const cleanUsername = username.replace('@', '').toLowerCase();
        const placeholderEmail = `${cleanUsername}@temp.afroyap.com`;

        bootMark('AuthService:SignUpUsername:Start');
        try {
            if (!auth || !auth.app) {
                // [Removed PII console.error - was logging auth details]
                throw new Error('FIREBASE_AUTH_MISSING');
            }
            if (!auth.app.options?.apiKey) {
                // [Removed PII console.error - was logging API key absence]
                throw new Error('FIREBASE_API_KEY_MISSING');
            }

            // 2. Create User in Firebase Auth
            bootMark('AuthService:SignUpUsername:CreatingAuth');

            // Helper: Individual timeout promise
            const withLocalTimeout = (promise: Promise<any>, ms: number, msg: string) =>
                Promise.race([
                    promise,
                    new Promise((_, reject) => setTimeout(() => reject(new Error(msg)), ms))
                ]);

            // 🛡️ iOS FIX: Sometimes createUserWithEmailAndPassword promise hangs on iOS 
            // even when the account is created. We race it against an AuthStateWatcher.
            const authWatcher = new Promise<UserCredential>((resolve) => {
                log('[SIGNUP] Attaching AuthWatcher...');
                const unsub = onAuthStateChanged(auth, (user) => {
                    if (user && user.email === placeholderEmail) {
                        log(`[SIGNUP] AuthWatcher detected login for: ${user.email}`);
                        unsub();
                        resolve({ user } as UserCredential);
                    }
                });
                setTimeout(() => {
                    log('[SIGNUP] AuthWatcher timed out/cleanup');
                    unsub();
                }, 45000);
            });

            // 💓 HEARTBEAT: Manually poll auth state to bypass bridge hangs
            const heartbeat = new Promise<UserCredential>((resolve) => {
                log('[SIGNUP] Starting Heartbeat poll...');
                const interval = setInterval(() => {
                    if (auth.currentUser && auth.currentUser.email === placeholderEmail) {
                        log(`[SIGNUP] Heartbeat DETECTED user: ${auth.currentUser.uid}`);
                        clearInterval(interval);
                        resolve({ user: auth.currentUser } as UserCredential);
                    }
                }, 2000); // Check every 2s
                setTimeout(() => {
                    log('[SIGNUP] Heartbeat stopping...');
                    clearInterval(interval);
                }, 45000);
            });

            // [Removed PII console.log - was logging email/placeholderEmail]
            log(`[SIGNUP] 8. Calling Auth API ...`);
            bootMark('AuthService:SignUpUsername:BeforeAuthCreate');

            const userCredential = await withLocalTimeout(
                Promise.race([
                    createUserWithEmailAndPassword(auth, placeholderEmail, password),
                    authWatcher,
                    heartbeat
                ]),
                25000, // 🛡️ Lower timeout to 25s for better reliability on slow connections
                'AUTH_CREATE_TIMEOUT'
            ).catch(async (err: any) => {
                log(`[SIGNUP] Create Error/Timeout: ${err.message || err.code}`);

                // 🛡️ HYBRID RECOVERY: If it timed out or account exists, TRENCH THROUGH WITH LOGIN
                if (err.message === 'AUTH_CREATE_TIMEOUT' || err.code === 'auth/email-already-in-use') {
                    log('[SIGNUP] Hang/Conflict detected. Forcing Login Sweep...');
                    try {
                        // 🛡️ HARDENED SWEEP: Strict 7s timeout on login too
                        const loginCred = await withLocalTimeout(
                            signInWithEmailAndPassword(auth, placeholderEmail, password),
                            7000,
                            'LOGIN_SWEEP_TIMEOUT'
                        );
                        log(`[SIGNUP] Login Sweep SUCCESS! UID: ${loginCred.user.uid}`);
                        return loginCred;
                    } catch (loginErr: any) {
                        log(`[SIGNUP] Login Sweep Failed: ${loginErr.message}`);
                        log('CRITICAL: ALL AUTH BLOCKED. Bridge likely dead.');
                        // 🔒 iOS FIX (Bug #5): Give a user-friendly message.
                        // The account may actually have been created but the network
                        // bridge timed out. Instruct the user to try logging in.
                        const friendlyErr = new Error(
                            'Account creation timed out on this network. Your account may already exist — try logging in with your username and password.'
                        );
                        (friendlyErr as any).code = 'auth/signup-timeout';
                        throw friendlyErr;
                    }
                }
                throw err;
            });

            log(`[SIGNUP] 9. Success! uid=${userCredential.user.uid.substring(0, 6)}`);
            bootMark('AuthService:SignUpUsername:AfterAuthCreate');
            const user = userCredential.user;
            console.log('[SIGNUP] 10. User created', { uid: user.uid.slice(0, 6) });
            bootMark('AuthService:SignUpUsername:AuthCreated', `uid=${user.uid.slice(0, 6)}`);

            // 3. Create User Document
            const newUser: User = {
                id: user.uid,
                email: placeholderEmail,
                username: `@${cleanUsername}`, // Set username immediately
                name: '', // Will be set in profile setup
                createdAt: new Date(),
                gold: 2000, // Starting gold
                goldBalance: 2000,
                friendsCount: 0,
                isPlaceholderAccount: true, // Flag to identify temp account
                // Initialize required fields with empty defaults
                location: '',
                education: '',
                country: '',
                dateOfBirth: '', // 🔒 PATCH: Ensure this field exists for completeness check
                countryFlag: '',
                image: '',
                bio: '',
                identities: [],
                occupation: '',
                portfolio: [],
                portfolioPreviews: [],
                pinnedYapIds: [],
                shoutedYapIds: [],
                joinedForumIds: [],
                ownedForumIds: [],
                ownedTribeIds: [],
                // Default all push categories to true so the Cloud Function never
                // needs to guess whether a field is missing vs explicitly disabled.
                notificationPreferences: {
                    socialNotifications: true,
                    likes: true,
                    comments: true,
                    shouts: true,
                    messages: true,
                    connections: true,
                    tribeActivity: true,
                    yapDay: true,
                    dailyPrompt: true,
                },
                // 🔒 COMPLIANCE: Store policy agreement metadata
                ...(policyMetadata && {
                    policyAcceptedAt: policyMetadata.policyAcceptedAt,
                    policyVersion: policyMetadata.policyVersion,
                    ageConfirmed: policyMetadata.ageConfirmed,
                    ageConfirmedAt: policyMetadata.ageConfirmedAt,
                    minAgeAtSignup: policyMetadata.minAgeAtSignup,
                }),
            };

            bootMark('AuthService:SignUpUsername:DocSaving');
            await createUserDocument(newUser);

            bootMark('AuthService:SignUpUsername:Finish');
            return newUser;
        } catch (err: any) {
            log(`[SIGNUP] Catching error: ${err.message}`);

            // 🛡️ Cleanup: Fail fast and clear the trigger so it doesn't break next login
            try {
                const { SafeStorage } = await import('../utils/safeStorage');
                SafeStorage.removeItem('login_intent_username');
            } catch (e) {}

            // 🛡️ iOS FAIL-SAFE: If account was created but we timed out/hung
            if (err.message === 'AUTH_CREATE_TIMEOUT' || err.code === 'auth/email-already-in-use') {
                log('[SIGNUP] Hang/Conflict detected. Checking if account was actually created...');

                // If we are logged in now, or if it was a conflict, we might have succeeded
                if (auth.currentUser || err.code === 'auth/email-already-in-use') {
                    log('[SIGNUP] Probable success! Attempting to finalize user doc...');

                    // Re-calculate user data for recovery
                    const recoveryId = auth.currentUser?.uid || `recovery_${cleanUsername}`;
                    const newUser: User = {
                        id: recoveryId,
                        email: placeholderEmail,
                        username: `@${cleanUsername}`,
                        name: '',
                        createdAt: new Date(),
                        gold: 2000,
                        goldBalance: 2000,
                        isPlaceholderAccount: true,
                        location: '',
                        education: '',
                        country: '',
                        dateOfBirth: '',
                        countryFlag: '',
                        image: '',
                        bio: '',
                        identities: [],
                        occupation: '',
                        portfolio: [],
                        portfolioPreviews: [],
                        pinnedYapIds: [],
                        shoutedYapIds: [],
                        joinedForumIds: [],
                        joinedTribeIds: [],
                        ownedForumIds: [],
                        ownedTribeIds: [],
                    };

                    try {
                        await createUserDocument(newUser);
                        log('[SIGNUP] Recovery successful! Proceeding...');
                        return newUser;
                    } catch (docError) {
                        log('[SIGNUP] Recovery doc save failed.');
                        // 🔒 iOS FIX (Bug #5): Doc save failed but auth account may exist.
                        // This can happen when the app regains connectivity mid-signup.
                        // Instruct user to log in — ProfileSetupScreen will recreate the doc.
                        const friendlyErr = new Error(
                            'Connection issue during signup. Your account may have been created — please try logging in with your username and password.'
                        );
                        (friendlyErr as any).code = 'auth/signup-partial';
                        throw friendlyErr;
                    }
                }
            }

            console.error("[SIGNUP] ERROR: Signup failed", {
                message: err.message,
                code: err.code,
                name: err.name,
                stack: err.stack?.substring(0, 200)
            });
            bootMark('AuthService:SignUpUsername:Fail', err.message);
            throw err;
        }
    },

    emailSignUp: async (email: string, password: string, userData: Partial<User>): Promise<User> => {
        const auth = await getAuthSafe();
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const cleanUsername = userData.username ? userData.username.replace('@', '').toLowerCase() : email.split('@')[0].toLowerCase();
        const newUser: User = {
            id: user.uid,
            name: userData.name || 'New User',
            username: `@${cleanUsername}`,
            location: userData.location || 'Nigeria',
            education: userData.education || '',
            occupation: userData.occupation || '',
            bio: userData.bio || '',
            identities: userData.identities || [],
            country: userData.country || 'Nigeria',
            countryFlag: userData.countryFlag || 'ng',
            // Empty, for the same reason the other signup path above says so:
            // that pravatar URL is a photograph of a random stranger written
            // into a real profile, identical on every account that took this
            // path. This site was missed when the others were fixed.
            image: userData.image || '',
            croppedImage: userData.croppedImage || '',
            gold: 2000,
            goldBalance: 2000,
            ...userData
        } as User;

        // Save to Firestore using helper
        await createUserDocument(newUser);
        // Do NOT call UserService.fetchCurrentUser() as it might try to use unsafe auth proxy
        return newUser;
    },

    /**
     * Sign out of BOTH auth layers.
     *
     * This used to call only the web SDK's signOut(auth), which is why logging
     * out looked like it did nothing. On native, Google sign-in runs through
     * @capacitor-firebase/authentication (see googleLogin), and that plugin
     * keeps its own session in the iOS/Android Firebase SDK. Clearing the
     * JavaScript layer alone left the native session signed in, so the app
     * navigated to the login screen and was then handed a live user again the
     * next time auth state resolved — the user ended up back inside their
     * account without ever having logged back in.
     *
     * The plugin call is wrapped and best-effort: a failure there must not stop
     * the web sign-out below, or a partial failure would leave the person more
     * signed in than before they pressed the button.
     */
    logout: async (): Promise<void> => {
        if (Capacitor.isNativePlatform()) {
            try {
                const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
                await FirebaseAuthentication.signOut();
            } catch (e) {
                console.warn('[AuthService] Native sign-out failed; continuing with web sign-out', e);
            }
        }

        const auth = await getAuthSafe();
        await signOut(auth);

        // Per-account state that would otherwise follow the next person who
        // signs in on this device. These are written under fixed keys with no
        // uid in them, so they are not namespaced by account and do carry over.
        try {
            const { SafeStorage } = await import('../utils/safeStorage');
            [
                'likedUserIds', 'passedUserIds',
                'pending_security_setup', 'pending_guidelines', 'pending_guide',
                'login_intent_username',
            ].forEach(k => SafeStorage.removeItem(k));
        } catch { /* storage unavailable */ }

        try {
            sessionStorage.removeItem('enteredFromPush_v1');
            sessionStorage.removeItem('pendingPushRoute_v1');
        } catch { /* storage unavailable */ }
    },

    getCurrentUser: (): any => {
        return legacyAuth.currentUser;
    },

    resetPassword: async (email: string): Promise<void> => {
        const auth = await getAuthSafe();
        await sendPasswordResetEmail(auth, email);
    },

    /**
     * Step 1 of Google sign-in: authenticate with Google and report whether this
     * is a brand-new AfroYap account.
     *
     * Native (Capacitor) uses @capacitor-firebase/authentication so the OAuth
     * flow runs in the OS-provided view — the previous web signInWithRedirect
     * path was unusable inside a Capacitor WebView (it threw
     * REDIRECT_IN_PROGRESS and nothing ever called getRedirectResult).
     *
     * NOTE: this deliberately does NOT create the user document. AfroYap
     * accounts need a unique username, which Google can't supply; deriving one
     * from the email local-part (the old behaviour) collides and throws
     * USERNAME_TAKEN_IN_REGISTRY. The UI collects a username and then calls
     * completeGoogleSignup(). Returning users skip that step entirely.
     */
    googleLogin: async (): Promise<{ isNewUser: boolean; uid: string; email: string; name: string; photoURL: string }> => {
        const auth = await getAuthSafe();
        const isNative = Capacitor.isNativePlatform();

        let credentialUser: any;

        if (isNative) {
            const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
            bootMark('GoogleNativeStart');
            const result = await FirebaseAuthentication.signInWithGoogle();
            // The plugin signs in on the native layer; mirror that into the JS SDK
            // so the rest of the app (which reads the JS auth state) sees the user.
            const idToken = result.credential?.idToken;
            if (!idToken) throw new Error('GOOGLE_NO_ID_TOKEN');
            const credential = GoogleAuthProvider.credential(idToken);
            const jsResult = await signInWithCredential(auth, credential);
            credentialUser = jsResult.user;
        } else {
            bootMark('GooglePopupStart');
            // The resolver is passed explicitly because firebaseConfig builds the
            // Auth instance with initializeAuth({ popupRedirectResolver: undefined })
            // — deliberate, for the IndexedDB-first persistence iOS needs. getAuth()
            // installs browserPopupRedirectResolver for you; initializeAuth does not,
            // so signInWithPopup found no resolver and threw auth/argument-error
            // before ever opening a window. Passing it here fixes the web flow
            // without disturbing that persistence setup, and native never reaches
            // this branch.
            const result = await Promise.race([
                signInWithPopup(auth, new GoogleAuthProvider(), browserPopupRedirectResolver),
                new Promise<any>((_, reject) => setTimeout(() => reject(new Error('GOOGLE_POPUP_TIMEOUT')), 30000))
            ]);
            credentialUser = result.user;
        }

        // Existing account? Match on the Firebase UID (not the email) so a
        // returning Google user always lands on their own account.
        const db = await getDbSafe();
        const existing = await getDoc(doc(db, 'users', credentialUser.uid));

        return {
            isNewUser: !existing.exists(),
            uid: credentialUser.uid,
            email: credentialUser.email || '',
            name: credentialUser.displayName || '',
            photoURL: credentialUser.photoURL || '',
        };
    },

    /**
     * Step 2 of Google sign-in, for new accounts only: create the user document
     * under the username the visitor chose.
     *
     * Uniqueness comes from createUserDocument's transactional
     * usernames/{username} claim — the same guarantee the username/password
     * signup path relies on — so a race between two people picking the same
     * name fails loudly here instead of producing an unusable account.
     *
     * Starting gold matches signUpWithUsername (2000) rather than diverging.
     * emailVerified is left false for the client to write; onGoogleUserCreated
     * (Cloud Function) sets it to true, since that field is admin-only.
     */
    completeGoogleSignup: async (username: string): Promise<User> => {
        const auth = await getAuthSafe();
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) throw new Error('GOOGLE_SESSION_LOST');

        const cleanUsername = username.trim().replace('@', '').toLowerCase();
        if (!cleanUsername) throw new Error('USERNAME_REQUIRED');

        const appUser: User = {
            id: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || 'New User',
            username: cleanUsername,
            location: '',
            education: '',
            occupation: '',
            bio: '',
            identities: [],
            country: '',
            countryFlag: '',
            // Google's own photo when they have one, otherwise EMPTY — not a
            // placeholder URL. '/anonymous-avatar.png' used to sit here and that
            // file has never existed in public/ (only the -blue and -orange
            // variants ship), so it 404'd and left a broken image. An empty
            // value lets the Avatar component fall back to the user's initial,
            // which is the app's own designed empty state.
            image: firebaseUser.photoURL || '',
            croppedImage: firebaseUser.photoURL || '',
            dateOfBirth: '',
            gold: 2000,
            goldBalance: 2000,
            // Marks the account as Google-created for onGoogleUserCreated.
            authProvider: 'google',
            // Explicitly false, not absent. Google hands over a name and the modal
            // collects a username, so an absent flag let the route guard's leniency
            // rule (username + name + a photo) pass as soon as the first onboarding
            // step added a photo — dropping the user into the app with the rest of
            // setup unanswered. ProfileSetupScreen flips this to true when the flow
            // actually finishes.
            setupComplete: false,
        } as any;

        await createUserDocument(appUser, {
            name: appUser.name,
            username: cleanUsername,
        });

        return UserService.fetchCurrentUser();
    },

    // Sign in with Apple is NOT implemented.
    //
    // An `appleLogin` used to sit here with no callers anywhere in src/ — no
    // button, no route, nothing reached it. Removed rather than left in place,
    // because a half-finished auth path that looks complete invites someone to
    // wire it up, and this one had defects the Google path had already been
    // fixed for:
    //
    //   • it never set `authProvider`, which onGoogleUserCreated keys off;
    //   • it never set `setupComplete: false`, and the Google branch above
    //     carries a comment explaining precisely how that omission drops a user
    //     into the app with onboarding unanswered;
    //   • it called signInWithRedirect inside a Capacitor WKWebView behind a
    //     15s timeout, then threw REDIRECT_IN_PROGRESS.
    //
    // ⚠️ App Store Guideline 4.8 still applies. The app offers Google sign-in
    // from both LoginScreen and SignupScreen, and an app using a third-party
    // social login for primary account creation must also offer an equivalent
    // privacy-preserving option. Deleting this dead code does not change that
    // exposure either way: the only things that do are implementing Sign in
    // with Apple properly, or removing Google sign-in so 4.8 stops applying.

    // Helper to find user email and UID by username or phone
    findUserEmailByIdentifier: async (identifier: string): Promise<{ email: string; uid: string | null; allCandidates?: { email: string; uid: string | null }[] } | null> => {
        console.log('[AuthService] findUserEmailByIdentifier', { identifier });
        // Robust email detection regex
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
        if (isEmail) return { email: identifier, uid: null };

        // Primary path: call the resolveUsernameToEmail Cloud Function.
        // It runs with admin SDK so it bypasses the isSignedIn() Firestore rule
        // that blocks pre-auth client reads on the users collection.
        try {
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const fn = httpsCallable(getFunctions(), 'resolveUsernameToEmail');
            const result = await fn({ username: identifier }) as any;
            const { email, uid } = result.data || {};
            if (email) {
                console.log('[AuthService] resolveUsernameToEmail CF resolved:', email);
                return { email, uid: uid || null, allCandidates: [{ email, uid: uid || null }] };
            }
        } catch (cfErr: any) {
            // not-found means the username genuinely doesn't exist in the registry
            if (cfErr?.code === 'functions/not-found') {
                console.log('[AuthService] resolveUsernameToEmail: username not in registry');
                return null;
            }
            // Any other error (network, unavailable) — fall through to Firestore fallback
            console.warn('[AuthService] resolveUsernameToEmail CF failed, falling back:', cfErr?.message);
        }

        try {
            const db = await getDbSafe();
            const originalBase = identifier.trim().replace('@', '');
            const normalizedUsername = originalBase.toLowerCase();
            console.log('[AuthService] normalizedUsername:', normalizedUsername);

            // 1. Fetch Registry Entry (Try lowercase first, then original case for legacy support)
            const registryRef = doc(db, 'usernames', normalizedUsername);
            const registryDoc = await getDoc(registryRef);
            let registryData = registryDoc.exists() ? registryDoc.data() : null;

            if (!registryData && originalBase !== normalizedUsername) {
                const registryRefOriginal = doc(db, 'usernames', originalBase);
                const registryDocOriginal = await getDoc(registryRefOriginal);
                if (registryDocOriginal.exists()) {
                    registryData = registryDocOriginal.data();
                }
            }

            // 🔍 INTEGRITY CHECK: We need to see if the registry and collection agree
            // 1. Fetch Registry Entry
            // (Handled above with legacy support)

            // 1B. PROACTIVE REGISTRY UID FETCH: If we have a registry hit, find the REAL email in the doc.
            // Wrapped in try-catch: the users collection requires isSignedIn() for get, but this
            // function is called pre-login. A permission-denied error should not abort the whole
            // lookup — we still have the temp-email fallback candidate below.
            let registryDocEmail = '';
            if (registryData && registryData.uid) {
                console.log('[AuthService] Registry hit! Fetching user doc by UID:', registryData.uid);
                try {
                    const userRef = doc(db, 'users', registryData.uid);
                    const userDoc = await getDoc(userRef);
                    if (userDoc.exists()) {
                        registryDocEmail = userDoc.data()?.email || '';
                        console.log('[AuthService] Registry UID resolved to email:', registryDocEmail);
                    }
                } catch (e: any) {
                    console.log('[AuthService] Could not fetch user doc by UID (pre-auth, expected):', e?.code);
                }
            }

            // 2. Search Collection by Username Variations.
            // Also wrapped: list queries on users require isSignedIn() + ageGroup match.
            // Pre-login this will fail with permission-denied — proceed with registry candidates.
            const usernameVariations = [
                `@${normalizedUsername}`, normalizedUsername,
                `@${originalBase}`, originalBase
            ];
            const uniqueVariations = [...new Set(usernameVariations)];
            let usernameSnapshot: any = { docs: [] };
            try {
                const usernameQuery = query(collection(db, "users"), where("username", "in", uniqueVariations));
                usernameSnapshot = await getDocs(usernameQuery);
            } catch (e: any) {
                console.log('[AuthService] Username collection query blocked (pre-auth, expected):', e?.code);
            }

            // 2B. Sort candidates by "completeness" and registry status
            const allCollectionDocs = usernameSnapshot.docs.map(d => ({ id: d.id, ...d.data() } as any));
            
            allCollectionDocs.sort((a, b) => {
                const getTime = (val: any) => {
                    if (!val) return 0;
                    if (val.toMillis) return val.toMillis();
                    if (val instanceof Date) return val.getTime();
                    if (typeof val === 'number') return val;
                    if (typeof val === 'string') return new Date(val).getTime() || 0;
                    return 0;
                };

                // CRITERIA 1: Registry Match
                const aIsRegistry = a.id === registryData?.uid;
                const bIsRegistry = b.id === registryData?.uid;
                if (aIsRegistry && !bIsRegistry) return -1;
                if (!aIsRegistry && bIsRegistry) return 1;

                // CRITERIA 2: Data Completeness (prefer accounts with real names/bios)
                const aScore = (a.name ? 1 : 0) + (a.bio ? 1 : 0) + (a.username ? 1 : 0);
                const bScore = (b.name ? 1 : 0) + (b.bio ? 1 : 0) + (b.username ? 1 : 0);
                if (aScore !== bScore) return bScore - aScore;

                // CRITERIA 3: Age (Legacy accounts usually better)
                const aTime = getTime(a.createdAt) || getTime(a.timestamp);
                const bTime = getTime(b.createdAt) || getTime(b.timestamp);
                return aTime - bTime; // Oldest first
            });

            let collectionUser = allCollectionDocs.length > 0 ? allCollectionDocs[0] : null;

            // 3. MULTI-CANDIDATE RESOLUTION
            const resolutionCandidates: { email: string; uid: string | null }[] = [];

            // A. Add Registry Entry if it exists (Prioritize the email found in the doc itself)
            if (registryData) {
                if (registryDocEmail) {
                    resolutionCandidates.push({ email: registryDocEmail, uid: registryData.uid });
                }
                
                // Fallbacks from registry metadata
                if (registryData.email) {
                    resolutionCandidates.push({ email: registryData.email, uid: registryData.uid });
                }
                
                // Generic placeholder fallback
                resolutionCandidates.push({ 
                    email: `${normalizedUsername}@temp.afroyap.com`, 
                    uid: registryData.uid 
                });
            }

            // B. Add Collection Entries
            allCollectionDocs.forEach(doc => {
                // Priority 1: The actual email in the doc
                if (doc.email) {
                    resolutionCandidates.push({ email: doc.email, uid: doc.id });
                }
                // Priority 2: The standard temp email for this username
                const tempEmail = `${normalizedUsername}@temp.afroyap.com`;
                resolutionCandidates.push({ email: tempEmail, uid: doc.id });
            });

            // C. Add Phone Entries (if doc has them)
            allCollectionDocs.forEach(doc => {
                if (doc.phoneNumber) {
                    // Try to find if an email is associated with this phone in Auth?
                    // We don't have a direct way, but we can try common patterns
                }
            });

            // D. Always include the temp email as a final fallback for this username
            resolutionCandidates.push({ email: `${normalizedUsername}@temp.afroyap.com`, uid: registryData?.uid || null });

            // E. Try Search by Phone (Proactive)
            const resolutionPhone = identifier.replace(/\D/g, '');
            if (resolutionPhone.length >= 7) {
                const phoneQuery = query(collection(db, "users"), where("phoneNumber", "in", [resolutionPhone, `+${resolutionPhone}`]));
                try {
                    const phoneSnapshot = await getDocs(phoneQuery);
                    phoneSnapshot.forEach(doc => {
                        const data = doc.data();
                        if (data.email) resolutionCandidates.push({ email: data.email, uid: doc.id });
                        resolutionCandidates.push({ email: `${data.username || normalizedUsername}@temp.afroyap.com`, uid: doc.id });
                    });
                } catch (e) {}
            }

            // F. Deduplicate and Sort
            // Sort criteria: 
            // 1. Exact UID match with registry
            // 2. Placeholder accounts first (standard for this app)
            // 3. Newest documents first
            const uniqueMap = new Map<string, { email: string; uid: string | null }>();
            resolutionCandidates.forEach(c => {
                const key = `${c.email}-${c.uid}`;
                if (!uniqueMap.has(key)) uniqueMap.set(key, c);
            });

            const uniqueCandidates = Array.from(uniqueMap.values()).sort((a, b) => {
                // 🕵️ ADVANCED SORTING: Prefer accounts with actual data
                const aData = allCollectionDocs.find(d => d.id === a.uid) || {};
                const bData = allCollectionDocs.find(d => d.id === b.uid) || {};
                
                // 1. Registry Match (Strong Signal)
                const aIsRegistry = a.uid === registryData?.uid;
                const bIsRegistry = b.uid === registryData?.uid;
                
                // 2. Data Completeness (Critical Signal for Recovery)
                const aScore = (aData.name ? 1 : 0) + (aData.bio ? 1 : 0) + (aData.username ? 1 : 0);
                const bScore = (bData.name ? 1 : 0) + (bData.bio ? 1 : 0) + (bData.username ? 1 : 0);
                
                if (aScore !== bScore) return bScore - aScore; // Prefer more complete profiles
                
                // If data is equal, prefer registry match
                if (aIsRegistry && !bIsRegistry) return -1;
                if (!aIsRegistry && bIsRegistry) return 1;

                // 3. Email type (prefer real emails)
                const aIsTemp = a.email.endsWith('@temp.afroyap.com');
                const bIsTemp = b.email.endsWith('@temp.afroyap.com');
                if (aIsTemp && !bIsTemp) return 1; 
                if (!aIsTemp && bIsTemp) return -1;

                // 4. Age (prefer older accounts if equal)
                const getTime = (val: any) => {
                    if (!val) return Date.now();
                    if (val.toMillis) return val.toMillis();
                    if (val instanceof Date) return val.getTime();
                    return Date.now();
                };
                const aTime = getTime(aData.createdAt) || getTime(aData.timestamp);
                const bTime = getTime(bData.createdAt) || getTime(bData.timestamp);
                return aTime - bTime;
            });

            console.log(`[AuthService] ========================================`);
            console.log(`[AuthService] RESOLUTION SUMMARY for '${normalizedUsername}':`);
            console.log(`[AuthService] Registry Data:`, registryData);
            console.log(`[AuthService] Registry Doc Email:`, registryDocEmail || '(none)');
            console.log(`[AuthService] Total Candidates:`, uniqueCandidates.length);
            uniqueCandidates.forEach((c, i) => {
                console.log(`[AuthService]   [${i}] Email: ${c.email}, UID: ${c.uid}`);
            });
            console.log(`[AuthService] ========================================`);

            if (uniqueCandidates.length > 0) {
                return { 
                    email: uniqueCandidates[0].email, 
                    uid: uniqueCandidates[0].uid,
                    allCandidates: uniqueCandidates
                };
            }

            // 4. Try Search by Phone (Last resort)
            const cleanPhone = identifier.replace(/\D/g, '');
            if (cleanPhone.length >= 7) {
                const phoneQuery = query(collection(db, "users"), where("phoneNumber", "==", cleanPhone));
                const phoneSnapshot = await getDocs(phoneQuery);
                if (!phoneSnapshot.empty) {
                    const data = phoneSnapshot.docs[0].data();
                    console.log('[AuthService] Found by phone:', data.email);
                    return { email: data.email, uid: phoneSnapshot.docs[0].id };
                }
            }
        } catch (error) {
            console.error("Error finding user by identifier:", error);
        }
        return null;
    },

    /**
     * 🔒 PATCH 1A: Delegate to UsernameService for registry-based availability
     * This ensures all availability checks use the usernames registry as source of truth
     * @param username - Username to check (with or without @)
     * @returns true if available, false if taken
     */
    checkUsernameAvailability: async (username: string): Promise<boolean> => {
        // Dynamically import to avoid circular dependency
        const { UsernameService } = await import('./usernameService');
        return UsernameService.checkUsernameAvailability(username);
    },

    loginWithIdentifier: async (identifier: string, password: string): Promise<User> => {
        console.log('[AuthService] loginWithIdentifier called', { identifier });
        
        // 🆔 INTENT CAPTURE: Store the name explicitly typed by the user
        // This allows syncAndAuditUser to know who we WERE TRYING to be
        const cleanIdentifier = identifier.trim().replace('@', '').toLowerCase();
        try {
            const { SafeStorage } = await import('../utils/safeStorage');
            SafeStorage.setItem('login_intent_username', cleanIdentifier);
        } catch (e) {}
        const trimmedIdentifier = identifier.trim();
        bootMark('AuthService:Login:Start');

        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedIdentifier);
        console.log('[AuthService] isEmail check:', isEmail);

        let candidates: { email: string; uid: string | null }[] = [];
        let resolvedUsername = '';
        let allCandidates: { email: string; uid: string | null }[] = [];

        if (isEmail) {
            candidates = [{ email: identifier, uid: null }];
        } else {
            console.log('[AuthService] resolving candidates for identifier...');
            const result = await Promise.race([
                AuthService.findUserEmailByIdentifier(identifier),
                new Promise<any>((_, reject) => setTimeout(() => reject(new Error('USERNAME_LOOKUP_TIMEOUT')), 10000))
            ]);
            console.log('[AuthService] resolution result:', result);

            if (!result || (!result.email && (!result.allCandidates || result.allCandidates.length === 0))) {
                throw new Error(`User not found with identifier '${identifier}'. Please check spelling or sign up.`);
            }
            
            candidates = result.allCandidates || [{ email: result.email, uid: result.uid }];
            allCandidates = candidates; // Use the same array for recovery
            resolvedUsername = identifier.replace('@', '').toLowerCase();
        }

        const auth = await getAuthSafe();
        bootMark('AuthService:Login:SignIn');
        
        let lastError: any = null;

        // 🔥 MULTI-CANDIDATE RETRY LOOP
        for (const candidate of candidates) {
            console.log(`[AuthService] Attempting login strike for: ${candidate.email} (UID: ${candidate.uid || 'unknown'})`);
            try {
                await Promise.race([
                    signInWithEmailAndPassword(auth, candidate.email, password),
                    new Promise<never>((_, reject) => setTimeout(() => reject(new Error('SIGNIN_TIMEOUT')), 15000))
                ]);
                console.log(`[AuthService] Login successful! Account: ${candidate.email}`);
                
                bootMark('AuthService:Login:FetchUser');
                return UserService.fetchCurrentUser();
            } catch (error: any) {
                console.warn(`[AuthService] Login failed for ${candidate.email}:`, error.code || error.message);
                lastError = error;
                
                // If it's a network error or something non-auth related, don't keep trying others
                const isNetworkError = typeof error.code === 'string' && error.code.includes('network');
                if (error.message === 'SIGNIN_TIMEOUT' || isNetworkError) {
                    throw error;
                }
                
                // Continue to next candidate if it's an invalid credential
                continue;
            }
        }

        // 🛡️ AUTH ACCOUNT RECOVERY: If all candidates failed, check if Firestore doc exists
        // This handles cases where signup created the Firestore doc but Auth account creation failed
        console.log('[AuthService] ========================================');
        console.log('[AuthService] PRE-RECOVERY DIAGNOSTIC:');
        console.log('[AuthService]   resolvedUsername:', resolvedUsername);
        console.log('[AuthService]   isEmail:', isEmail);
        console.log('[AuthService]   allCandidates:', allCandidates);
        console.log('[AuthService]   allCandidates.length:', allCandidates?.length);
        console.log('[AuthService] ========================================');
        
        const isWrongPassword = lastError?.code === 'auth/wrong-password' || lastError?.code === 'auth/invalid-credential';
        const isRateLimited = lastError?.code === 'auth/too-many-requests';
        const shouldAttemptRecovery = resolvedUsername && !isEmail && allCandidates && allCandidates.length > 0 && !isWrongPassword && !isRateLimited;

        if (shouldAttemptRecovery) {
            console.log('[AuthService] ========================================');
            console.log('[AuthService] 🛡️ ATTEMPTING AUTH ACCOUNT RECOVERY');
            console.log('[AuthService] ========================================');
            
            try {
                const db = await getDbSafe();
                const { getDoc, doc: firestoreDoc } = await import('firebase/firestore');
                const { toast } = await import('sonner');
                
                // Check if any of the candidate UIDs have a Firestore document
                for (const candidate of allCandidates) {
                    if (!candidate.uid) continue;
                    
                    const userDocRef = firestoreDoc(db, 'users', candidate.uid);
                    const userDoc = await getDoc(userDocRef);
                    
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        console.log(`[AuthService] 🎯 Found orphaned Firestore doc for UID: ${candidate.uid}`);
                        toast.info(`Orphaned account detected. Attempting recovery...`, { duration: 5000 });
                        
                        // Recovery Strategy:
                        // 1. Try candidate's own email if it seems real (not temp)
                        // 2. Fallback to temp email pattern
                        const recoveryEmails = [];
                        if (candidate.email && !candidate.email.endsWith('@temp.afroyap.com')) {
                            recoveryEmails.push(candidate.email);
                        }
                        recoveryEmails.push(`${resolvedUsername}@temp.afroyap.com`);

                        for (const recoverEmail of recoveryEmails) {
                            try {
                                console.log(`[AuthService] 🛡️ Attempting recovery with email: ${recoverEmail}`);
                                const cred = await createUserWithEmailAndPassword(auth, recoverEmail, password);
                                const newUid = cred.user.uid;
                                console.log(`[AuthService] ✅ Auth account created successfully! New UID: ${newUid}`);

                                // 🔗 DATA STITCHING: Deep Migration
                                try {
                                    const { setDoc, updateDoc, writeBatch, query, collection, where, getDocs } = await import('firebase/firestore');
                                    
                                    // 1. Copy core data to new UID doc
                                    if (newUid !== candidate.uid) {
                                        console.log(`[AuthService] 🧶 Stitching data from ${candidate.uid} to ${newUid}...`);
                                        await setDoc(firestoreDoc(db, 'users', newUid), {
                                            ...userData,
                                            id: newUid,
                                            source: 'RECOVERY_STITCHED',
                                            recoveredAt: serverTimestamp(),
                                            originalId: String(candidate.uid),
                                            // Ensure critical fields exist to bypass setup screen
                                            dateOfBirth: userData.dateOfBirth || '1990-01-01',
                                            country: userData.country || 'Nigeria',
                                            username: userData.username || `@${resolvedUsername}`
                                        });

                                        // 2. Deep Migration: Yaps, Shouts, Friends
                                        console.log(`[AuthService] 🚀 Starting deep migration for root collections...`);
                                        const batch = writeBatch(db);
                                        
                                        // A. Migrate Yaps
                                        const yapsQ = query(collection(db, "yaps"), where("userId", "==", candidate.uid));
                                        const yapsSnap = await getDocs(yapsQ);
                                        yapsSnap.forEach(doc => batch.update(doc.ref, { userId: newUid }));
                                        
                                        // B. Migrate Shouts
                                        const shoutsQ = query(collection(db, "shouts"), where("shoutedBy", "==", candidate.uid));
                                        const shoutsSnap = await getDocs(shoutsQ);
                                        shoutsSnap.forEach(doc => batch.update(doc.ref, { shoutedBy: newUid }));

                                        // C. Migrate Friends (Subcollection)
                                        const friendsQ = query(collection(db, "users", candidate.uid, "friends"));
                                        const friendsSnap = await getDocs(friendsQ);
                                        for (const friendDoc of friendsSnap.docs) {
                                            await setDoc(doc(db, "users", newUid, "friends", friendDoc.id), friendDoc.data());
                                        }

                                        await batch.commit();
                                        console.log(`[AuthService] ✅ Deep migration complete.`);

                                        // 3. Mark old doc as migrated
                                        await updateDoc(userDocRef, {
                                            migratedTo: newUid,
                                            isArchived: true
                                        }).catch(() => {});
                                    }

                                    // 4. Self-Healing: Registry
                                    console.log(`[AuthService] 🩹 Healing registry for '${resolvedUsername}' -> ${newUid}`);
                                    const registryRef = firestoreDoc(db, 'usernames', resolvedUsername);
                                    await setDoc(registryRef, {
                                        uid: newUid,
                                        email: recoverEmail,
                                        username: resolvedUsername,
                                        updatedAt: serverTimestamp(),
                                        source: 'RECOVERY_AUTO_HEAL'
                                    });
                                } catch (stitchErr) {
                                    console.error("[AuthService] Data stitching failed:", stitchErr);
                                }

                                toast.success(`Account restored! Finalizing...`);
                                await signInWithEmailAndPassword(auth, recoverEmail, password);
                                
                                // ⏱️ CONSISTENCY DELAY: Wait for Firestore to catch up
                                await new Promise(resolve => setTimeout(resolve, 2000));
                                
                                return UserService.fetchCurrentUser();
                            } catch (createError: any) {
                                console.warn(`[AuthService] Recovery attempt fail for ${recoverEmail}: ${createError.code}`);
                                if (createError.code === 'auth/email-already-in-use') {
                                    // Try to log in, and then check if the profile needs healing
                                    try {
                                        const cred = await signInWithEmailAndPassword(auth, recoverEmail, password);
                                        const currentUid = cred.user.uid;
                                        console.log(`[AuthService] ✅ Logged in to existing account: ${recoverEmail} (UID: ${currentUid})`);

                                        // HEAL: If this account is blank but we have orphaned data, branch it!
                                        const currentDoc = await getDoc(doc(db, 'users', currentUid));
                                        if (!currentDoc.exists() || !currentDoc.data()?.username) {
                                            console.log(`[AuthService] 🩹 Existing account is blank. Applying emergency stitch...`);
                                            await setDoc(doc(db, 'users', currentUid), {
                                                ...userData,
                                                id: currentUid,
                                                source: 'RECOVERY_HEALED',
                                                recoveredAt: serverTimestamp(),
                                                // Ensure bypass setup
                                                dateOfBirth: userData.dateOfBirth || '1990-01-01',
                                                country: userData.country || 'Nigeria',
                                                username: userData.username || `@${resolvedUsername}`,
                                                originalId: userData.id || userData.originalId || String(candidate.uid)
                                            });

                                            // Repair Registry
                                            await setDoc(doc(db, 'usernames', resolvedUsername), {
                                                uid: currentUid,
                                                email: recoverEmail,
                                                username: resolvedUsername,
                                                updatedAt: serverTimestamp()
                                            });
                                        }

                                        return UserService.fetchCurrentUser();
                                    } catch (loginErr) {
                                        console.warn(`[AuthService] Login to existing account failed:`, loginErr);
                                    }
                                }
                            }
                        }
                    }
                }
                
                console.log('[AuthService] No orphaned Firestore docs found for recovery');
            } catch (recoveryError) {
                console.error('[AuthService] Recovery attempt failed:', recoveryError);
            }
        }

        // If we get here, all candidates failed and recovery didn't help
        console.error('[AuthService] All login candidates exhausted');

        // 🛡️ Cleanup: Fail fast and clear the trigger
        try {
            const { SafeStorage } = await import('../utils/safeStorage');
            SafeStorage.removeItem('login_intent_username');
        } catch (e) {}

        throw lastError || new Error('Login failed');
    },

    checkLoginMethods: async (email: string): Promise<string[]> => {
        try {
            console.log(`[AuthService] ======== CHECKING LOGIN METHODS ========`);
            console.log(`[AuthService] Email to check:`, email);
            const auth = await getAuthSafe();
            const methods = await fetchSignInMethodsForEmail(auth, email);
            console.log(`[AuthService] Methods found:`, methods.length > 0 ? methods : '(NONE - Account may not exist with this email)');
            console.log(`[AuthService] ==========================================`);
            return methods;
        } catch (error) {
            console.error("[AuthService] Error checking login methods:", error);
            return [];
        }
    }
};

export const MessagingService = {
    createChat: async (userAId: string, userBId: string, skipPersistence: boolean = false, isRequest: boolean = false): Promise<string> => {
        if (!userAId || !userBId) {
            console.error('[MessagingService] Missing required IDs for createChat:', { userAId, userBId });
            throw new Error('Missing user IDs for chat creation');
        }

        try {
            const db = await getDbSafe();
            
            // 🔎 ADVANCED IDENTITY RESOLUTION: Resolve usernames to UIDs
            const resolveToUid = async (id: string) => {
                // If it looks like a UID (20+ chars), skip registry
                if (id.length >= 20) return id;
                
                try {
                    const normalized = id.replace('@', '').toLowerCase();
                    const registryRef = doc(db, 'usernames', normalized);
                    const registrySnap = await getDoc(registryRef);
                    if (registrySnap.exists()) {
                        const uid = registrySnap.data()?.uid || registrySnap.data()?.userId;
                        if (uid) {
                            console.log(`[MessagingService] Resolved username ${id} to UID ${uid}`);
                            return uid;
                        }
                    }
                } catch (e) {
                    console.warn(`[MessagingService] Failed to resolve identity for ${id}:`, e);
                }
                return id; // Fallback to original
            };

            const [uidA, uidB] = await Promise.all([
                resolveToUid(userAId),
                resolveToUid(userBId)
            ]);

            console.log(`[MessagingService] Requesting chat between ${uidA} and ${uidB}`);
            
            // 🔒 SECURITY: Check if either user has blocked the other
            const [userADoc, userBDoc] = await Promise.all([
                getDoc(doc(db, 'users', uidA)),
                getDoc(doc(db, 'users', uidB))
            ]);

            if (!userBDoc.exists()) {
                console.error(`[MessagingService] Target user ${uidB} does not exist in 'users' collection!`);
                // Check if uidB was meant to be a username and failed resolution
                if (uidB.length < 20) {
                     console.warn(`[MessagingService] ${uidB} looks like a username. Attempting last-resort registry lookup...`);
                     const normalized = uidB.replace('@', '').toLowerCase();
                     const regRef = doc(db, 'usernames', normalized);
                     const regSnap = await getDoc(regRef);
                     if (regSnap.exists()) {
                         const realUid = regSnap.data().uid || regSnap.data().userId;
                         if (realUid) {
                             console.log(`[MessagingService] Last-resort success: resolved ${uidB} to ${realUid}`);
                             // This is a recursive-style correction, but for safety we just update the local ref
                             return MessagingService.createChat(uidA, realUid, skipPersistence);
                         }
                     }
                }
                throw new Error(`Target user ${uidB} not found`);
            }
            
            const userAData = userADoc.data();
            const userBData = userBDoc.data();
            const userAName = userAData?.username?.toLowerCase();
            const userBName = userBData?.username?.toLowerCase();

            const userABlocked = userAData?.blockedUsers || [];
            const userBBlocked = userBData?.blockedUsers || [];
            
            // Distinguish "I blocked them" vs "they blocked me" so the UI
            // can show an accurate message. uidA is always the sender.
            const senderBlockedRecipient = userABlocked.includes(uidB);
            const recipientBlockedSender = userBBlocked && Array.isArray(userBBlocked) && userBBlocked.includes(uidA);
            if (senderBlockedRecipient || recipientBlockedSender) {
                console.warn(`[MessagingService] Blocked interaction: sender=${uidA} recipient=${uidB} senderBlocked=${senderBlockedRecipient} recipientBlocked=${recipientBlockedSender}`);
                if (senderBlockedRecipient) {
                    throw new Error('blocked_by_you: You have blocked this user. Unblock them in Settings to message them.');
                } else {
                    throw new Error('blocked_by_them: You have been blocked by this user.');
                }
            }
            
            const participants = [uidA, uidB].sort();
            
            // 🔒 PATCH: Search for EXISTING chat first to prevent duplicates
            const chatsRef = collection(db, "chats");
            
            // Check 1: Using UID
            const uidQuery = query(chatsRef, where("participants", "array-contains", uidA));
            // Check 2: Using Username (Legacy)
            const nameQuery1 = userAName ? query(chatsRef, where("participants", "array-contains", userAName)) : null;

            const [uidSnap, nameSnap1] = await Promise.all([
                getDocs(uidQuery),
                nameQuery1 ? getDocs(nameQuery1) : { docs: [] }
            ]);
            
            const allDocs = [...uidSnap.docs, ...nameSnap1.docs];
            
            // 🔎 DETERMINISTIC SELECTION: Both users MUST pick the same chat ID if multiple exist.
            const existingChat = allDocs
                .filter((doc, index, self) => self.findIndex(d => d.id === doc.id) === index) // Unique by ID
                .filter(doc => {
                    const data = doc.data();
                    const p = (data.participants || []).map((id: string) => String(id).toLowerCase());
                    const uidALower = uidA.toLowerCase();
                    const uidBLower = uidB.toLowerCase();
                    
                    if (uidA === uidB) {
                        return p.every((id: string) => id === uidALower || (userAName && id === userAName));
                    } else {
                        const matchesUIDs = p.length === 2 && p.includes(uidALower) && p.includes(uidBLower);
                        const matchesNames = userAName && userBName && p.length === 2 && p.includes(userAName) && p.includes(userBName);
                        return matchesUIDs || matchesNames;
                    }
                })
                .sort((a, b) => {
                    // 1. Priority: Deterministic UID-based IDs
                    const aDet = a.id.includes(uidA) && a.id.includes(uidB);
                    const bDet = b.id.includes(uidA) && b.id.includes(uidB);
                    if (aDet && !bDet) return -1;
                    if (!aDet && bDet) return 1;

                    // 2. Priority: Oldest Chat
                    const timeA = a.data().createdAt?.toMillis?.() || 0;
                    const timeB = b.data().createdAt?.toMillis?.() || 0;
                    return timeA - timeB;
                })[0];

            if (existingChat) {
                console.log(`[MessagingService] Resuming master chat: ${existingChat.id}`);
                return existingChat.id;
            }

            const chatId = [uidA, uidB].sort().join('_');
            const chatRef = doc(db, "chats", chatId);
            
            if (!skipPersistence) {
                const chatPayload: any = {
                    participants,
                    lastMessage: "",
                    timestamp: serverTimestamp(),
                    createdAt: serverTimestamp()
                };
                if (isRequest) {
                    chatPayload.isRequest = true;
                    chatPayload.requestStatus = 'pending';
                    chatPayload.requestSenderId = uidA;
                    chatPayload.messageCount = 0;
                }
                await setDoc(chatRef, chatPayload, { merge: true });
            }

            return chatId;
        } catch (error: any) {
            console.error(`[MessagingService] Error in createChat (${userAId} x ${userBId}):`, error);
            if (error?.code === 'permission-denied') {
                console.warn('[MessagingService] DATABASE BLOCKED this request. Check firestore.rules partitions.');
            }
            throw error;
        }
    },

    sendMessage: async (chatId: string, senderId: string, text: string, additionalData?: any): Promise<void> => {
        const db = await getDbSafe();
        
        // 🔒 SECURITY: Rate limit — 1s between messages to prevent spam
        _checkRateLimit(senderId, 'sendMessage', 1000);

        let lastMessageText = text;
        if (!text) {
            if (additionalData?.audioUrl) lastMessageText = '🎙️ Voice Message';
            else if (additionalData?.sharedYap) lastMessageText = '🔗 Shared a Yap';
            else if (additionalData?.groupInvite) lastMessageText = '📩 Group Invite';
            // An image-only message matched NONE of the above, so the body was
            // empty and the push fell through to the generic "You have a new
            // interaction on AfroYap" — which says nothing and looks like a
            // second, redundant notification.
            else if (additionalData?.imageUrl) lastMessageText = '📷 Photo';
            else if (additionalData?.videoUrl) lastMessageText = '🎬 Video';
            else lastMessageText = 'Sent a message';
        }

        // 🔒 SECURITY: Verify sender is participant
        const chatRef = doc(db, "chats", chatId);
        const chatDoc = await getDoc(chatRef);
        
        let participants: string[] = [];
        if (!chatDoc.exists()) {
            // 🔒 LAZY CREATION: Initialize the chat document on first message
            const uidA = senderId;
            const participantsSnap = chatId.split('_');
            const uidB = participantsSnap.find(id => id !== senderId) || senderId;
            participants = [uidA, uidB].sort();

            await setDoc(chatRef, {
                participants,
                lastMessage: lastMessageText,
                timestamp: serverTimestamp(),
                createdAt: serverTimestamp()
            });
        } else {
            const chatData = chatDoc.data() || {};
            participants = chatData.participants || [];
            if (!participants.includes(senderId)) {
                throw new Error('Unauthorized: Sender not in chat');
            }
            // 🔒 MESSAGE REQUEST LIMIT: Non-friends limited to 1 message until request is accepted
            if (chatData.isRequest === true && chatData.requestStatus === 'pending' && chatData.requestSenderId === senderId) {
                const msgCount = chatData.messageCount || 0;
                if (msgCount >= 1) {
                    throw new Error('message_limit_reached: You can send more messages once your friend request is accepted.');
                }
            }
        }
        
        // 🔒 SECURITY: Verify recipient hasn't blocked sender
        const recipientId = participants.find((p: string) => p !== senderId);
        if (recipientId) {
            const recipientDoc = await getDoc(doc(db, 'users', recipientId));
            const blockedUsers = recipientDoc.data()?.blockedUsers || [];
            
            if (blockedUsers.includes(senderId)) {
                throw new Error('Cannot send message: You have been blocked');
            }
        }
        
        const messagesRef = collection(db, "chats", chatId, "messages");
        
        const messageData: any = {
            senderId,
            text,
            timestamp: serverTimestamp(),
            read: false
        };

        if (additionalData) {
            // Merge additional data (like sharedYap)
            Object.assign(messageData, additionalData);
        }

        await addDoc(messagesRef, messageData);

        // Increment messageCount if this is a request chat
        const chatSnapForCount = await getDoc(chatRef);
        const updatePayload: any = {
            lastMessage: lastMessageText,
            timestamp: serverTimestamp()
        };
        if (chatSnapForCount.exists() && chatSnapForCount.data()?.isRequest === true) {
            updatePayload.messageCount = (chatSnapForCount.data()?.messageCount || 0) + 1;
        }
        await updateDoc(chatRef, updatePayload);

        // ─────────────────────────────────────────────────────────────────
        //  PUSH NOTIFICATION TRIGGER
        // ─────────────────────────────────────────────────────────────────
        if (recipientId) {
            try {
                // Fetch sender name for the notification
                const senderSnap = await getDoc(doc(db, 'users', senderId));
                const senderName = senderSnap.exists() ? (senderSnap.data()?.name || senderSnap.data()?.username || 'Someone') : 'Someone';
                // Same resolver the rest of the app uses: honours their chosen
                // crop and never returns a placeholder.
                const senderAvatar = senderSnap.exists() ? getUserAvatar(senderSnap.data() as any) : '';

                await NotificationService.sendNotification(
                    recipientId,
                    'DIRECT_MESSAGE',
                    // Just the name. "New message from X" over the message text
                    // reads as two notifications stacked — the first line
                    // announces that a thing happened, the second is the thing.
                    // Every messaging app puts the person on line one and what
                    // they said on line two.
                    senderName,
                    lastMessageText,
                    {
                        senderId,
                        fromUserId: senderId,
                        fromUserName: senderName,
                        fromUserAvatar: senderAvatar,
                        chatId,
                        deepLink: `afroyap://chat/${chatId}`
                    }
                );
                console.log(`[MessagingService] Push notification sent to ${recipientId}`);
            } catch (notifErr) {
                console.error("[MessagingService] Failed to send push notification:", notifErr);
            }
        }
    },

    subscribeToMessages: async (chatId: string, callback: (messages: any[]) => void): Promise<() => void> => {
        const db = await getDbSafe();
        const messagesRef = collection(db, "chats", chatId, "messages");
        const q = query(messagesRef, orderBy("timestamp", "asc"));

        return onSnapshot(q, (snapshot) => {
            const messages = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    timestamp: (data.timestamp?.toMillis) ? data.timestamp.toMillis() : (typeof data.timestamp === 'number' ? data.timestamp : (data.timestamp ? new Date(data.timestamp).getTime() : Date.now())),
                    time: (data.timestamp?.toDate) ? data.timestamp.toDate().toISOString() : (typeof data.timestamp === 'number' ? new Date(data.timestamp).toISOString() : (data.timestamp ? new Date(data.timestamp).toISOString() : new Date().toISOString()))
                };
            });
            callback(messages);
        }, (error) => {
            console.error(`[MessagingService] Error in subscribeToMessages for chat ${chatId}:`, error);
        });
    },

    editMessage: async (chatId: string, messageId: string, newText: string): Promise<void> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        if (!auth.currentUser) throw new Error("Must be logged in");
        const userId = auth.currentUser.uid;

        console.log(`[MessagingService] editMessage: Chat=${chatId}, Message=${messageId}`);

        const messageRef = doc(db, "chats", chatId, "messages", messageId);
        const messageSnap = await getDoc(messageRef);
        
        if (!messageSnap.exists()) throw new Error("Message not found");
        
        const data = messageSnap.data();
        if (data.senderId !== userId) throw new Error("Unauthorized: You can only edit your own messages");
        
        // 🔒 Time Limit check (15 minutes)
        const sentTime = data.timestamp?.toMillis?.() || 0;
        const now = Date.now();
        if (now - sentTime > 15 * 60 * 1000) {
            throw new Error("Cannot edit: 15 minute limit reached");
        }

        await updateDoc(messageRef, {
            text: newText,
            edited: true,
            editedAt: serverTimestamp()
        });

        // Sync parent chat's lastMessage preview
        const chatRef = doc(db, "chats", chatId);
        const chatSnap = await getDoc(chatRef);
        if (chatSnap.exists() && chatSnap.data().lastMessage === data.text) {
             await updateDoc(chatRef, {
                 lastMessage: newText
             });
        }
    },

    deleteMessage: async (chatId: string, messageId: string): Promise<void> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        if (!auth.currentUser) throw new Error("Must be logged in");
        const userId = auth.currentUser.uid;

        console.log(`[MessagingService] deleteMessage: Chat=${chatId}, Message=${messageId}`);

        const messageRef = doc(db, "chats", chatId, "messages", messageId);
        const messageSnap = await getDoc(messageRef);
        
        if (!messageSnap.exists()) return; // Already gone
        
        const data = messageSnap.data();
        if (data.senderId !== userId) throw new Error("Unauthorized: You can only delete your own messages");

        await deleteDoc(messageRef);

        // Update parent chat preview if this was the last message
        const chatRef = doc(db, "chats", chatId);
        const chatSnap = await getDoc(chatRef);
        if (chatSnap.exists() && chatSnap.data().lastMessage === data.text) {
             await updateDoc(chatRef, {
                 lastMessage: "Message deleted"
             });
        }
    },

    getMessages: async (chatId: string): Promise<any[]> => {
        const db = await getDbSafe();
        const messagesRef = collection(db, "chats", chatId, "messages");
        const q = query(messagesRef, orderBy("timestamp", "asc"));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    markAsRead: async (chatId: string, messageId: string): Promise<void> => {
        try {
            const db = await getDbSafe();
            const messageRef = doc(db, "chats", chatId, "messages", messageId);
            await updateDoc(messageRef, { read: true });
        } catch (error) {
            console.error("Error marking message as read:", error);
        }
    },

    /**
     * Toggle a like on a chat message. Tap once → add the user to likedBy,
     * tap again → remove. Returns the new liked state for optimistic UI.
     */
    toggleMessageLike: async (chatId: string, messageId: string, userId: string): Promise<boolean> => {
        const db = await getDbSafe();
        const msgRef = doc(db, 'chats', chatId, 'messages', messageId);
        const snap = await getDoc(msgRef);
        if (!snap.exists()) throw new Error('Message not found');
        const data = snap.data();
        const alreadyLiked = (data.likedBy || []).includes(userId);
        if (alreadyLiked) {
            await updateDoc(msgRef, {
                likedBy: arrayRemove(userId),
                likes: Math.max(0, (data.likes || 1) - 1),
            });
            return false;
        }
        await updateDoc(msgRef, {
            likedBy: arrayUnion(userId),
            likes: (data.likes || 0) + 1,
        });
        return true;
    },

    markMessagesAsRead: async (chatId: string, userId: string): Promise<void> => {
        try {
            const db = await getDbSafe();
            const messagesRef = collection(db, 'chats', chatId, 'messages');

            // ⚠️ NO `where('read', '==', false)` filter here.
            // Firestore's `==` requires the field to exist on the doc, so legacy
            // messages (created before the `read` field was added) are excluded
            // from the query — but the unread COUNTER on the other side does
            // `!m.read`, which treats `undefined` as unread. The result: those
            // legacy docs were permanently counted as unread because they could
            // never match the markAsRead query. Filtering in-memory instead
            // catches both `read: false` AND missing-field cases.
            const snapshot = await getDocs(messagesRef);
            if (snapshot.empty) return;

            const targets: any[] = [];
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                // Mark as read if:
                //   • the message was NOT sent by the current user, AND
                //   • it isn't already read (either read:false OR no read field)
                if (data.senderId !== userId && data.read !== true) {
                    targets.push(doc.ref);
                }
            });

            // Chunked at 400. A writeBatch caps at 500 operations, so opening a
            // long conversation for the first time threw here, was caught below,
            // and silently left every message unread — the busiest chats being
            // exactly the ones that could never be cleared.
            const CHUNK = 400;
            for (let i = 0; i < targets.length; i += CHUNK) {
                const batch = writeBatch(db);
                targets.slice(i, i + CHUNK).forEach(ref => batch.update(ref, { read: true }));
                await batch.commit();
            }
        } catch (error) {
            console.error("Error marking messages as read:", error);
        }
    },

    subscribeToUserChats: async (userId: string, callback: (chats: any[]) => void): Promise<() => void> => {
        const db = await getDbSafe();
        const q = query(collection(db, "chats"), where("participants", "array-contains", userId));
        return onSnapshot(q, (snapshot) => {
            const chats = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            callback(chats);
        }, (error) => {
            console.error(`[MessagingService] Error in subscribeToUserChats for user ${userId}:`, error);
            // Return empty array on error to prevent UI crash
            callback([]);
        });
    },

    deleteChat: async (chatId: string): Promise<void> => {
        try {
            const db = await getDbSafe();
            await deleteDoc(doc(db, 'chats', chatId));
            console.log(`[MessagingService] Deleted chat document: ${chatId}`);
        } catch (error) {
            console.error(`[MessagingService] Error deleting chat ${chatId}:`, error);
            throw error;
        }
    },

    /**
     * Accept a message request — clears request flags so the conversation
     * moves from the Requests inbox into the main chat list.
     */
    acceptMessageRequest: async (chatId: string): Promise<void> => {
        const db = await getDbSafe();
        await updateDoc(doc(db, 'chats', chatId), {
            isRequest: false,
            requestStatus: 'accepted',
        });
    },

    /**
     * Soft-delete a message request by marking it as 'declined'.
     * The document is archived (not deleted) so it cannot be re-sent as a duplicate.
     */
    archiveMessageRequest: async (chatId: string): Promise<void> => {
        const db = await getDbSafe();
        await updateDoc(doc(db, 'chats', chatId), {
            requestStatus: 'declined',
        });
    },

    /**
     * Subscribe to pending message requests for a given user.
     * Returns an unsubscribe function.
     */
    subscribeToMessageRequests: async (userId: string, callback: (chats: any[]) => void): Promise<() => void> => {
        const db = await getDbSafe();
        const q = query(
            collection(db, 'chats'),
            where('participants', 'array-contains', userId),
            where('requestStatus', '==', 'pending')
        );
        return onSnapshot(q, (snapshot) => {
            const chats = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            callback(chats);
        }, (error) => {
            console.error(`[MessagingService] Error in subscribeToMessageRequests for ${userId}:`, error);
            callback([]);
        });
    },
};

export async function uploadProfilePhoto(userId: string, file: File | Blob, slotIndex: number, type: 'original' | 'preview' | 'video' = 'original'): Promise<string> {
    try {
        const timestamp = Date.now();
        // Extract original name if available, else default
        const fileName = (file as File).name || (type === 'video' ? 'video.mp4' : 'photo.jpg');
        
        let path = '';
        if (type === 'preview') {
            path = `users/${userId}/profilePhotos/${slotIndex}/preview-${timestamp}-${fileName}`;
        } else if (type === 'video') {
            path = `users/${userId}/portfolioVideos/${slotIndex}/${timestamp}-${fileName}`;
        } else {
            path = `users/${userId}/profilePhotos/${slotIndex}/${timestamp}-${fileName}`;
        }

        const storage = await getStorageSafe();
        const storageRef = ref(storage, path);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        return downloadURL;
    } catch (error) {
        console.error("Error uploading profile photo/video:", error);
        throw error;
    }
}

export async function uploadProfileBanner(userId: string, file: File | Blob): Promise<string> {
    try {
        console.log(`[LegionService] Starting banner upload for userId: ${userId}, size: ${file instanceof File ? file.size : 'Blob'}`);
        const storage = await getStorageSafe();
        const timestamp = Date.now();
        const path = `users/${userId}/banners/${timestamp}.jpg`;
        const storageRef = ref(storage, path);
        
        console.log(`[LegionService] Uploading to path: ${path}`);
        const snapshot = await uploadBytes(storageRef, file);
        console.log(`[LegionService] Upload complete, fetching download URL...`);
        
        const downloadURL = await getDownloadURL(snapshot.ref);
        console.log(`[LegionService] Banner URL generated: ${downloadURL.substring(0, 50)}...`);
        return downloadURL;
    } catch (error) {
        console.error("[LegionService] Error uploading profile banner:", error);
        throw error;
    }
}

export async function updateUserPortfolio(
    userId: string,
    portfolio: string[],
    profilePictureIndex?: number,
    portfolioPreviews?: string[],
    photoMetadata?: { slot: number; signature: string; timestamp: number }[],
    portfolioVideos?: string[]
): Promise<void> {
    try {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);
        const updates: any = {
            portfolio,
            portfolioPreviews: portfolioPreviews || [], // Always save, default to empty array
            portfolioVideos: portfolioVideos || [], // Added video support
            lastModified: serverTimestamp()
        };

        if (photoMetadata) {
            updates.photoMetadata = photoMetadata;
        }

        if (profilePictureIndex !== undefined) {
            updates.profilePictureIndex = profilePictureIndex;
            
            // 🔒 PATCH: Prioritize the SQUARE PREVIEW for the main avatar
            // This ensures the user-designated crop is used, preventing "zoomed in" chest shots
            if (portfolioPreviews && portfolioPreviews[profilePictureIndex]) {
                updates.avatar = portfolioPreviews[profilePictureIndex];
                // Also update croppedAvatar for consistency
                updates.croppedAvatar = portfolioPreviews[profilePictureIndex];
            } 
            // Fallback to original if preview is missing (legacy behavior)
            else if (portfolio[profilePictureIndex]) {
                updates.avatar = portfolio[profilePictureIndex];
            }
        }

        await updateDoc(userRef, updates);
    } catch (error) {
        console.error("Error updating user portfolio:", error);
        throw error;
    }
}

// 🔒 CONCURRENCY LOCK: Prevent duplicate notifications for the same event (sender + type + target)
// during the gap between the aggregation query and the write operation.
const notificationLocks = new Map<string, Promise<void>>();

export const NotificationService = {
    getNotifications: async (): Promise<Notification[]> => {
        const auth = await getAuthSafe();
        const db = await getDbSafe();
        const currentUser = auth.currentUser;
        if (!currentUser) return [];

        try {
            const q = query(
                collection(db, "notifications"),
                where("userId", "==", currentUser.uid),
                orderBy("timestamp", "desc")
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp),
                } as unknown as Notification;
            });
        } catch (error) {
            console.error("[NotificationService] Error fetching notifications:", error);
            return [];
        }
    },

    markAsRead: async (notificationId: string): Promise<void> => {
        try {
            const db = await getDbSafe();
            await updateDoc(doc(db, "notifications", notificationId), { read: true });
        } catch (error) {
            console.error("[NotificationService] Error marking as read:", error);
        }
    },

    /**
     * Mark every notification read, for real.
     *
     * "Mark all read" appeared to work and then everything came back unread.
     * Four separate reasons, all of them silent:
     *
     *  1. THE `read` FIELD HAS TO EXIST. The query filtered `read == false`, but
     *     Firestore equality only matches documents that HAVE the field, while
     *     the unread count is computed client-side as `!n.read` — which counts a
     *     missing field as unread. So any notification without `read` was
     *     permanently unread: counted by the badge, invisible to this query,
     *     impossible to clear. Exactly the bug already fixed for chat messages
     *     in markMessagesAsRead, whose comment says so; it was never brought
     *     back here. Filtering in memory catches `read: false` AND absent.
     *
     *  2. limit(100). Unread number 101 onward was never touched.
     *
     *  3. MERGED IDENTITIES. The listener subscribes for the current uid AND
     *     originalId, so notifications addressed to a pre-merge id are shown and
     *     counted — but this took a single uid, so those could never be cleared.
     *
     *  4. FAILURES WERE SWALLOWED. The catch only logged, so the caller's
     *     optimistic "all read" stayed on screen until the listener replaced it
     *     with the truth. That is precisely the "marks read, then they come
     *     back" symptom. It throws now, and the caller rolls back.
     *
     * Batches are chunked at 400 (the limit is 500) so a large inbox commits
     * instead of throwing.
     */
    markAllAsRead: async (userIds: string | string[]): Promise<void> => {
        const ids = (Array.isArray(userIds) ? userIds : [userIds])
            .map(id => String(id || '').trim())
            .filter(Boolean);
        if (ids.length === 0) return;

        const db = await getDbSafe();
        const targets: any[] = [];

        for (const uid of ids) {
            // No `read` filter, deliberately: see (1) above.
            const snapshot = await getDocs(query(
                collection(db, "notifications"),
                where("userId", "==", uid),
            ));
            snapshot.docs.forEach(d => {
                if (d.data()?.read !== true) targets.push(d.ref);
            });
        }

        if (targets.length === 0) return;

        const CHUNK = 400;
        for (let i = 0; i < targets.length; i += CHUNK) {
            const batch = writeBatch(db);
            targets.slice(i, i + CHUNK).forEach(ref => batch.update(ref, { read: true }));
            await batch.commit();
        }

        console.log(`[NotificationService] Marked ${targets.length} notifications as read across ${ids.length} id(s)`);
    },

    registerDeviceToken: async (userId: string, token: string, platform: 'ios' | 'android' | 'web'): Promise<void> => {
        try {
            const db = await getDbSafe();
            const tokenRef = doc(db, 'userDeviceTokens', userId);
            const deviceId = await _getStableDeviceId();

            // 1. Write the new token atomically (merge so we never erase other devices' tokens).
            //    Store the device id so we can identify this same physical device on
            //    the next token rotation.
            await setDoc(tokenRef, {
                [token]: { platform, deviceId, updatedAt: serverTimestamp() }
            }, { merge: true });

            // 2. Prune stale tokens. The main cause of duplicate pushes is a single
            //    device's FCM token rotating (app update/reinstall/FCM refresh) while
            //    the old token stays valid — so remove any OTHER token for THIS SAME
            //    deviceId immediately. (Pruning by platform alone would wrongly delete a
            //    second same-platform device.) Legacy tokens with no deviceId fall back
            //    to the old 60-day same-platform prune.
            try {
                const snap = await getDoc(tokenRef);
                if (snap.exists()) {
                    const sixtyDaysAgo = Date.now() - 60 * 24 * 60 * 60 * 1000;
                    const staleFields: Record<string, any> = {};
                    for (const [t, meta] of Object.entries(snap.data() as Record<string, any>)) {
                        if (t === token) continue; // never delete the token we just wrote
                        const m = meta as any;
                        if (deviceId && m?.deviceId && String(m.deviceId) === String(deviceId)) {
                            // Same physical device → this is a rotated/old token.
                            staleFields[t] = deleteField();
                        } else if (!m?.deviceId && m?.platform === platform) {
                            // Legacy token without a deviceId → old 60-day prune.
                            const updatedMs = m?.updatedAt?.toMillis?.() ?? 0;
                            if (updatedMs < sixtyDaysAgo) staleFields[t] = deleteField();
                        }
                    }
                    if (Object.keys(staleFields).length > 0) {
                        await updateDoc(tokenRef, staleFields);
                        console.log(`[NotificationService] Pruned ${Object.keys(staleFields).length} stale token(s) for ${userId}`);
                    }
                }
            } catch (pruneErr) {
                console.warn('[NotificationService] Token pruning failed (non-critical):', pruneErr);
            }

            console.log(`[NotificationService] Registered ${platform} device token for ${userId}`);
        } catch (error) {
            console.error("[NotificationService] Error registering device token:", error);
        }
    },

    sendNotification: async (userId: string, type: string, title: string, message: string, metadata?: any): Promise<void> => {
        const typeKey = type.toUpperCase();
        const senderId = String(metadata?.senderId || metadata?.fromUserId || '');
        const targetId = String(metadata?.targetId || metadata?.yapId || metadata?.postId || '');

        // 🔒 Check if this is an aggregateable type that needs locking
        const isAggregateType = ['YAP_LIKE', 'NEW_COMMENT', 'YAP_COMMENT', 'YAP_SHOUT', 'SLAP', 'FRIEND_REQUEST', 'CONNECTION_REQUEST'].includes(typeKey);
        const lockKey = isAggregateType ? `${userId}_${typeKey}_${senderId}_${targetId}` : null;

        if (lockKey) {
            const existingLock = notificationLocks.get(lockKey);
            if (existingLock) {
                console.log(`[NotificationService] Waiting for existing lock: ${lockKey}`);
                await existingLock;
            }
        }

        let resolveLock: () => void;
        const lockPromise = new Promise<void>(resolve => { resolveLock = resolve; });
        if (lockKey) notificationLocks.set(lockKey, lockPromise);

        try {
            const db = await getDbSafe();
            const notificationsRef = collection(db, "notifications");

            // ─────────────────────────────────────────────────────────────────
            //  WATCHER_MOD bypasses certain checks — toujours delivery
            // ─────────────────────────────────────────────────────────────────
            const isCritical = type.toUpperCase() === 'WATCHER_MOD' || type.toUpperCase() === 'SYSTEM';

            // REMOVED local preference check. 
            // We now always create the notification document in Firestore 
            // so that in-app history is MAINTAINED. 
            // The Cloud Function onNotificationCreated will handle checking 
            // preferences and suppressing the out-of-app push notification (FCM).

            // ─────────────────────────────────────────────────────────────────
            //  DERIVE SCHEMA FIELDS — infer from metadata to lock data contract
            //  senderId  → the actor who triggered this event
            //  targetId  → the post / entity this event is about
            //  deepLink  → the route the app should open when tapping
            //  threadId  → iOS APNs stacking key (groups same-type notifications)
            //  collapseKey → Android FCM grouping key
            // ─────────────────────────────────────────────────────────────────
            const senderId  = String(metadata?.senderId || metadata?.fromUserId || '');
            const targetId  = String(metadata?.targetId || metadata?.yapId || metadata?.postId || metadata?.shoutId || '');

            let deepLink    = 'afroyap://notifications';
            let threadId    = 'general';
            let collapseKey = 'general';

            const typeKey = type.toUpperCase();
            if (typeKey === 'SLAP') {
                deepLink    = senderId ? `afroyap://profile/${senderId}` : 'afroyap://notifications';
                threadId    = 'slaps';
                collapseKey = `slaps-${userId}`;
            } else if (typeKey === 'CONNECTION_REQUEST' || typeKey === 'FRIEND_REQUEST') {
                deepLink    = 'afroyap://connections';
                threadId    = 'connections';
                collapseKey = `connections-${userId}`;
                // Correct label — never inherit from a comment/like type
                title       = title || 'New Connection Request';
            } else if (typeKey === 'CONNECTION_ACCEPTED' || typeKey === 'FRIEND_ACCEPTED') {
                deepLink    = senderId ? `afroyap://profile/${senderId}` : 'afroyap://connections';
                threadId    = 'connections';
                collapseKey = `connections-${userId}`;
                title       = title || 'Connection Accepted';
            } else if (typeKey === 'NEW_COMMENT' || typeKey === 'YAP_COMMENT' || typeKey === 'FORUM_COMMENT' || typeKey === 'TRIBE_COMMENT') {
                if (typeKey === 'FORUM_COMMENT') {
                    deepLink = targetId ? `afroyap://forum/${targetId}` : 'afroyap://notifications';
                    threadId = `forum-comments-${targetId}`;
                    collapseKey = `forum-comments-${targetId}-${userId}`;
                    title = title || 'New Forum Reply';
                } else if (typeKey === 'TRIBE_COMMENT') {
                    deepLink = targetId ? `afroyap://tribe/${targetId}` : 'afroyap://notifications';
                    threadId = `tribe-comments-${targetId}`;
                    collapseKey = `tribe-comments-${targetId}-${userId}`;
                    title = title || 'New Tribe Reply';
                } else {
                    deepLink = targetId ? `afroyap://yap/${targetId}` : 'afroyap://notifications';
                    threadId = `yap-comments-${targetId}`;
                    collapseKey = `yap-comments-${targetId}-${userId}`;
                    title = title || 'New Comment';
                }
            } else if (typeKey === 'YAP_LIKE' || typeKey === 'FORUM_LIKE' || typeKey === 'TRIBE_LIKE') {
                if (typeKey === 'FORUM_LIKE') {
                    deepLink = targetId ? `afroyap://forum/${targetId}` : 'afroyap://notifications';
                    threadId = `forum-likes-${targetId}`;
                    collapseKey = `forum-likes-${targetId}-${userId}`;
                } else if (typeKey === 'TRIBE_LIKE') {
                    deepLink = targetId ? `afroyap://tribe/${targetId}` : 'afroyap://notifications';
                    threadId = `tribe-likes-${targetId}`;
                    collapseKey = `tribe-likes-${targetId}-${userId}`;
                } else {
                    deepLink = targetId ? `afroyap://yap/${targetId}` : 'afroyap://notifications';
                    threadId = `yap-likes-${targetId}`;
                    collapseKey = `yap-likes-${targetId}-${userId}`;
                }
            } else if (typeKey === 'MENTION') {
                // Mentions always deep-link to the post they happened in — the
                // caller passes the parent post id as targetId for every source
                // type (yap / comment / shout / forum / tribe).
                deepLink    = metadata?.deepLink || (targetId ? `afroyap://yap/${targetId}` : 'afroyap://notifications');
                threadId    = `mentions-${targetId}`;
                collapseKey = `mentions-${targetId}-${userId}`;
            } else if (typeKey === 'YAP_SHOUT') {
                deepLink    = targetId ? `afroyap://yap/${targetId}` : 'afroyap://notifications';
                threadId    = `yap-shouts-${targetId}`;
                collapseKey = `yap-shouts-${targetId}-${userId}`;
            } else if (type === 'WATCHER_MOD') {
                deepLink    = 'afroyap://notifications';
                threadId    = 'moderation';
                collapseKey = `moderation-${userId}`;
            } else if (type === 'GOLDEN_HOUR' || type === 'YAPTAB_RESET') {
                // Transient alerts shouldn't be saved to DB per user request
                // But if they are sent through here, we treat them as system
                deepLink = 'afroyap://yaptab';
                threadId = 'system-alerts';
                collapseKey = `system-${type}`;
            } else if (type === 'DIRECT_MESSAGE') {
                deepLink    = senderId ? `afroyap://chat/${metadata?.chatId || senderId}` : 'afroyap://notifications';
                threadId    = 'messages';
                collapseKey = `messages-${userId}`;
            } else if (typeKey === 'TRIBE_JOIN_REQUEST' || typeKey === 'TRIBE_JOIN_APPROVED' || typeKey === 'TRIBE_JOIN_DENIED') {
                // Approval-gated (university) tribe flow. These used to fall
                // through with collapseKey 'general', which onNotificationCreated
                // passes straight through as the Android and web notification
                // tag — so each one replaced the last in the tray and an owner
                // with three pending requests saw a single banner.
                //
                // deepLink deliberately stays on the notifications screen: that
                // is where the inline Approve / Deny buttons are rendered.
                const tribeKey = String(metadata?.tribeId || '');
                threadId    = 'tribe-activity';
                collapseKey = typeKey === 'TRIBE_JOIN_REQUEST'
                    ? `tribe-request-${tribeKey}-${senderId}`  // one per requester per tribe
                    : `tribe-decision-${tribeKey}-${userId}`;  // one per tribe per recipient
            }

            // ─────────────────────────────────────────────────────────────────
            //  AGGREGATION LOOKUP
            // ─────────────────────────────────────────────────────────────────
            let existingId: string | null = null;
            let existingData: any = null;

            // WATCHER_MOD always creates a fresh notification — no batching
            if (!isCritical && type !== 'GOLDEN_HOUR' && type !== 'YAPTAB_RESET') {
                try {
                    // Everything that happens TO a post, so repeated interaction
                    // on one post collapses into a single row instead of filling
                    // the list with near-identical lines. MENTION was missing:
                    // being mentioned five times in one thread produced five
                    // notifications where one would do.
                    const isEngagement = [
                        'YAP_LIKE', 'FORUM_LIKE', 'TRIBE_LIKE',
                        'YAP_COMMENT', 'FORUM_COMMENT', 'TRIBE_COMMENT', 'NEW_COMMENT',
                        'YAP_SHOUT', 'MENTION', 'REPLY_LIKE'
                    ].includes(typeKey);

                    if (targetId && isEngagement) {
                        // Matched on targetId in the QUERY, not by scanning.
                        //
                        // This used to pull the 10 most recent notifications of
                        // this type and look for the post among them. On a busy
                        // account the post's own notification is easily older
                        // than ten others, so nothing matched and a duplicate row
                        // was created — the stacking quietly stopped working at
                        // exactly the volume it exists to handle.
                        //
                        // Type is matched case-insensitively because writers
                        // disagree: some send 'YAP_LIKE', others 'yap_like', and
                        // an equality match on the raw string treated those as
                        // different kinds of event.
                        const typeVariants = Array.from(new Set([type, typeKey, type.toLowerCase()]));
                        try {
                            const q = query(
                                notificationsRef,
                                where("userId", "==", userId),
                                where("targetId", "==", targetId),
                                where("type", "in", typeVariants),
                                orderBy("timestamp", "desc"),
                                limit(1)
                            );
                            const snapshot = await getDocs(q);
                            if (!snapshot.empty) {
                                const candidate = snapshot.docs[0];
                                // Bounded to a week. Without a window, a like today
                                // would fold into one from months ago and resurface
                                // a conversation nobody remembers.
                                const ts = candidate.data().timestamp;
                                const tsMs = ts?.toMillis?.() ?? new Date(ts ?? 0).getTime();
                                if (Date.now() - tsMs <= 7 * 24 * 60 * 60 * 1000) {
                                    existingId = candidate.id;
                                    existingData = candidate.data();
                                }
                            }
                        } catch (indexError) {
                            // The composite index may still be building. Fall back
                            // to the old scan rather than failing the notification
                            // outright: a duplicate row is a far smaller problem
                            // than a like that notifies nobody.
                            console.warn('[NotificationService] Indexed grouping unavailable, scanning recent instead.', indexError);
                            const fallbackQ = query(
                                notificationsRef,
                                where("userId", "==", userId),
                                where("type", "==", type),
                                orderBy("timestamp", "desc"),
                                limit(20)
                            );
                            const snapshot = await getDocs(fallbackQ);
                            const match = snapshot.docs.find(d => d.data().targetId === targetId);
                            if (match) { existingId = match.id; existingData = match.data(); }
                        }

                    } else if (typeKey === 'CONNECTION_REQUEST' || typeKey === 'FRIEND_REQUEST') {
                        const q = query(
                            notificationsRef,
                            where("userId", "==", userId),
                            where("type", "in", ['CONNECTION_REQUEST', 'FRIEND_REQUEST', 'friend_request']),
                            where("senderId", "==", senderId), // 🔒 UNIQUE: Only aggregate if same sender
                            orderBy("timestamp", "desc"),
                            limit(5)
                        );
                        const snapshot = await getDocs(q);
                        if (!snapshot.empty) { existingId = snapshot.docs[0].id; existingData = snapshot.docs[0].data(); }

                    } else if (typeKey === 'CONNECTION_ACCEPTED' || typeKey === 'FRIEND_ACCEPTED') {
                        if (senderId) {
                            const q = query(
                                notificationsRef,
                                where("userId", "==", userId),
                                where("type", "in", ['CONNECTION_ACCEPTED', 'FRIEND_ACCEPTED', 'friend_accepted']),
                                orderBy("timestamp", "desc"),
                                limit(5)
                            );
                            const snapshot = await getDocs(q);
                            const match = snapshot.docs.find(d => d.data().senderId === senderId && !d.data().read);
                            if (match) { existingId = match.id; existingData = match.data(); }
                        }
                    } else if (type.toLowerCase() === 'slap') {
                        if (senderId) {
                            const q = query(
                                notificationsRef,
                                where("userId", "==", userId),
                                where("type", "in", ['slap', 'SLAP']),
                                orderBy("timestamp", "desc"),
                                limit(10)
                            );
                            const snapshot = await getDocs(q);
                            const nowMs = Date.now();
                            const match = snapshot.docs.find(d => {
                                const data = d.data();
                                const docSenderId = data.senderId || data.metadata?.fromUserId;
                                if (String(docSenderId) !== String(senderId)) return false;
                                
                                let tsMs = nowMs;
                                if (data.timestamp?.toMillis) tsMs = data.timestamp.toMillis();
                                else if (data.timestamp?.seconds) tsMs = data.timestamp.seconds * 1000;
                                else if (typeof data.timestamp === 'number') tsMs = data.timestamp;
                                
                                // Group only if within 40 minutes (2400000 ms)
                                if (nowMs - tsMs > 40 * 60 * 1000) return false;
                                
                                return true;
                            });
                            if (match) { existingId = match.id; existingData = match.data(); }
                        }
                    }
                } catch (groupingError) {
                    console.warn("[NotificationService] Grouping query failed. Creating fresh notification.", groupingError);
                }
            }

            // ─────────────────────────────────────────────────────────────────
            //  BUILD AGGREGATED MESSAGE
            // ─────────────────────────────────────────────────────────────────
            // If the matched existing notification was ALREADY read, do not
            // aggregate into it — aggregation writes `read: false`, which
            // would silently undo the user's explicit mark-as-read action and
            // make notifications they'd already seen reappear as unread.
            // Fall through to the "create new" branch instead so the new event
            // gets its own fresh unread doc and the old one stays read.
            if (existingData && existingData.read === true) {
                existingId = null;
                existingData = null;
            }
            if (existingId && existingData) {
                const prevCount  = existingData.metadata?.userCount || 1;
                const burst      = metadata?.slapsBurst || 1;
                const newCount   = prevCount + burst;
                const recentUsers: string[] = existingData.metadata?.recentUsers || [];
                const newName    = metadata?.fromUserName || metadata?.fromUsername || '';
                if (newName && !recentUsers.includes(newName)) recentUsers.unshift(newName);

                const newestName  = recentUsers[0] || newName || 'Someone';
                const othersCount = newCount - 1;
                let newMessage    = message;

                // One phrasing for every engagement type, keyed on the verb.
                //
                // The per-type chain here covered YAP_* only, so a forum like, a
                // tribe comment or a mention would have its COUNT incremented
                // while its text still read as a single person — the row said
                // "X liked your post" no matter how many had. Deriving the verb
                // means a new engagement type gets the aggregate wording for
                // free rather than silently reverting to the singular.
                const ENGAGEMENT_VERBS: Record<string, string> = {
                    YAP_LIKE: 'liked your post',
                    FORUM_LIKE: 'liked your post',
                    TRIBE_LIKE: 'liked your post',
                    YAP_COMMENT: 'commented on your post',
                    NEW_COMMENT: 'commented on your post',
                    FORUM_COMMENT: 'commented on your post',
                    TRIBE_COMMENT: 'commented on your post',
                    YAP_SHOUT: 'shouted your post',
                    MENTION: 'mentioned you',
                    REPLY_LIKE: 'liked your reply',
                };

                if (typeKey === 'CONNECTION_REQUEST' || typeKey === 'FRIEND_REQUEST') {
                    newMessage = newCount === 1
                        ? `${newestName} wants to connect with you`
                        : `${newCount} people want to connect with you`;
                } else if (ENGAGEMENT_VERBS[typeKey]) {
                    const verb = ENGAGEMENT_VERBS[typeKey];
                    newMessage = othersCount === 0
                        ? `${newestName} ${verb}`
                        : `${newestName} and ${othersCount} other${othersCount !== 1 ? 's' : ''} ${verb}`;
                } else if (type.toLowerCase() === 'slap') {
                    title = `${newestName} slapped you 👋`;
                    newMessage = `${newCount} time${newCount > 1 ? 's' : ''}!`;
                }

                await updateDoc(doc(db, "notifications", existingId), {
                    title:      title || existingData.title, // 🔒 FIX: Ensure title is updated/persisted during aggregation
                    message:    newMessage,
                    read:       false,
                    userAvatar: metadata?.fromUserAvatar || existingData.userAvatar,
                    userName:   newName || existingData.userName,
                    // Keep root schema fields up-to-date
                    senderId:   senderId || existingData.senderId,
                    targetId:   targetId || existingData.targetId,
                    deepLink,
                    threadId,
                    collapseKey,
                    timestamp: serverTimestamp(),
                    "metadata.userCount":      newCount,
                    "metadata.recentUsers":    recentUsers.slice(0, 10),
                    "metadata.fromUserAvatar": metadata?.fromUserAvatar || existingData.metadata?.fromUserAvatar,
                    "metadata.fromUserName":   newName || existingData.metadata?.fromUserName,
                    "metadata.fromUserId":     senderId || existingData.metadata?.fromUserId, // 🔒 FIX: Ensure fromUserId is preserved/updated
                });
                console.log(`[NotificationService] Aggregated into ${existingId} (count: ${newCount})`);
            } else {
                // ── FRESH DOCUMENT — full AfroYapNotification data contract ──
                const docRef = await addDoc(notificationsRef, {
                    // ── Identity ──────────────────────────────────────────────
                    userId:     String(userId),
                    // ── AfroYapNotification root fields ───────────────────────
                    type:       typeKey,
                    title:      title || '',
                    message,
                    senderId,          // actor who triggered event
                    targetId,          // post/entity this is about
                    deepLink,          // pre-computed navigation target
                    // ── OS stacking keys ─────────────────────────────────────
                    threadId,          // iOS APNs thread-id for grouping
                    collapseKey,       // Android FCM collapse_key for grouping
                    // ── State ─────────────────────────────────────────────────
                    read:       false,
                    // 🔒 friend_request docs must have explicit 'pending' status
                    // so the incoming/outgoing listeners can filter by status reliably
                    ...(typeKey === 'FRIEND_REQUEST' || typeKey === 'CONNECTION_REQUEST'
                        ? { status: 'pending' }
                        : {}),
                    timestamp:  serverTimestamp(),
                    // ── Display ───────────────────────────────────────────────
                    userAvatar:    metadata?.fromUserAvatar   || '',
                    userName:      metadata?.fromUserName     || '',
                    userUsername:  metadata?.fromUserUsername || metadata?.fromUsername || '', // 🔒 Required for incoming request display
                    // ── Legacy metadata blob (backwards compat) ───────────────
                    metadata: {
                        ...metadata,
                        fromUserId:  senderId || undefined,
                        userCount:   metadata?.slapsBurst || 1,
                        recentUsers: metadata?.fromUserName ? [metadata.fromUserName] : [],
                    },
                });
                console.log(`[NotificationService] Created NEW notification. ID: ${docRef.id}, Target: ${userId}, Type: ${type}`);
            }
        } catch (error) {
            console.error("[NotificationService] CRITICAL: Delivery failed:", error);
        } finally {
            if (lockKey) {
                notificationLocks.delete(lockKey);
                resolveLock!();
            }
        }
    },

    subscribeToNotifications: async (userIds: string[], callback: (notifications: Notification[]) => void): Promise<() => void> => {
        const db = await getDbSafe();
        if (!userIds || userIds.length === 0) return () => { };

        console.log('[NotificationService] Starting robust subscription for:', userIds);

        const docMaps: Map<string, any[]> = new Map();
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;

        // Track ALL active unsubscribe handles — including fallback listeners — so
        // the cleanup closure can always terminate every open channel.
        const allUnsubscribes: Array<() => void> = [];

        const handleUpdate = () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const allDocs: any[] = [];
                docMaps.forEach(docs => allDocs.push(...docs));

                // Deduplicate by doc.id
                const uniqueDocIds = new Set<string>();
                const deduplicatedDocs = allDocs.filter(doc => {
                    if (uniqueDocIds.has(doc.id)) return false;
                    uniqueDocIds.add(doc.id);
                    return true;
                });

                const sortedNotifs = deduplicatedDocs
                    .map(doc => {
                        const data = doc.data();
                        let ts = data.timestamp;
                        if (ts?.toDate) ts = ts.toDate();
                        else if (typeof ts === 'string') ts = new Date(ts);
                        else if (!ts) ts = new Date();
                        else if (!(ts instanceof Date)) ts = new Date(0);

                        return {
                            id: doc.id,
                            ...data,
                            timestamp: ts,
                        } as unknown as Notification;
                    })
                    .sort((a, b) => {
                        const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : 0;
                        const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : 0;
                        return timeB - timeA;
                    });

                callback(sortedNotifs);
            }, 80);
        };

        userIds.forEach((uid) => {
            // limit(20): fetch only the 20 most-recent notifications.
            // Older entries are loaded on-demand via pagination in NotificationsScreen.
            const q = query(
                collection(db, "notifications"),
                where("userId", "==", uid),
                orderBy("timestamp", "desc"),
                limit(20)
            );

            const primaryUnsub = onSnapshot(q, (snapshot: any) => {
                console.log(`[NotificationService] Update for ${uid}: ${snapshot.docs.length} docs`);
                docMaps.set(uid, snapshot.docs);
                handleUpdate();
            }, (error) => {
                console.error(`[NotificationService] Snapshot error for ${uid}:`, error.code, error.message);
                if (error.message.toLowerCase().includes('index')) {
                    console.warn(`[NotificationService] Missing index for ${uid}. Falling back to simple query. Create a composite index: notifications[userId ASC, timestamp DESC].`);
                    // Fallback: simple query without orderBy (avoids the missing index error)
                    const simpleQ = query(
                        collection(db, "notifications"),
                        where("userId", "==", uid),
                        limit(20)
                    );
                    // Track the fallback so it's also cleaned up on unmount
                    const fallbackUnsub = onSnapshot(simpleQ, (snap: any) => {
                        docMaps.set(uid, snap.docs);
                        handleUpdate();
                    });
                    allUnsubscribes.push(fallbackUnsub);
                }
            });

            allUnsubscribes.push(primaryUnsub);
        });

        return () => {
            console.log('[NotificationService] Cleaning up all subscriptions for:', userIds);
            if (debounceTimer) clearTimeout(debounceTimer);
            allUnsubscribes.forEach(unsub => unsub());
        };
    },

    /**
     * Broadcast a "Daily Yap / Legion Pin" notification to all users who
     * have `notificationPreferences.legionPinnedYap !== false`.
     * Batched in groups of 25 to stay within Firestore write limits.
     */
    broadcastPinnedYapNotification: async (
        yapId: string,
        yapContent: string,
        yapUsername: string,
        yapAvatar: string,
        legionUsername: string,
        pinnedByUserId: string,
    ): Promise<void> => {
        try {
            const db = await getDbSafe();

            // 1. Fetch all users (only grab id + notificationPreferences to minimise read cost)
            const usersSnap = await getDocs(collection(db, 'users'));

            const MAX_CONTENT_LENGTH = 60;
            const handle = yapUsername.startsWith('@') ? yapUsername : `@${yapUsername}`;
            const truncated = yapContent.length > MAX_CONTENT_LENGTH
                ? yapContent.slice(0, MAX_CONTENT_LENGTH).trimEnd() + '…'
                : yapContent;
            const msgBody = `${handle}: "${truncated}" — Respond before the YapDay ends!`;
            const legionHandle = legionUsername.startsWith('@') ? legionUsername : `@${legionUsername}`;

            const notifRef = collection(db, 'notifications');
            let batch = writeBatch(db);
            let operationCount = 0;

            for (const userDoc of usersSnap.docs) {
                const data = userDoc.data();
                const uid = userDoc.id;

                if (uid === pinnedByUserId) continue;

                const prefs = data.notificationPreferences || {};
                if (prefs.legionPinnedYap === false) continue;

                const newNotifRef = doc(notifRef);
                batch.set(newNotifRef, {
                    userId:     uid,
                    type:       'legion_pin',
                    title:      '📌 Pinned By Legion',
                    message:    msgBody,
                    senderId:   pinnedByUserId,
                    targetId:   yapId,
                    deepLink:   `afroyap://yap/${yapId}`,
                    threadId:   'legion-pin',
                    collapseKey: `legion-pin-${yapId}`,
                    read:       false,
                    timestamp:  serverTimestamp(),
                    userAvatar: yapAvatar,
                    userName:   legionHandle,
                    metadata: {
                        fromUserId:   pinnedByUserId,
                        fromUserName: legionHandle,
                        yapId,
                        userCount:    1,
                        recentUsers:  [legionHandle],
                    },
                });

                operationCount++;
                if (operationCount >= 450) {
                    await batch.commit();
                    batch = writeBatch(db);
                    operationCount = 0;
                }
            }

            if (operationCount > 0) {
                await batch.commit();
            }

            console.log(`[NotificationService] Broadcast legion_pin to users for yap ${yapId}`);
        } catch (error) {
            console.error('[NotificationService] broadcastPinnedYapNotification failed:', error);
        }
    },
};

export const SearchService = {
    searchUsers: async (searchTerm: string, currentUserId: string, viewerAgeGroup?: 'adult' | 'minor', viewerIsMod?: boolean): Promise<User[]> => {
        try {
            const db = await getDbSafe();
            const usersRef = collection(db, "users");
            const cleanQuery = searchTerm.replace('@', '').toLowerCase();

            // Bracket-filtered: search results are scoped to the viewer's age group.
            // Fail-closed: absent viewerAgeGroup → 'minor', matches list rule default.
            // Firestore lacks full-text search; scoring and filtering happen below.
            const ageGroup = viewerAgeGroup ?? 'minor';
            const q = query(
                usersRef,
                where("ageGroup", "==", ageGroup),
                limit(1000)
            );

            const snapshot = await getDocs(q);
            const users = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as User))
                // A permanently banned account is off the app, so it must not be
                // findable by name either — otherwise the person is gone from
                // every feed and still sitting at the top of search. Filtered
                // here rather than in the query because Firestore cannot match a
                // document whose `banned` field is simply absent, which is the
                // case for almost every account.
                .filter((u: any) => !(u.banned === true && (u.banExpiry === null || u.banExpiry === undefined))
                    && u.status !== 'banned');

            // Weighted Scoring Logic for Relevance
            const resultsWithScores = users.map(user => {
                // Moderation: hidden accounts are excluded from search for regular
                // users, but Legion/Watcher mods can still find them.
                if ((user as any).accountHidden === true && !viewerIsMod) return null;
                const isDiscoverable = user.showInDiscover !== false;
                const matchesUsername = (user.username?.toLowerCase() || '').includes(cleanQuery);
                const matchesName = (user.name?.toLowerCase() || '').includes(cleanQuery);
                const matchesTags = (user.identities || []).some(id => id.toLowerCase().includes(cleanQuery));

                if (!((isDiscoverable || matchesUsername) && (matchesUsername || matchesName || matchesTags) && user.id !== currentUserId)) {
                    return null;
                }

                let score = 0;
                const lowerName = (user.name?.toLowerCase() || '');
                const lowerUsername = (user.username?.toLowerCase() || '').replace('@', '');
                
                // 1. Exact matches (Highest priority)
                if (lowerUsername === cleanQuery) score += 100;
                if (lowerName === cleanQuery) score += 90;
                
                // 2. Starts with matches
                if (lowerUsername.startsWith(cleanQuery)) score += 50;
                if (lowerName.startsWith(cleanQuery)) score += 40;
                
                // 3. Contains matches
                if (lowerUsername.includes(cleanQuery)) score += 20;
                if (lowerName.includes(cleanQuery)) score += 10;
                
                // 4. Tag matches (Lowest priority)
                if (matchesTags) score += 5;

                return { ...user, score };
            }).filter((u): u is User & { score: number } => u !== null);

            // Sort by score (descending)
            resultsWithScores.sort((a, b) => b.score - a.score);

            // Map standard image field and slice
            return resultsWithScores.map(user => ({
                ...user,
                image: user.portfolioPreviews?.[0] || user.avatar || user.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=random`
            })).slice(0, 5);
        } catch (error) {
            console.error("User search failed:", error);
            return [];
        }
    },

    searchYaps: async (keyword: string): Promise<Yap[]> => {
        try {
            const db = await getDbSafe();
            const yapsRef = collection(db, "yaps");
            const cleanQuery = keyword.toLowerCase();

            // Fetch recent yaps and filter by content/author
            // Relax constraints: only check visibility if it's explicitly set to 'friends'
            const q = query(
                yapsRef,
                orderBy('timestamp', 'desc'),
                limit(300)
            );

            const snapshot = await getDocs(q);
            const yaps = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                time: doc.data().timestamp?.toDate ? doc.data().timestamp.toDate().toISOString() : new Date().toISOString()
            } as Yap));

            return yaps.filter(yap => {
                const isPublic = !yap.visibility || yap.visibility === 'public';
                const matchesKeyword = (yap.content?.toLowerCase() || '').includes(cleanQuery) ||
                    (yap.username?.toLowerCase() || '').includes(cleanQuery) ||
                    (yap.author?.toLowerCase() || '').includes(cleanQuery);
                return isPublic && matchesKeyword;
            }).slice(0, 50);
        } catch (error) {
            console.error("Yap search failed:", error);
            return [];
        }
    },

    searchExplore: async (
        searchTerm: string,
        /** Optional viewer-scoped filter (muted words), supplied by the caller
         *  from useContentFilter. Applied before results are trimmed to 5 — the
         *  query fetches 120 groups of each kind, so there is plenty to fall
         *  back on. */
        isMutedGroup?: (group: any) => boolean,
    ): Promise<{
        countries: any[],
        forums: Forum[],
        tribes: Tribe[],
        regions: any[]
    }> => {
        const buildVer = "2026-02-10:02:40";
        console.log(`[SearchService] Version ${buildVer} | Query:`, searchTerm);

        // Relevance score for a tribe/forum against the query. Explicit signals
        // (name, the group's own tags) rank highest, then post subject-matter
        // (mention count), then member-driven tags — so a group that actually has
        // the tag always outranks a "general" group whose members merely lean that
        // way, while general groups still surface (via name/member tags) when
        // nothing more specific matches.
        const communityRelevance = (g: any, q: string, mentions: number): number => {
            const name = (g.name?.toLowerCase() || '');
            const desc = (g.description?.toLowerCase() || '');
            const hasTag = (arr: any) => Array.isArray(arr) && arr.some((t: any) => String(t).toLowerCase().includes(q));
            const explicitTagHit = hasTag(g.tags) || hasTag(g.identities);
            const memberTagHit = hasTag(g.memberTags);
            return (name.includes(q) ? 100 : 0)
                 + (explicitTagHit ? 60 : 0)
                 + (desc.includes(q) ? 25 : 0)
                 + Math.min(mentions, 20) * 2   // post subject matter (capped)
                 + (memberTagHit ? 10 : 0);
        };

        try {
            const db = await getDbSafe();
            const cleanQuery = searchTerm.trim().toLowerCase();
            if (!cleanQuery) return { countries: [], forums: [], tribes: [], regions: [] };

            // 1. Countries Search
            const knownCountries = [
                'Nigeria', 'Ghana', 'South Africa', 'Kenya', 'Ethiopia', 'Tanzania', 'Uganda', 'Rwanda', 'Senegal', 'Cameroon',
                'Egypt', 'Morocco', 'Algeria', 'Tunisia', 'Libya', 'Sudan', 'Somalia', 'Djibouti', 'Eritrea',
                'Ivory Coast', 'Mali', 'Burkina Faso', 'Niger', 'Chad', 'Gambia', 'Guinea', 'Liberia', 'Sierra Leone',
                'Angola', 'Zambia', 'Zimbabwe', 'Botswana', 'Namibia', 'Mozambique', 'Malawi', 'Mauritius', 'Madagascar',
                'Jamaica', 'Trinidad & Tobago', 'Haiti', 'Barbados', 'Bahamas', 'Suriname', 'Guyana',
                'America', 'European Africans', 'UK Diaspora', 'Canada', 'Soulaan', 'Black American/Soulaan'
            ];

            // Country to region mapping
            const countryToRegion: Record<string, string> = {
                'Nigeria': 'West Africa', 'Ghana': 'West Africa', 'Senegal': 'West Africa', 'Ivory Coast': 'West Africa',
                'Mali': 'West Africa', 'Burkina Faso': 'West Africa', 'Niger': 'West Africa', 'Benin': 'West Africa',
                'Togo': 'West Africa', 'Sierra Leone': 'West Africa', 'Liberia': 'West Africa', 'Gambia': 'West Africa',
                'Guinea': 'West Africa', 'Mauritania': 'West Africa', 'Cape Verde': 'West Africa', 'Guinea-Bissau': 'West Africa',

                'South Africa': 'Southern Africa', 'Zimbabwe': 'Southern Africa', 'Botswana': 'Southern Africa',
                'Zambia': 'Southern Africa', 'Mozambique': 'Southern Africa', 'Namibia': 'Southern Africa',
                'Malawi': 'Southern Africa', 'Lesotho': 'Southern Africa', 'eSwatini': 'Southern Africa',

                'Kenya': 'East Africa', 'Ethiopia': 'East Africa', 'Tanzania': 'East Africa', 'Uganda': 'East Africa',
                'Rwanda': 'East Africa', 'Somalia': 'East Africa', 'Mauritius': 'East Africa', 'Madagascar': 'East Africa',
                'Burundi': 'East Africa', 'Eritrea': 'East Africa', 'Djibouti': 'East Africa', 'Seychelles': 'East Africa',
                'Comoros': 'East Africa', 'South Sudan': 'East Africa',

                'Egypt': 'North Africa', 'Morocco': 'North Africa', 'Algeria': 'North Africa', 'Tunisia': 'North Africa',
                'Libya': 'North Africa', 'Sudan': 'North Africa',

                'Cameroon': 'Central Africa', 'Angola': 'Central Africa', 'Chad': 'Central Africa', 'Gabon': 'Central Africa',
                'Congo': 'Central Africa', 'DR Congo': 'Central Africa', 'Equatorial Guinea': 'Central Africa',
                'São Tomé': 'Central Africa', 'CAR': 'Central Africa',

                'Jamaica': 'Caribbean', 'Trinidad & Tobago': 'Caribbean', 'Haiti': 'Caribbean', 'Barbados': 'Caribbean',
                'Bahamas': 'Caribbean', 'Grenada': 'Caribbean', 'Suriname': 'Caribbean', 'Guyana': 'Caribbean',

                'America': 'Diaspora', 'European Africans': 'Diaspora',
                'UK Diaspora': 'Diaspora', 'Canada': 'Diaspora', 'Soulaan': 'Diaspora', 'Black American/Soulaan': 'Diaspora'
            };

            let matchedNames = knownCountries
                .filter(c => {
                    const countryMatch = c.toLowerCase().includes(cleanQuery);
                    const regionMatch = countryToRegion[c]?.toLowerCase().includes(cleanQuery);
                    return countryMatch || regionMatch;
                })
                .slice(0, 3);

            // Force Ethiopia if "eth" is part of the query
            if (cleanQuery.includes('eth')) {
                console.log('[SearchService] Hyper-match for "eth". Forcing Ethiopia.');
                if (!matchedNames.some(c => c.toLowerCase() === 'ethiopia')) {
                    matchedNames = ['Ethiopia', ...matchedNames].slice(0, 3);
                }
            }

            const countriesData = await Promise.all(matchedNames.map(async (name) => {
                try {
                    const q = query(collection(db, "users"), where("country", "==", name), limit(100));
                    const snapshot = await getDocs(q);
                    return {
                        name,
                        members: snapshot.size >= 100 ? "100+" : snapshot.size,
                        type: 'country'
                    };
                } catch (e) {
                    return { name, members: 'Many', type: 'country' };
                }
            }));

            // 2. Forums Search (Independent)
            let forums: Forum[] = [];
            try {
                // Fetch recent posts to find mentions
                const postsSnap = await getDocs(query(collection(db, "forum_posts"), orderBy("timestamp", "desc"), limit(500)));
                const forumMentions: Record<string, number> = {};
                
                postsSnap.docs.forEach(doc => {
                    const data = doc.data();
                    if ((data.content?.toLowerCase() || '').includes(cleanQuery)) {
                        const forumId = data.topicId;
                        if (forumId) {
                            forumMentions[forumId] = (forumMentions[forumId] || 0) + 1;
                        }
                    }
                });

                const forumsSnap = await getDocs(query(collection(db, "forums"), limit(120)));
                forums = forumsSnap.docs
                    .map(doc => {
                        const data = doc.data();
                        const f = { id: doc.id, ...data, mentions: forumMentions[doc.id] || 0 } as Forum & { mentions?: number; relevance?: number };
                        f.relevance = communityRelevance(f, cleanQuery, f.mentions || 0);
                        return f;
                    })
                    .filter(f => (f.relevance || 0) > 0)
                    .filter(f => !isMutedGroup || !isMutedGroup(f))
                    .sort((a, b) => (b.relevance || 0) - (a.relevance || 0))
                    .slice(0, 5);
            } catch (e) {
                console.warn('[SearchService] Forums fetch failed:', e);
            }

            // 3. Tribes Search (Independent)
            let tribes: Tribe[] = [];
            try {
                // Fetch recent posts to find mentions
                const postsSnap = await getDocs(query(collection(db, "tribe_posts"), orderBy("timestamp", "desc"), limit(500)));
                const tribeMentions: Record<string, number> = {};
                
                postsSnap.docs.forEach(doc => {
                    const data = doc.data();
                    if ((data.content?.toLowerCase() || '').includes(cleanQuery)) {
                        const tribeId = data.tribeId;
                        if (tribeId) {
                            tribeMentions[tribeId] = (tribeMentions[tribeId] || 0) + 1;
                        }
                    }
                });

                const tribesSnap = await getDocs(query(collection(db, "tribes"), limit(120)));
                tribes = tribesSnap.docs
                    .map(doc => {
                        const data = doc.data();
                        const t = { id: doc.id, ...data, mentions: tribeMentions[doc.id] || 0 } as Tribe & { mentions?: number; relevance?: number };
                        t.relevance = communityRelevance(t, cleanQuery, t.mentions || 0);
                        return t;
                    })
                    .filter(t => (t.relevance || 0) > 0)
                    .filter(t => !isMutedGroup || !isMutedGroup(t))
                    .sort((a, b) => (b.relevance || 0) - (a.relevance || 0))
                    .slice(0, 5);
            } catch (e) {
                console.warn('[SearchService] Tribes fetch failed:', e);
            }

            // 4. Ethnic/Regional Flags Search (Independent)
            const ethnicRegions = [
                { name: 'Yoruba', region: 'Nigeria - West Africa', code: 'yoruba' },
                { name: 'Igbo', region: 'Nigeria - West Africa', code: 'igbo' },
                { name: 'Hausa', region: 'Nigeria - West Africa', code: 'hausa' },
                { name: 'Akan', region: 'Ghana - West Africa', code: 'akan' },
                { name: 'Ashanti', region: 'Ghana - West Africa', code: 'ashanti' },
                { name: 'Zulu', region: 'South Africa - Southern Africa', code: 'zulu' },
                { name: 'Xhosa', region: 'South Africa - Southern Africa', code: 'xhosa' },
                { name: 'Kikuyu', region: 'Kenya - East Africa', code: 'kikuyu' },
                { name: 'Luo', region: 'Kenya - East Africa', code: 'luo' },
                { name: 'Swahili', region: 'East Africa', code: 'swahili' },
                { name: 'Berber', region: 'North Africa', code: 'berber' },
                { name: 'Fulani/Peul', region: 'West Africa', code: 'fulani' },
                { name: 'Shona', region: 'Zimbabwe - Southern Africa', code: 'shona' },
                { name: 'Tswana', region: 'Botswana - Southern Africa', code: 'tswana' },
                { name: 'Wolof', region: 'Senegal - West Africa', code: 'wolof' },
                { name: 'Somali', region: 'Somalia - East Africa', code: 'somali' }
            ];

            const matchedRegions = ethnicRegions
                .filter(r =>
                    r.name.toLowerCase().includes(cleanQuery) ||
                    r.region.toLowerCase().includes(cleanQuery)
                )
                .slice(0, 3);

            return { countries: countriesData, forums, tribes, regions: matchedRegions };
        } catch (error) {
            console.error("Explore search fatal error:", error);
            return { countries: [], forums: [], tribes: [], regions: [] };
        }
    }
};

// ==================== LEGION SERVICE ====================
// Secure service for Legion-only moderation and governance actions
// All methods require backend validation of Legion rank

export interface AuditLog {
    id: string;
    actorId: string;
    actorUsername: string;
    targetId: string;
    targetUsername?: string;
    actionType: 'gold_adjust' | 'rank_change' | 'ban' | 'content_delete';
    beforeState?: any;
    afterState?: any;
    reason: string;
    timestamp: Date;
}

export const LegionService = {
    /**
     * Adjust a user's gold balance (grant or remove)
     * @param targetUserId - ID of the user to adjust gold for
     * @param amount - Amount to adjust (positive to grant, negative to remove)
     * @param reason - Reason for the adjustment (required for audit)
     * @returns Promise with success status, new balance, and audit ID
     */
    adjustGold: async (targetUserId: string, amount: number, reason: string): Promise<{ success: boolean; newBalance?: number; auditId?: string; error?: string }> => {
        try {
            console.log('[LegionService] adjustGold called:', { targetUserId, amount, reason });

            const db = await getDbSafe();
            const auth = await getAuthSafe();
            const currentUser = auth.currentUser;

            if (!currentUser) {
                return { success: false, error: 'Not authenticated' };
            }

            // Get current user's profile to verify Legion rank
            const actorDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (!actorDoc.exists() || actorDoc.data().rank !== 'legion') {
                return { success: false, error: 'Unauthorized: Legion rank required' };
            }

            // Get target user
            const targetUserRef = doc(db, 'users', targetUserId);
            const targetUserDoc = await getDoc(targetUserRef);

            if (!targetUserDoc.exists()) {
                return { success: false, error: 'Target user not found' };
            }

            const targetUserData = targetUserDoc.data();
            // Was `targetUserData.gold || 0` — the fifth distinct balance-read in
            // this codebase. Now the canonical one, like every other path.
            const oldBalance = readGoldBalance(targetUserData);
            const newBalance = oldBalance + amount;

            if (newBalance < 0) {
                return { success: false, error: 'Insufficient gold balance' };
            }

            await updateDoc(targetUserRef, goldFields(newBalance));

            // Create audit log
            const auditRef = await addDoc(collection(db, 'moderation_logs'), {
                actorId: currentUser.uid,
                actorUsername: actorDoc.data().username || 'Unknown',
                targetId: targetUserId,
                targetUsername: targetUserData.username || 'Unknown',
                actionType: 'gold_adjust',
                beforeState: { gold: oldBalance },
                afterState: { gold: newBalance },
                amount: amount,
                reason: reason,
                timestamp: serverTimestamp()
            });

            console.log('[LegionService] Gold adjusted successfully:', { oldBalance, newBalance, auditId: auditRef.id });

            return {
                success: true,
                newBalance: newBalance,
                auditId: auditRef.id
            };
        } catch (error: any) {
            console.error('[LegionService] adjustGold error:', error);
            return {
                success: false,
                error: error.message || 'Failed to adjust gold'
            };
        }
    },

    /**
     * Change a user's rank
     * @param targetUserId - ID of the user to change rank for
     * @param newRank - New rank to assign
     * @param reason - Reason for the rank change (required for audit)
     * @returns Promise with success status, old/new ranks, and audit ID
     */
    changeRank: async (targetUserId: string, newRank: UserRank, reason: string): Promise<{ success: boolean; oldRank?: UserRank; newRank?: UserRank; auditId?: string; error?: string }> => {
        try {
            console.log('[LegionService] changeRank called:', { targetUserId, newRank, reason });

            const db = await getDbSafe();
            const auth = await getAuthSafe();
            const currentUser = auth.currentUser;

            if (!currentUser) {
                return { success: false, error: 'Not authenticated' };
            }

            // Get current user's profile to verify Legion rank
            const actorDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (!actorDoc.exists() || actorDoc.data().rank !== 'legion') {
                return { success: false, error: 'Unauthorized: Legion rank required' };
            }

            // Get target user
            const targetUserRef = doc(db, 'users', targetUserId);
            const targetUserDoc = await getDoc(targetUserRef);

            if (!targetUserDoc.exists()) {
                return { success: false, error: 'Target user not found' };
            }

            const targetUserData = targetUserDoc.data();
            const oldRank = (targetUserData.rank || 'user') as UserRank;

            // Update user's rank
            await updateDoc(targetUserRef, {
                rank: newRank
            });

            // Dispatch notification to target user
            await NotificationService.sendNotification(
                targetUserId,
                'SYSTEM',
                'Rank Updated',
                `Your authority level has been updated to: ${newRank.toUpperCase()}`,
                { newRank, actorId: currentUser.uid }
            );

            // Create audit log
            const auditRef = await addDoc(collection(db, 'moderation_logs'), {
                actorId: currentUser.uid,
                actorUsername: actorDoc.data().username || 'Unknown',
                targetId: targetUserId,
                targetUsername: targetUserData.username || 'Unknown',
                actionType: 'rank_change',
                beforeState: { rank: oldRank },
                afterState: { rank: newRank },
                reason: reason,
                timestamp: serverTimestamp()
            });

            console.log('[LegionService] Rank changed successfully:', { oldRank, newRank, auditId: auditRef.id });

            return {
                success: true,
                oldRank: oldRank,
                newRank: newRank,
                auditId: auditRef.id
            };
        } catch (error: any) {
            console.error('[LegionService] changeRank error:', error);
            return {
                success: false,
                error: error.message || 'Failed to change rank'
            };
        }
    },

    /**
     * Ban a user (temporary or permanent)
     * @param targetUserId - ID of the user to ban
     * @param duration - Ban duration ('temporary' or 'permanent')
     * @param reason - Reason for the ban (required for audit)
     * @returns Promise with success status, ban expiry, and audit ID
     */
    banUser: async (targetUserId: string, duration: 'temporary' | 'permanent', reason: string): Promise<{ success: boolean; banExpiry?: Date; auditId?: string; error?: string }> => {
        try {
            console.log('[LegionService] banUser called:', { targetUserId, duration, reason });

            const db = await getDbSafe();
            const auth = await getAuthSafe();
            const currentUser = auth.currentUser;

            if (!currentUser) {
                return { success: false, error: 'Not authenticated' };
            }

            // Get current user's profile to verify Legion rank
            const actorDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (!actorDoc.exists() || actorDoc.data().rank !== 'legion') {
                return { success: false, error: 'Unauthorized: Legion rank required' };
            }

            // Get target user
            const targetUserRef = doc(db, 'users', targetUserId);
            const targetUserDoc = await getDoc(targetUserRef);

            if (!targetUserDoc.exists()) {
                return { success: false, error: 'Target user not found' };
            }

            const targetUserData = targetUserDoc.data();

            // Calculate ban expiry
            const banExpiry = duration === 'permanent' ? null : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 days

            // Update user's ban status
            await updateDoc(targetUserRef, {
                banned: true,
                banExpiry: banExpiry,
                banReason: reason,
                // Stamped so an appeal can record WHICH ban it is contesting. Without
                // it a reversed-then-reinstated account's old appeal is
                // indistinguishable from one against the current ban.
                bannedAt: serverTimestamp()
            });

            // Send notification
            await NotificationService.sendNotification(
                targetUserId,
                'SYSTEM',
                'Account Restricted',
                `Your account has been restricted. Reason: ${reason}. Expiry: ${banExpiry ? banExpiry.toLocaleString() : 'Permanent'}`,
                { banExpiry, reason }
            );

            // Create audit log
            const auditRef = await addDoc(collection(db, 'moderation_logs'), {
                actorId: currentUser.uid,
                actorUsername: actorDoc.data().username || 'Unknown',
                targetId: targetUserId,
                targetUsername: targetUserData.username || 'Unknown',
                actionType: 'ban',
                beforeState: { banned: targetUserData.banned || false },
                afterState: { banned: true, banExpiry: banExpiry },
                duration: duration,
                reason: reason,
                timestamp: serverTimestamp()
            });

            console.log('[LegionService] User banned successfully:', { duration, banExpiry, auditId: auditRef.id });

            return {
                success: true,
                banExpiry: banExpiry || undefined,
                auditId: auditRef.id
            };
        } catch (error: any) {
            console.error('[LegionService] banUser error:', error);
            return {
                success: false,
                error: error.message || 'Failed to ban user'
            };
        }
    },

    /**
     * Lift a ban. The counterpart banUser never had.
     *
     * Every ban field is cleared together, in one place, because they are read
     * together: isBanned() (rules) and resolveBanState() (client) both treat
     * `banned` and `banExpiry` as a pair, so clearing one and leaving the other
     * leaves a half-lifted ban that behaves differently in the two. Reversing a
     * ban previously meant editing the user document by hand, which is exactly
     * how that kind of drift starts — the same drift that made bans unenforced
     * in the first place.
     *
     * Deliberately does NOT touch banned_fingerprints: reversing a ban on this
     * account says nothing about whether a fingerprint match against a DIFFERENT
     * signup was correct. Those are separate reversals.
     */
    unbanUser: async (targetUserId: string, reason?: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const db = await getDbSafe();
            const auth = await getAuthSafe();
            const currentUser = auth.currentUser;
            if (!currentUser) return { success: false, error: 'Not authenticated' };

            const actorDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const actorRank = actorDoc.exists() ? actorDoc.data().rank : null;
            // Watchers review appeals, so both moderation ranks can lift a ban —
            // unlike banUser, which is Legion-only.
            if (!['legion', 'watcher'].includes(actorRank)) {
                return { success: false, error: 'Unauthorized: moderator rank required' };
            }

            const targetRef = doc(db, 'users', targetUserId);
            const targetDoc = await getDoc(targetRef);
            if (!targetDoc.exists()) return { success: false, error: 'Target user not found' };
            const before = targetDoc.data();

            await updateDoc(targetRef, {
                banned: false,
                banExpiry: null,
                banReason: null,
                // The legacy shape isBanned() also accepts. Left un-cleared it would
                // keep the account locked out even though `banned` is false.
                status: 'active',
                unbannedAt: serverTimestamp(),
                unbannedBy: currentUser.uid,
            });

            const { logAuditAction } = await import('./WatcherService');
            await logAuditAction({
                watcher_id: currentUser.uid,
                watcher_username: actorDoc.data()?.username || 'Unknown',
                target_user_id: targetUserId,
                action_type: 'unban',
                reason: reason || 'Ban lifted',
                metadata: { previousBanReason: before.banReason || null },
            } as any);

            return { success: true };
        } catch (error: any) {
            console.error('[LegionService] unbanUser error:', error);
            return { success: false, error: error.message || 'Failed to unban user' };
        }
    },

    /**
     * Delete content (Yap, forum post, or reply)
     * @param contentId - ID of the content to delete
     * @param contentType - Type of content ('yap', 'forum', or 'reply')
     * @param reason - Reason for deletion (required for audit)
     * @returns Promise with success status and audit ID
     */
    deleteContent: async (contentId: string, contentType: 'yap' | 'forum' | 'reply', reason: string): Promise<{ success: boolean; auditId?: string; error?: string }> => {
        try {
            console.log('[LegionService] deleteContent called:', { contentId, contentType, reason });

            const db = await getDbSafe();
            const auth = await getAuthSafe();
            const currentUser = auth.currentUser;

            if (!currentUser) {
                return { success: false, error: 'Not authenticated' };
            }

            // Get current user's profile to verify Legion rank
            const actorDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (!actorDoc.exists() || actorDoc.data().rank !== 'legion') {
                return { success: false, error: 'Unauthorized: Legion rank required' };
            }

            // Determine collection based on content type
            let collectionName = 'yaps';
            if (contentType === 'forum') collectionName = 'forumPosts';
            if (contentType === 'reply') collectionName = 'forumReplies';

            // Get content
            const contentRef = doc(db, collectionName, contentId);
            const contentDoc = await getDoc(contentRef);

            if (!contentDoc.exists()) {
                return { success: false, error: 'Content not found' };
            }

            const contentData = contentDoc.data();

            // Soft delete: mark as deleted instead of removing
            await updateDoc(contentRef, {
                deleted: true,
                deletedBy: currentUser.uid,
                deletedReason: reason,
                deletedAt: serverTimestamp()
            });

            // Create audit log
            const auditRef = await addDoc(collection(db, 'moderation_logs'), {
                actorId: currentUser.uid,
                actorUsername: actorDoc.data().username || 'Unknown',
                targetId: contentId,
                targetUsername: contentData.username || 'Unknown',
                actionType: 'content_delete',
                contentType: contentType,
                beforeState: { deleted: false },
                afterState: { deleted: true },
                reason: reason,
                timestamp: serverTimestamp()
            });

            console.log('[LegionService] Content deleted successfully:', { contentId, contentType, auditId: auditRef.id });

            return {
                success: true,
                auditId: auditRef.id
            };
        } catch (error: any) {
            console.error('[LegionService] deleteContent error:', error);
            return {
                success: false,
                error: error.message || 'Failed to delete content'
            };
        }
    },

    /**
     * Mute a user (temporary restriction)
     */
    muteUser: async (targetUserId: string, hours: number, reason: string): Promise<{ success: boolean; muteExpiry?: Date; auditId?: string; error?: string }> => {
        try {
            const db = await getDbSafe();
            const auth = await getAuthSafe();
            const currentUser = auth.currentUser;
            if (!currentUser) return { success: false, error: 'Not authenticated' };

            const actorDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (!actorDoc.exists() || actorDoc.data().rank !== 'legion') {
                return { success: false, error: 'Unauthorized' };
            }

            const muteExpiry = new Date(Date.now() + hours * 60 * 60 * 1000);
            const targetUserRef = doc(db, 'users', targetUserId);

            await updateDoc(targetUserRef, {
                muted: true,
                muteExpiry: muteExpiry,
                muteReason: reason
            });

            const auditRef = await addDoc(collection(db, 'moderation_logs'), {
                actorId: currentUser.uid,
                actorUsername: actorDoc.data().username || 'Unknown',
                targetId: targetUserId,
                actionType: 'mute',
                durationHours: hours,
                reason: reason,
                timestamp: serverTimestamp()
            });

            await NotificationService.sendNotification(
                targetUserId,
                'SYSTEM',
                'Account Muted',
                `You have been muted for ${hours} hours. Reason: ${reason}`,
                { muteExpiry, reason }
            );

            return { success: true, muteExpiry, auditId: auditRef.id };
        } catch (error: any) {
            return { success: false, error: error.message };
        }
    },

    /**
     * Search users by username (max 3 for dropdown)
     */
    searchUsers: async (searchQuery: string): Promise<{ success: boolean; users?: UserProfile[]; error?: string }> => {
        try {
            const db = await getDbSafe();
            const terms = new Set<string>();
            const trimmed = searchQuery.trim();
            
            if (!trimmed) return { success: true, users: [] };

            // Add variations to cover most casing scenarios
            terms.add(trimmed);
            terms.add(trimmed.toLowerCase());
            terms.add(trimmed.toUpperCase());
            terms.add(trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase());
            
            // 🔒 Handle @ prefix variations (for "@username" storage)
            if (!trimmed.startsWith('@')) {
                terms.add('@' + trimmed);
                terms.add('@' + trimmed.toLowerCase());
                terms.add('@' + trimmed.toUpperCase());
                terms.add('@' + trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase());
            }

            // Run parallel queries
            const queries = Array.from(terms).map(term => {
                return query(
                    collection(db, 'users'),
                    where('username', '>=', term),
                    where('username', '<=', term + '\uf8ff'),
                    limit(5)
                );
            });

            const snapshots = await Promise.all(queries.map(q => getDocs(q)));
            
            // Deduplicate by Username (Keep the "best" version of each username)
            const usersMap = new Map<string, UserProfile>();
            
            snapshots.forEach((snap) => {
                snap.docs.forEach(doc => {
                    const data = doc.data() as UserProfile;
                    const normalizedUsername = data.username.toLowerCase();
                    const existing = usersMap.get(normalizedUsername);

                    // Logic to determine if this is a "better" version of the user
                    // 1. If no existing, take this one
                    // 2. If existing has no avatar but this one does, take this one
                    // 3. (Optional) Could check for last login or higher gold/rank if available
                    
                    const hasAvatar = !!(data.avatar || data.image || data.croppedAvatar || (data.portfolio && data.portfolio.length > 0));
                    
                    if (!existing) {
                        usersMap.set(normalizedUsername, { id: doc.id, ...data });
                    } else {
                         const existingHasAvatar = !!(existing.avatar || existing.image || existing.croppedAvatar || (existing.portfolio && existing.portfolio.length > 0));
                         if (!existingHasAvatar && hasAvatar) {
                             // Replace "ghost" with populated user
                             usersMap.set(normalizedUsername, { id: doc.id, ...data });
                         }
                    }
                });
            });

            const users = Array.from(usersMap.values());
            
            return { success: true, users: users.slice(0, 10) }; // Return unified list
        } catch (error: any) {
            return { success: false, error: error.message };
        }
    },

    /**
     * Get audit logs for a user or action type
     * @param targetUserId - Optional user ID to filter logs
     * @param actionType - Optional action type to filter logs
     * @param limitCount - Maximum number of logs to return (default: 50)
     * @returns Promise with array of audit logs
     */
    getAuditLogs: async (targetUserId?: string, actionType?: string, limitCount: number = 50): Promise<{ success: boolean; logs?: AuditLog[]; error?: string }> => {
        try {
            // TODO: Implement Cloud Function call
            // const result = await httpsCallable(functions, 'getAuditLogs')({ targetUserId, actionType, limit: limitCount });
            console.log('[LegionService] getAuditLogs called:', { targetUserId, actionType, limitCount });

            // Placeholder response
            return {
                success: false,
                error: 'Backend implementation required. Cloud Function not yet deployed.'
            };
        } catch (error: any) {
            console.error('[LegionService] getAuditLogs error:', error);
            return {
                success: false,
                error: error.message || 'Failed to fetch audit logs'
            };
        }
    },

    /**
     * Get a user's current gold balance
     * @param userId - ID of the user
     * @returns Promise with the user's gold balance
     */
    getUserGoldBalance: async (userId: string): Promise<{ success: boolean; balance?: number; error?: string }> => {
        try {
            const db = await getDbSafe();
            const userDoc = await getDoc(doc(db, 'users', userId));

            if (!userDoc.exists()) {
                return {
                    success: false,
                    error: 'User not found'
                };
            }

            const userData = userDoc.data();
            return {
                success: true,
                balance: readGoldBalance(userData)
            };
        } catch (error: any) {
            console.error('[LegionService] getUserGoldBalance error:', error);
            return {
                success: false,
                error: error.message || 'Failed to fetch gold balance'
            };
        }
    }
};

// ==================== ECONOMY SERVICE ====================
export const EconomyService = {
    /**
     * Purchase a gold pack
     * @param userId - ID of the user purchasing
     * @param packId - ID/Name of the pack (e.g., "500_gold", "1200_gold")
     * @param amount - Amount of gold in the pack
     * @param price - Price paid (for logging)
     * @returns Promise with success status
     */
    purchasePack: async (userId: string, packId: string, amount: number, price: string): Promise<{ success: boolean; newBalance?: number; error?: string }> => {
        try {
            console.log('[EconomyService] purchasePack called:', { userId, packId, amount, price });

            const db = await getDbSafe();
            const userRef = doc(db, 'users', userId);

            // Run as a transaction to ensure atomic balance update
            let savedUserData: any = null;
            const result = await runTransaction(db, async (transaction) => {
                const userDoc = await transaction.get(userRef);
                if (!userDoc.exists()) {
                    throw new Error("User does not exist");
                }

                const userData = userDoc.data();
                savedUserData = userData;
                // Was `userData.gold || 0`, which ignored goldBalance — the field
                // every other path treats as authoritative. Harmless while the two
                // are in sync, wrong the moment they are not.
                const currentBalance = readGoldBalance(userData);
                const newBalance = currentBalance + amount;

                // 1. Update User Balance
                transaction.update(userRef, goldFields(newBalance));

                return { newBalance, currentBalance };
            });

            // 2. Audit Log — best-effort AFTER transaction to avoid permission failures
            try {
                const auditRef = doc(collection(db, 'auditLogs'));
                await setDoc(auditRef, {
                    actorId: userId,
                    actorUsername: savedUserData?.username || 'Unknown',
                    targetId: userId,
                    targetUsername: savedUserData?.username || 'Unknown',
                    actionType: 'gold_purchase',
                    beforeState: { gold: result.currentBalance },
                    afterState: { gold: result.newBalance },
                    amount: amount,
                    reason: `Purchased Pack: ${packId} (${price})`,
                    metadata: { packId, price },
                    timestamp: serverTimestamp()
                });
            } catch (auditErr) {
                console.warn('[EconomyService] Audit log write failed (non-critical):', auditErr);
            }

            console.log('[EconomyService] Pack purchased successfully. New Balance:', result.newBalance);
            return { success: true, newBalance: result.newBalance };

        } catch (error: any) {
            console.error('[EconomyService] purchasePack error:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Deduct gold from a user for an action
     * @param userId - ID of the user
     * @param amount - Amount to deduct
     * @param actionType - Type of action (for logging)
     * @returns Promise with success status and new balance
     */
    /**
     * Give gold back.
     *
     * The mirror of deductGold, and deliberately narrow: it exists so a
     * cancelled friend request returns what it cost. Callers must only reach it
     * on a path that actually undid the thing that was paid for, because like
     * every other gold mutation in this codebase it runs client-side — there are
     * no Cloud Functions for gold, and firestore.rules leaves `gold`/`goldBalance`
     * owner-writable because the daily streak, invite rewards and pack purchases
     * are all client writes that increase the balance and a rule cannot tell them
     * from a forged one.
     *
     * So this adds no new exploit class; it adds a convenient button to one that
     * already exists. The protection that matters is at the CALL SITE:
     * cancelFriendRequest refunds only when it actually transitions a pending
     * request to rejected, so calling it twice cannot pay out twice.
     */
    refundGold: async (userId: string, amount: number, actionType: string): Promise<{ success: boolean; newBalance?: number }> => {
        try {
            if (!Number.isFinite(amount) || amount <= 0) return { success: false };
            const db = await getDbSafe();
            const userRef = doc(db, 'users', String(userId).trim());

            const newBalance = await runTransaction(db, async (transaction) => {
                const userDoc = await transaction.get(userRef);
                if (!userDoc.exists()) throw new Error('User document not found');
                const next = readGoldBalance(userDoc.data()) + amount;
                transaction.update(userRef, { ...goldFields(next), updatedAt: serverTimestamp() });
                return next;
            });

            // Best-effort, and after the transaction, so a rules failure on the
            // audit collection cannot roll back a refund the user has been told
            // about. Same shape as deductGold's log.
            try {
                await setDoc(doc(collection(db, 'auditLogs')), {
                    actorId: String(userId), targetId: String(userId),
                    actionType, amount, reason: `Gold refund for ${actionType}`,
                    timestamp: serverTimestamp(),
                });
            } catch { /* logging only */ }

            return { success: true, newBalance };
        } catch (error) {
            console.error('[EconomyService] refundGold error:', error);
            return { success: false };
        }
    },

    deductGold: async (userId: string, amount: number, actionType: string): Promise<{ success: boolean; newBalance?: number; error?: string }> => {
        try {
            console.log('[EconomyService] deductGold called:', { userId, amount, actionType });

            const db = await getDbSafe();
            const cleanUserId = String(userId).trim();
            const userRef = doc(db, 'users', cleanUserId);

            const result = await runTransaction(db, async (transaction) => {
                const userDoc = await transaction.get(userRef);
                if (!userDoc.exists()) {
                    throw new Error(`AUTHORITY_ERROR: User document not found for ID: ${cleanUserId}.`);
                }

                const userData = userDoc.data();
                const currentBalance = readGoldBalance(userData);

                console.log(`[EconomyService] User found: ${userData.username}, Balance: ${currentBalance}`);

                if (currentBalance < amount) {
                    throw new Error(`INSUFFICIENT_FUNDS: Need ${amount}, have ${currentBalance}`);
                }

                const newBalance = currentBalance - amount;

                // 1. Update User Balance
                transaction.update(userRef, {
                    ...goldFields(newBalance),
                    updatedAt: serverTimestamp()
                });

                return { newBalance, userData };
            });

            // 2. Create Audit Log (Separate call to isolate permission failure)
            try {
                const auditRef = doc(collection(db, 'auditLogs'));
                await setDoc(auditRef, {
                    actorId: cleanUserId,
                    actorUsername: result.userData.username || 'Unknown',
                    targetId: cleanUserId,
                    targetUsername: result.userData.username || 'Unknown',
                    actionType: actionType,
                    beforeState: { gold: readGoldBalance(result.userData) },
                    afterState: { gold: result.newBalance },
                    amount: -amount,
                    reason: `Gold Deduction for ${actionType}`,
                    timestamp: serverTimestamp()
                });
            } catch (auditError) {
                console.warn('[EconomyService] Audit log failed (Ignored for UX):', auditError);
            }

            console.log(`[EconomyService] Gold successfully deducted. New balance: ${result.newBalance}`);
            return { success: true, newBalance: result.newBalance };
        } catch (error: any) {
            console.error('[EconomyService] deductGold error:', error);
            
            let friendlyError = 'Failed to deduct gold';
            if (error.message?.includes('INSUFFICIENT_FUNDS')) {
                friendlyError = 'Insufficient gold. Please visit the Market to top up.';
            } else if (error.message?.includes('AUTHORITY_ERROR')) {
                friendlyError = 'Backend Authority Error: Your account identity may be out of sync. Please try logging in again.';
            } else if (error.code === 'permission-denied' || error.message?.toLowerCase().includes('permission') || error.message?.toLowerCase().includes('insufficient')) {
                friendlyError = `Backend Permission Error: ${error.message || 'Access Denied'}. Your identity or rules may be out of sync.`;
            } else {
                friendlyError = `Transaction Error: ${error.message || 'Unknown error'}`;
            }

            return {
                success: false,
                error: friendlyError
            };
        }
    }
};

// ==================== REPORT SERVICE ====================
export const ReportService = {
    /**
     * Submit a new report
     */
    submitReport: async (
        reporterId: string,
        reportedId: string,
        targetType: 'user' | 'yap' | 'reply' | 'forum' | 'group',
        category: string,
        reason: string,
        metadata?: any
    ): Promise<{ success: boolean; reportId?: string; error?: string }> => {
        try {
            const db = await getDbSafe();
            const auth = await getAuthSafe();
            
            // Validate reporter matches auth (basic security)
            if (auth.currentUser?.uid !== reporterId) {
                return { success: false, error: 'Unauthorized reporter' };
            }

            // 🔒 SECURITY: Rate limit — 10s between reports
            _checkRateLimit(reporterId, 'submitReport', 10000);

            // 🔒 SECURITY: Dedup check — prevent same reporter spamming same target
            const dedupQ = query(
                collection(db, 'reports'),
                where('reporterId', '==', reporterId),
                where('reportedId', '==', reportedId)
            );
            const dedupSnap = await getDocs(dedupQ);
            if (!dedupSnap.empty) {
                return { success: false, error: 'You have already reported this content. Our team is reviewing it.' };
            }

            // Get reporter username for easier display
            const reporterDoc = await getDoc(doc(db, 'users', reporterId));
            const reporterUsername = reporterDoc.exists() ? reporterDoc.data().username : 'Unknown';

            // Optional: Get reported username if target is user
            let reportedUsername = '';
            if (targetType === 'user') {
                 const reportedDoc = await getDoc(doc(db, 'users', reportedId));
                 if (reportedDoc.exists()) reportedUsername = reportedDoc.data().username;
            }

            // Owner of the reported content (for the canonical targetUserId).
            const targetUserId =
                metadata?.targetUserId ||
                metadata?.authorId ||
                metadata?.shouterId ||
                metadata?.ownerId ||
                (targetType === 'user' ? reportedId : '');

            const reportData = {
                // canonical schema (matches WatcherService.reportToWatchers)
                type: targetType,
                targetId: reportedId,
                targetUserId,
                reportedByUserId: reporterId,
                reason,
                status: 'pending',
                timestamp: serverTimestamp(),
                // legacy aliases (read by existing dashboards)
                reporterId,
                reporterUsername,
                reportedId,
                reportedUsername,
                targetType,
                category,
                metadata: metadata || {}
            };

            const docRef = await addDoc(collection(db, 'reports'), reportData);

            return { success: true, reportId: docRef.id };
        } catch (error: any) {
            console.error('[ReportService] submitReport error:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Fetch pending reports (For Watchers/Legions)
     */
    fetchPendingReports: async (): Promise<{ success: boolean; reports?: any[]; error?: string }> => {
        try {
            const db = await getDbSafe();
            const auth = await getAuthSafe();
            const currentUser = auth.currentUser;

            if (!currentUser) return { success: false, error: 'Not authenticated' };

            // Verify role
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const rank = userDoc.data()?.rank;
            if (rank !== 'watcher' && rank !== 'legion') {
                return { success: false, error: 'Unauthorized: Requires Watcher or Legion rank' };
            }

            const q = query(
                collection(db, 'reports'),
                where('status', '==', 'pending'),
                orderBy('timestamp', 'desc'),
                limit(50)
            );

            const snapshot = await getDocs(q);
            const reports = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            return { success: true, reports };
        } catch (error: any) {
            console.error('[ReportService] fetchPendingReports error:', error);
            // Hint for missing index
            if (error.message?.includes('index') || error.code === 'failed-precondition') {
                return { success: false, error: 'Database requires index optimization. Please contact technical support with this error: ' + error.message };
            }
            return { success: false, error: error.message };
        }
    },

    /**
     * Resolve or Dismiss a report
     */
    updateReportStatus: async (
        reportId: string,
        status: 'resolved' | 'dismissed',
        resolutionNote?: string
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            const db = await getDbSafe();
            const auth = await getAuthSafe();
            const currentUser = auth.currentUser;

            if (!currentUser) return { success: false, error: 'Not authenticated' };

             // Verify role
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const rank = userDoc.data()?.rank;
            if (rank !== 'watcher' && rank !== 'legion') {
                return { success: false, error: 'Unauthorized' };
            }

            const reportRef = doc(db, 'reports', reportId);
            const reportSnap = await getDoc(reportRef);
            
            await updateDoc(reportRef, {
                status,
                resolvedBy: currentUser.uid,
                resolvedAt: serverTimestamp(),
                resolutionNote: resolutionNote || ''
            });

            // 🔔 Feedback Loop: Notify the reporter that action has been taken
            if (reportSnap.exists()) {
                const data = reportSnap.data();
                if (data.reporterId) {
                    const title = "Safety Report Update";
                    const message = status === 'resolved'
                        ? "Thanks for your report. We've reviewed the content and taken appropriate action to keep AfroYap safe."
                        : "Thanks for your report. Our team has reviewed the content and determined it complies with our community guidelines.";
                    
                    await NotificationService.sendNotification(
                        data.reporterId,
                        'WATCHER_MOD',
                        title,
                        message,
                        { 
                            reportId, 
                            status, 
                            targetType: data.targetType,
                            deepLink: 'afroyap://notifications'
                        }
                    );
                }
            }

            return { success: true };
        } catch (error: any) {
            console.error('[ReportService] updateReportStatus error:', error);
            return { success: false, error: error.message };
        }
    }
};


export const AdminService = {
    /**
     * Moderator delete: takes the account off the platform server-side.
     *
     * Use this, not purgeUserData, for removing someone else's account. The
     * function disables sign-in and deletes the profile before it returns, then
     * purges everything else in the background. purgeUserData runs under the
     * moderator's own rules, never touches the Auth record, and removes the
     * profile last, so the account stays visible for as long as it runs.
     */
    deleteAccountAsModerator: async (userId: string, reason: string): Promise<void> => {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        await httpsCallable(getFunctions(), 'adminDeleteAccount')({ userId, reason });
    },

    /**
     * Purges all data associated with a user ID across Firestore and Storage.
     * Note: Does NOT delete the Auth account (requires Admin SDK or current user context).
     */
    purgeUserData: async (userId: string): Promise<void> => {
        const db = await getDbSafe();
        const storage = await getStorageSafe();

        try {
            console.log(`[AdminService] Purging data for user: ${userId}`);

            // Firestore caps a writeBatch at 500 operations. Several phases below
            // put every matching document into a single batch, so they failed
            // outright for exactly the users who had the most to delete — and
            // most of those phases only console.warn, so the failure was silent.
            const deleteDocsChunked = async (docs: any[]) => {
                for (let i = 0; i < docs.length; i += 400) {
                    const batch = writeBatch(db);
                    docs.slice(i, i + 400).forEach((d: any) => batch.delete(d.ref));
                    await batch.commit();
                }
            };

            // 0. Fetch User Profile for Cleanup Lists
            const userRef = doc(db, "users", userId);
            const userDoc = await getDoc(userRef);
            const userData = userDoc.exists() ? userDoc.data() : {};

            // --- PHASE 1: UN-LIKE EVERYTHING (Fix Metrics) ---
            const likedYapIds = userData.likedYapIds || [];
            if (likedYapIds.length > 0) {
                const chunks = [];
                for (let i = 0; i < likedYapIds.length; i += 20) {
                    chunks.push(likedYapIds.slice(i, i + 20));
                }

                for (const chunk of chunks) {
                    await Promise.allSettled(chunk.map(async (yapId: string) => {
                        const yapRef = doc(db, 'yaps', yapId);
                        try {
                            await updateDoc(yapRef, {
                                "metrics.likes": increment(-1),
                                likes: increment(-1),
                                likedBy: arrayRemove(userId)
                            });
                        } catch (e) {
                            // Best effort for tribe_posts, forum_posts
                        }
                    }));
                }
            }

            // --- PHASE 2: REMOVE SHOUTS (Fix Metrics & Docs) ---
            try {
                const shoutsQ = query(collection(db, 'shouts'), where('shoutedBy', '==', userId));
                const shoutsSnap = await getDocs(shoutsQ);
                
                if (!shoutsSnap.empty) {
                    const batch = writeBatch(db);
                    let opCount = 0;
                    for (const shoutDoc of shoutsSnap.docs) {
                        batch.delete(shoutDoc.ref);
                        opCount++;
                        const data = shoutDoc.data();
                        if (data.originalYapId) {
                            const originalRef = doc(db, 'yaps', data.originalYapId);
                            batch.update(originalRef, {
                                "metrics.shouts": increment(-1),
                                shouts: increment(-1)
                            });
                        }
                        if (opCount >= 400) {
                            await batch.commit();
                            opCount = 0;
                        }
                    }
                    if (opCount > 0) await batch.commit();
                    console.log(`[AdminService] Shouts purged for ${userId}`);
                }
            } catch (phase2Error) {
                console.warn(`[AdminService] Phase 2 (Shouts) failed for ${userId}:`, phase2Error);
            }

            // --- PHASE 3: DELETE COMMENTS ---
            try {
                const commentsQ = query(collectionGroup(db, 'comments'), where('userId', '==', userId));
                const commentsSnap = await getDocs(commentsQ);
                if (!commentsSnap.empty) {
                     await deleteDocsChunked(commentsSnap.docs);
                     console.log(`[AdminService] Comments purged for ${userId} (${commentsSnap.size})`);
                }
            } catch (phase3Error) {
                console.warn(`[AdminService] Phase 3 (Comments Group) failed for ${userId}:`, phase3Error);
            }

            // --- PHASE 4: DELETE ALL POSTS ---
            // Chunked at 400. A writeBatch caps at 500 operations, and this put
            // EVERY matching document into one batch — so a user with more than
            // 500 yaps had the commit rejected outright and kept all of them.
            // The failure was silent and depended entirely on how much someone
            // had posted, which is the worst way for a deletion to fail: the
            // people most visible on the app were the ones it did not work for.
            const deleteUserCollection = async (colName: string, userIdField: string = 'userId') => {
                const q = query(collection(db, colName), where(userIdField, '==', userId));
                const snap = await getDocs(q);
                if (snap.empty) return;

                await deleteDocsChunked(snap.docs);
            };

            await deleteUserCollection('yaps', 'authorId'); 
            await deleteUserCollection('yaps', 'userId'); 
            await deleteUserCollection('tribe_posts', 'userId');
            await deleteUserCollection('forum_posts', 'userId');

            // --- PHASE 4.5: DELETE OWNED TRIBES & FORUMS ---
            try {
                // Delete tribes where user is still the owner
                const tribesQ = query(collection(db, 'tribes'), where('ownerId', '==', userId));
                const tribesSnap = await getDocs(tribesQ);
                if (!tribesSnap.empty) {
                    const batch = writeBatch(db);
                    tribesSnap.forEach(doc => batch.delete(doc.ref));
                    await batch.commit();
                    console.log(`[AdminService] Owned tribes deleted for ${userId}`);
                }

                // Delete forums where user is still the owner
                const forumsQ = query(collection(db, 'forums'), where('ownerId', '==', userId));
                const forumsSnap = await getDocs(forumsQ);
                if (!forumsSnap.empty) {
                    const batch = writeBatch(db);
                    forumsSnap.forEach(doc => batch.delete(doc.ref));
                    await batch.commit();
                    console.log(`[AdminService] Owned forums deleted for ${userId}`);
                }
            } catch (phase45Error) {
                console.warn(`[AdminService] Phase 4.5 (Tribes/Forums) failed for ${userId}:`, phase45Error);
            }

            // --- PHASE 4.55: BUSINESS PROFILES ---
            // A business profile is publicly readable at /business/:id, so leaving
            // one behind keeps a deleted account's shop live. Only the owner may
            // delete one (onBusinessProfileDeleted then clears feed and store), so
            // a self-deletion deletes. A moderator purge is denied that delete by
            // design and suspends instead, which the admin branch of the rule allows.
            // Registrations and purchase checks are private and admin-deletable only.
            try {
                const purger = (await getAuthSafe()).currentUser?.uid || '';
                const businessSnap = await getDocs(query(collection(db, 'business_profiles'), where('ownerId', '==', userId)));
                for (const profileDoc of businessSnap.docs) {
                    try {
                        await deleteDoc(profileDoc.ref);
                    } catch {
                        if (profileDoc.data().status !== 'suspended') {
                            await updateDoc(profileDoc.ref, {
                                status: 'suspended',
                                suspendedAt: serverTimestamp(),
                                suspendedBy: purger,
                                suspendReason: 'Account deleted',
                            });
                        }
                    }
                }
            } catch (businessError) {
                console.warn(`[AdminService] Phase 4.55 (Business profiles) failed for ${userId}:`, businessError);
            }

            // --- PHASE 4.6: DELETE MATCHES, NOTIFICATIONS, REPORTS ---
            try {
                // Delete matches involving this user
                const matchesQ1 = query(collection(db, 'matches'), where('userId1', '==', userId));
                const matchesQ2 = query(collection(db, 'matches'), where('userId2', '==', userId));
                const [matches1Snap, matches2Snap] = await Promise.all([
                    getDocs(matchesQ1),
                    getDocs(matchesQ2)
                ]);
                
                if (!matches1Snap.empty || !matches2Snap.empty) {
                    const batch = writeBatch(db);
                    matches1Snap.forEach(doc => batch.delete(doc.ref));
                    matches2Snap.forEach(doc => batch.delete(doc.ref));
                    await batch.commit();
                    console.log(`[AdminService] Matches deleted for ${userId}`);
                }

                // Delete notifications for/from this user
                await deleteUserCollection('notifications', 'userId');
                
                // Delete reports by/against this user
                const reportsQ1 = query(collection(db, 'reports'), where('reporterId', '==', userId));
                const reportsQ2 = query(collection(db, 'reports'), where('reportedUserId', '==', userId));
                const [reports1Snap, reports2Snap] = await Promise.all([
                    getDocs(reportsQ1),
                    getDocs(reportsQ2)
                ]);
                
                if (!reports1Snap.empty || !reports2Snap.empty) {
                    const batch = writeBatch(db);
                    reports1Snap.forEach(doc => batch.delete(doc.ref));
                    reports2Snap.forEach(doc => batch.delete(doc.ref));
                    await batch.commit();
                    console.log(`[AdminService] Reports deleted for ${userId}`);
                }
            } catch (phase46Error) {
                console.warn(`[AdminService] Phase 4.6 (Matches/Notifications/Reports) failed for ${userId}:`, phase46Error);
            }

            // --- PHASE 4.7: REMOVE FRIEND RELATIONSHIPS ---
            try {
                // Determine exactly who the user is friends with to avoid a full table scan
                const userFriendsRef = collection(db, `users/${userId}/friends`);
                const userFriendsSnap = await getDocs(userFriendsRef);
                
                if (!userFriendsSnap.empty) {
                    const batch = writeBatch(db);
                    let opCount = 0;

                    for (const friendDoc of userFriendsSnap.docs) {
                        const friendId = friendDoc.id;
                        
                        // 1. Delete friend from this user's subcollection
                        batch.delete(friendDoc.ref);
                        opCount++;
                        
                        // 2. Delete this user from the friend's subcollection
                        const oppositeFriendRef = doc(db, `users/${friendId}/friends/${userId}`);
                        batch.delete(oppositeFriendRef);
                        opCount++;
                        
                        if (opCount >= 400) {
                            await batch.commit();
                            // Restart batch
                            opCount = 0;
                        }
                    }
                    
                    if (opCount > 0) {
                        await batch.commit();
                    }
                }
                
                console.log(`[AdminService] Friend relationships removed for ${userId}`);
            } catch (phase47Error) {
                console.warn(`[AdminService] Phase 4.7 (Friends) failed for ${userId}:`, phase47Error);
            }

            // --- PHASE 4.8: ROOMS ---
            //
            // Rooms were missed entirely by this purge. A deleted account left
            // its membership behind, which keeps memberCount inflated so the
            // room never drops into grace when it actually empties, and any room
            // it HOSTED outlived its host.
            //
            // Rooms are temporary — 48h at most, then sweepExpiredRooms deletes
            // them wholesale — so this self-healed eventually. "Eventually" is
            // still up to two days of a room run by nobody.
            //
            // leaveRoom is reused rather than reimplemented: it owns the
            // memberCount transaction and the lobby/grace transitions that come
            // with a member leaving, and getting those subtly different here
            // would be worse than not doing it at all.
            try {
                const { RoomService } = await import('./RoomService');
                const memberships = await getDocs(query(
                    collectionGroup(db, 'members'),
                    where('userId', '==', userId),
                ));
                for (const m of memberships.docs) {
                    const roomId = m.ref.parent.parent?.id;
                    if (!roomId) continue;
                    try {
                        await RoomService.leaveRoom(userId, roomId);
                    } catch {
                        // Owner-of-a-live-room and similar refusals are handled
                        // by the owned-room pass below.
                    }
                }

                // Any room still hosted by this account is closed, which is the
                // same thing ending a room does; the sweep performs the delete.
                const owned = await getDocs(query(
                    collection(db, 'rooms'),
                    where('ownerId', '==', userId),
                ));
                for (const r of owned.docs) {
                    if (r.data()?.status === 'closed') continue;
                    await updateDoc(r.ref, { status: 'closed' }).catch(() => {});
                }
            } catch (roomsError) {
                console.warn(`[AdminService] Room cleanup failed for ${userId}:`, roomsError);
            }

            // --- PHASE 5: STORAGE CLEANUP ---
            try {
                // Recursive: business media sits two levels down
                // (users/{uid}/business/{kind}/...), which a one-level walk left behind.
                const purgeFolder = async (folder: ReturnType<typeof ref>): Promise<void> => {
                    const listResult = await listAll(folder);
                    await Promise.allSettled(listResult.items.map(item => deleteObject(item)));
                    for (const sub of listResult.prefixes) await purgeFolder(sub);
                };
                await purgeFolder(ref(storage, `users/${userId}`));
                console.log(`[AdminService] Storage purged for ${userId}`);
            } catch (storageError) {
                console.warn(`[AdminService] Storage cleanup skipped or failed for ${userId}:`, storageError);
            }

            // --- PHASE 6.5: DELETE CHATS INVOLVING THIS USER ---
            //
            // Covers direct messages AND message requests: requests are ordinary
            // chat documents carrying isRequest/requestStatus, not a separate
            // collection, so the participant query already reaches them.
            //
            // Two things were wrong here, and both hid behind the phase-level
            // catch that only warns:
            //
            //  1. Messages were deleted in ONE writeBatch, which Firestore caps
            //     at 500 operations. A conversation past that threw, the throw
            //     unwound to the catch below, and the purge reported nothing.
            //     So the users with the most messages were exactly the ones
            //     whose messages survived deletion. deleteDocsChunked already
            //     existed at the top of this function for precisely this; this
            //     phase just never used it.
            //
            //  2. Only uid-keyed chats were matched. Legacy chats stored the
            //     USERNAME in `participants` — MessagingContext still runs a
            //     dual subscription for exactly this reason — so those
            //     conversations, and every message in them, outlived the
            //     account.
            //
            // The per-chat try/catch is the third fix: one bad conversation no
            // longer abandons every chat queued behind it.
            try {
                const participantKeys = Array.from(new Set(
                    [
                        userId,
                        userData.username,
                        userData.username ? String(userData.username).replace(/^@+/, '') : null,
                        userData.username ? String(userData.username).replace(/^@+/, '').toLowerCase() : null,
                    ].filter(Boolean).map(String),
                ));

                // Deduped by document id: a chat can match on more than one key.
                const chatDocs = new Map<string, any>();
                for (const key of participantKeys) {
                    try {
                        const snap = await getDocs(query(
                            collection(db, 'chats'),
                            where('participants', 'array-contains', key),
                        ));
                        snap.docs.forEach(d => chatDocs.set(d.id, d));
                    } catch (keyError) {
                        console.warn(`[AdminService] Chat lookup failed for key "${key}":`, keyError);
                    }
                }

                let purged = 0;
                let failed = 0;
                for (const chatDoc of chatDocs.values()) {
                    try {
                        const messagesSnap = await getDocs(collection(db, 'chats', chatDoc.id, 'messages'));
                        if (!messagesSnap.empty) {
                            await deleteDocsChunked(messagesSnap.docs);
                        }
                        await deleteDoc(chatDoc.ref);
                        purged++;
                    } catch (chatError) {
                        failed++;
                        console.error(
                            `[AdminService] Could not purge chat ${chatDoc.id} for ${userId}:`,
                            chatError,
                        );
                    }
                }

                // Loud when anything survived: a chat left behind after an
                // account deletion is retained personal data, not a nuisance.
                if (failed > 0) {
                    console.error(
                        `[AdminService] Chats purged for ${userId}: ${purged} removed, ${failed} FAILED`,
                    );
                } else if (purged > 0) {
                    console.log(`[AdminService] Chats purged for ${userId}: ${purged} removed`);
                }
            } catch (phase65Error) {
                console.error(`[AdminService] Phase 6.5 (Chats) failed for ${userId}:`, phase65Error);
            }

            // --- PHASE 6: FINAL PROFILE CLEANUP ---
            if (userData.username) {
                 const cleanUsername = userData.username.replace('@', '').toLowerCase();
                 await deleteDoc(doc(db, "usernames", cleanUsername)).catch(() => {});
            }
            // Activity status. Owner-only in the rules, so a moderator purge is
            // denied here; that orphan is unreadable anyway once users/{uid} is gone,
            // because the status read rule gets that doc for the block check.
            await deleteDoc(doc(db, "status", userId)).catch(() => {});
            await deleteDoc(userRef);

            console.log(`[AdminService] Purge complete for user: ${userId}`);
        } catch (error) {
            console.error(`[AdminService] Purge failed for ${userId}:`, error);
            throw error;
        }
    },

    syncProfileToPosts: async (userId: string, updates: { 
        name?: string; 
        username?: string; 
        avatar?: string; 
        countryFlag?: string; 
        image?: string; 
        croppedAvatar?: string; 
        croppedImage?: string;
        bannerImage?: string;
        coverImage?: string;
        showLocation?: boolean;
        home_residence?: string;
    }) => {
        const db = await getDbSafe();
        
        // Reads a possibly dotted field path ("originalYap.authorCountryFlag") out
        // of a document, so the skip-if-unchanged check below can see nested values.
        // Plain data[key] returns undefined for a dotted key, which made every such
        // field look changed and rewrite on every sync.
        const readPath = (data: any, path: string): any =>
            path.split('.').reduce((acc, part) => (acc == null ? undefined : acc[part]), data);

        // A writeBatch caps at 500 operations. A prolific author can hold more yaps
        // than that on their own, and committing past the cap throws — which the
        // caller catches and logs, so the sync would silently do nothing for exactly
        // the people with the most posts to correct. Chunked well under the cap.
        const BATCH_LIMIT = 400;

        const runBatchUpdate = async (snapshot: any, fields: any) => {
            if (snapshot.empty) return;
            const pending: Array<{ ref: any; docUpdates: any }> = [];
            snapshot.forEach((doc: any) => {
                const data = doc.data();
                const docUpdates: any = {};
                for (const [key, value] of Object.entries(fields)) {
                    if (readPath(data, key) !== value) docUpdates[key] = value;
                }
                if (Object.keys(docUpdates).length > 0) pending.push({ ref: doc.ref, docUpdates });
            });

            for (let i = 0; i < pending.length; i += BATCH_LIMIT) {
                const batch = writeBatch(db);
                for (const { ref, docUpdates } of pending.slice(i, i + BATCH_LIMIT)) {
                    batch.update(ref, docUpdates);
                }
                await batch.commit();
            }
        };

        try {
            // 1. Sync Yaps & Group Posts
            const postFields: any = {};
            if (updates.name) postFields.author = updates.name;
            if (updates.username) postFields.username = updates.username;
            if (updates.avatar) postFields.avatar = updates.avatar;
            if (updates.image && !updates.avatar) postFields.avatar = updates.image;
            if (updates.croppedAvatar) postFields.avatar = updates.croppedAvatar;
            if (updates.croppedImage && !updates.croppedAvatar) postFields.avatar = updates.croppedImage;
            if (updates.countryFlag) {
                // Update both authorCountryFlag AND country on the yap doc.
                // Without updating `country`, old yap docs still show the stale flag
                // because `country` was the old field used before authorCountryFlag existed.
                postFields.authorCountryFlag = updates.countryFlag;
                postFields.country = updates.countryFlag; // Sync the legacy field too
            }
            if (updates.showLocation !== undefined) postFields.showLocation = updates.showLocation;
            if (updates.home_residence) postFields.home_residence = updates.home_residence;
            if (updates.bannerImage) {
                postFields.bannerImage = updates.bannerImage;
                postFields.coverImage = updates.bannerImage;
            } else if (updates.coverImage) {
                postFields.bannerImage = updates.coverImage;
                postFields.coverImage = updates.coverImage;
            }

            if (Object.keys(postFields).length > 0) {
                const yapsQ = query(collection(db, 'yaps'), where('userId', '==', userId));
                await runBatchUpdate(await getDocs(yapsQ), postFields);

                const forumQ = query(collection(db, 'forum_posts'), where('userId', '==', userId));
                await runBatchUpdate(await getDocs(forumQ), postFields);

                const tribeQ = query(collection(db, 'tribe_posts'), where('userId', '==', userId));
                await runBatchUpdate(await getDocs(tribeQ), postFields);

                // 1b. Sync Shouts (Shouter's info only)
                const shoutFields: any = {};
                if (updates.name) shoutFields.shoutedByName = updates.name;
                if (updates.username) shoutFields.shoutedByUsername = updates.username;
                if (updates.avatar) shoutFields.shoutedByAvatar = updates.avatar;
                if (updates.countryFlag) {
                    shoutFields.shoutedByCountryFlag = updates.countryFlag;
                    // A shout also carries a top-level authorCountryFlag, set to the
                    // SHOUTER's flag when the shout was created. The feed mapper
                    // prefers shoutedByCountryFlag so this is normally masked, but
                    // leaving it stale means the two disagree on the same document
                    // and anything reading the raw field gets the old flag.
                    shoutFields.authorCountryFlag = updates.countryFlag;
                }
                if (updates.showLocation !== undefined) shoutFields.shoutedByShowLocation = updates.showLocation;
                if (updates.bannerImage) {
                    shoutFields.shoutedByBannerImage = updates.bannerImage;
                    shoutFields.shoutedByCoverImage = updates.bannerImage;
                } else if (updates.coverImage) {
                    shoutFields.shoutedByBannerImage = updates.coverImage;
                    shoutFields.shoutedByCoverImage = updates.coverImage;
                }
                
                const shoutsQ = query(collection(db, 'shouts'), where('shoutedBy', '==', userId));

                if (Object.keys(shoutFields).length > 0) {
                    await runBatchUpdate(await getDocs(shoutsQ), shoutFields);
                }

                // 1c. Sync the frozen originalYap snapshot inside OTHER people's shouts.
                //
                // A shout stores a copy of the yap it quotes, taken at the moment of
                // shouting and never refreshed. Everything above updates documents
                // this user OWNS; this one updates documents owned by whoever shouted
                // them. Without it, changing your heritage left your old flag showing
                // on every shout of your yaps — visible in the feed, which renders the
                // snapshot as-is. (The yap and shout detail screens re-fetch the
                // original on open, so they self-corrected and hid the problem.)
                //
                // Dot-path fields update inside the map without rewriting it, so a
                // concurrent edit to another part of the snapshot is not clobbered.
                const snapshotFields: any = {};
                if (updates.name) snapshotFields['originalYap.author'] = updates.name;
                if (updates.username) snapshotFields['originalYap.username'] = updates.username;
                if (updates.avatar) snapshotFields['originalYap.avatar'] = updates.avatar;
                if (updates.countryFlag) {
                    snapshotFields['originalYap.authorCountryFlag'] = updates.countryFlag;
                    snapshotFields['originalYap.country'] = updates.countryFlag;
                }

                if (Object.keys(snapshotFields).length > 0) {
                    // Nested-field equality needs no composite index: Firestore
                    // indexes map subfields automatically.
                    const quotedQ = query(collection(db, 'shouts'), where('originalYap.userId', '==', userId));
                    await runBatchUpdate(await getDocs(quotedQ), snapshotFields);
                }
            }

            // 2. Sync Comments
            const commentFields: any = {};
            if (updates.name) commentFields.author = updates.name;
            if (updates.username) commentFields.username = updates.username;
            if (updates.avatar) commentFields.avatar = updates.avatar;
            if (updates.countryFlag) commentFields.authorCountryFlag = updates.countryFlag;
            if (updates.bannerImage) {
                commentFields.authorBannerImage = updates.bannerImage;
                commentFields.authorCoverImage = updates.bannerImage;
            } else if (updates.coverImage) {
                commentFields.authorBannerImage = updates.coverImage;
                commentFields.authorCoverImage = updates.coverImage;
            }

            if (Object.keys(commentFields).length > 0) {
                const commentsQ = query(collectionGroup(db, 'comments'), where('authorId', '==', userId));
                await runBatchUpdate(await getDocs(commentsQ), commentFields);
            }

            console.log(`[AdminService] Profile sync complete for user: ${userId}`);
        } catch (error) {
            console.error(`[AdminService] Profile sync failed for ${userId}:`, error);
            // We don't throw here to avoid blocking the main profile update, 
            // but we log it for diagnostics.
        }
    },

    /**
     * Fetches moderation logs for the "Real Audit" dashboard.
     */
    fetchModerationLogs: async (limitCount: number = 50): Promise<any[]> => {
        try {
            const db = await getDbSafe();
            const logsRef = collection(db, 'moderation_logs');
            const q = query(
                logsRef, 
                orderBy('timestamp', 'desc'), 
                limit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error('[AdminService] fetchModerationLogs failed:', error);
            return [];
        }
    }
};

/**
 * 📣 GROUP POST NOTIFIER (Internal)
 * Sends TRIBE_POST or FORUM_POST push notifications to every member of a
 * group except the poster. Fire-and-forget — never throws; errors are logged.
 *
 * Reads only member doc IDs from the members subcollection (no user profile
 * reads) to keep the fan-out lightweight. The Cloud Function
 * onNotificationCreated handles per-user preference checks and FCM delivery.
 *
 * @param groupType   'tribe' | 'forum'
 * @param groupId     Firestore document ID of the tribe or forum
 * @param groupName   Display name shown in the notification title
 * @param postId      ID of the newly created post document
 * @param posterId    UID of the user who created the post (excluded from recipients)
 * @param posterName  Display name of the poster (shown in notification body)
 * @param content     Post content — truncated to 120 chars for the notification body
 */
/**
 * Stable per-install device identifier used to dedupe rotated FCM tokens.
 * A UUID persisted in localStorage — stable across sessions both in the native
 * app webview and on web, so a device's old (rotated) token can be pruned.
 */
async function _getStableDeviceId(): Promise<string> {
    try {
        const KEY = 'afroyap_device_id';
        const existing = localStorage.getItem(KEY);
        if (existing) return existing;
        const id: string = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
            ? (crypto as any).randomUUID()
            : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(KEY, id);
        return id;
    } catch {
        return `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
}

async function _notifyGroupMembers(
    groupType: 'tribe' | 'forum',
    groupId: string,
    groupName: string,
    postId: string,
    posterId: string,
    posterName: string,
    content: string,
    excludeUserIds: string[] = []
): Promise<void> {
    try {
        const db = await getDbSafe();
        const { collection, getDocs } = await import('firebase/firestore');

        const memberSnap = await getDocs(
            collection(db, groupType === 'tribe' ? 'tribes' : 'forums', groupId, 'members')
        );

        const notifType = groupType === 'tribe' ? 'TRIBE_POST' : 'FORUM_POST';
        const title     = groupType === 'tribe'
            ? `New post in ${groupName}`
            : `New post in ${groupName}`;
        const body      = `${posterName}: ${content.slice(0, 120)}${content.length > 120 ? '…' : ''}`;
        const metadata  = {
            senderId:    posterId,
            fromUserId:  posterId,
            fromUserName: posterName,
            targetId:    postId,
            ...(groupType === 'tribe'
                ? { tribeId: groupId, tribeName: groupName }
                : { forumId: groupId, forumName: groupName }),
        };

        // Anyone who already received a MENTION push for this same post should
        // NOT also receive the group-wide TRIBE_POST/FORUM_POST push — the more
        // specific mention notification wins, otherwise the same event lands as
        // two banners on the recipient's device.
        const excludeSet = new Set([posterId, ...excludeUserIds].map(String));

        // Fan-out: one notification per member, skip the poster + mentioned users.
        const sends = memberSnap.docs
            .map(d => d.id)
            .filter(uid => !excludeSet.has(String(uid)))
            .map(uid =>
                NotificationService.sendNotification(uid, notifType, title, body, metadata)
                    .catch(e => console.warn(`[GroupNotify] Failed for ${uid}:`, e))
            );

        await Promise.allSettled(sends);
        console.log(`[GroupNotify] Notified ${sends.length} members of new ${groupType} post in ${groupId} (skipped ${excludeSet.size - 1} mentioned)`);
    } catch (e) {
        console.warn('[GroupNotify] Member notification failed (non-critical):', e);
    }
}

/**
 * 🕵️ MENTION PROCESSOR (Internal)
 * Parses content for @username patterns and sends notifications.
 */
async function _processMentions(
    content: string,
    sourceId: string,
    sourceType: 'yap' | 'comment' | 'shout' | 'forum' | 'tribe',
    authorId: string,
    authorName: string,
    excludeUserIds: string[] = []
): Promise<string[]> {
    // Returns the list of UIDs that actually received a MENTION push, so callers
    // can pass it to a follow-up fan-out (e.g. _notifyGroupMembers) to suppress
    // a second push for the same event.
    const notified: string[] = [];
    if (!content || !content.includes('@')) return notified;

    // 1. Extract @username patterns (supports dots and underscores)
    const mentionRegex = /@([\w\.]+)/g;
    const matches = content.match(mentionRegex);
    if (!matches) return notified;

    // 2. Filter unique usernames and limit to first 10 to prevent spam/abuse
    const uniqueUsernames = Array.from(new Set(matches))
        .map(u => u.toLowerCase())
        .slice(0, 10);

    // Dedup set: any user who already received a notification for this same
    // event (e.g. the post author who got a COMMENT notification) shouldn't
    // also get a MENTION notification — that's a duplicate ping for one action.
    const excludeSet = new Set(excludeUserIds.map(String));

    for (const username of uniqueUsernames) {
        try {
            // 3. Resolve username to user document
            const user = await UserService.fetchUserByUsername(username);

            // Skip if user not found, mentioning self, or already notified.
            if (!user || String(user.id) === String(authorId)) continue;
            if (excludeSet.has(String(user.id))) {
                console.log(`[MentionService] Skipping mention for ${username} — already notified via primary event.`);
                continue;
            }
            
            // 4. Build the notification.
            // Title: "<author> mentioned you"
            // Body : the reply/post content with the recipient's @handle stripped,
            //        so the receiver sees the actual message addressed to them.
            const title = `${authorName} mentioned you`;

            // Strip the recipient's @username (case-insensitive, with optional trailing
            // punctuation collapsed) plus any leftover double-spaces.
            const handle = username.replace(/^@/, '');
            const stripRegex = new RegExp(`@${handle}\\b`, 'gi');
            const cleaned = content.replace(stripRegex, '').replace(/\s{2,}/g, ' ').trim();

            // Trim to a sensible notification length.
            const body = cleaned.length > 140 ? cleaned.slice(0, 140).trim() + '…' : cleaned;

            // Build deep link by source type. sourceId is now the parent post id
            // for comment mentions, so we can always deep-link to /yap/{postId}.
            let deepLink: string;
            if (sourceType === 'comment' || sourceType === 'yap' || sourceType === 'shout') {
                deepLink = `afroyap://yap/${sourceId}`;
            } else if (sourceType === 'forum') {
                deepLink = `afroyap://yap/${sourceId}`;
            } else if (sourceType === 'tribe') {
                deepLink = `afroyap://yap/${sourceId}`;
            } else {
                deepLink = `afroyap://${sourceType}/${sourceId}`;
            }

            await NotificationService.sendNotification(
                String(user.id),
                'MENTION',
                title,
                body,
                {
                    targetId: sourceId,
                    sourceType,
                    fromUserId: authorId,
                    fromUserName: authorName,
                    deepLink
                }
            );

            notified.push(String(user.id));
            console.log(`[MentionService] Mentioned ${username} (${user.id}) in ${sourceType} ${sourceId}`);
        } catch (err) {
            console.warn(`[MentionService] Failed to process mention for ${username}:`, err);
        }
    }

    return notified;
}
